import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import './pack-it.css'; // Keep the CSS for now, we'll update it
import { useWebSocket } from './WebSocketContext';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut, updatePassword } from '@aws-amplify/auth';
import axios from 'axios';
import { makeAuthenticatedRequest, handleAuthError } from './utils/authUtils';
import { GoogleMap, useJsApiLoader, Autocomplete, Marker, DirectionsRenderer, Polyline, InfoWindow, useLoadScript  } from '@react-google-maps/api';
import AdvancedMarker from './AdvancedMarker';
import Legend from './components/Legend';
import Header from './components/Header';
import Footer from './components/Footer';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_CENTER = { lat: 33.7413, lng: -118.1038 };
const DEFAULT_ZOOM = 11;
const libraries = ['places', 'marker'];
  // Ensure itinerary locations are geocoded before route calculation
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const mapContainerStyle = {
  width: "100%",
  height: "500px",
};

const googleMapsLibraries = ['places', 'marker'];
const googleMapsOptions = {
  googleMapsApiKey,
  libraries: googleMapsLibraries,
  id: 'script-loader',
  version: 'weekly',
  language: 'en',
  region: 'US',
};

const categoryIcons = {
  placesToStay: '/images/blue-dot.png',
  //placesToEat: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  placesToEat: '/images/green-dot.png',
  thingsToDo: '/images/yellow-dot.png',
  destinations: '/images/red-dot.png',
};

// Utility function to normalize thingstodo data into a flat array of activities
// Utility function to normalize thingstodo data into a flat array of activities
const normalizeThingsToDo = (thingstodo) => {
  const activities = [];
  if (!thingstodo) {
    console.log("normalizeThingsToDo: No thingstodo provided.");
    return activities;
  }

  if (Array.isArray(thingstodo)) {
    thingstodo.forEach((item, sectionIndex) => {
      if (item.thingsToDo) {
        // New format: nested as a date-keyed object
        if (typeof item.thingsToDo === 'object' && !Array.isArray(item.thingsToDo)) {
          Object.entries(item.thingsToDo).forEach(([dateKey, acts]) => {
            safeToArray(acts).forEach((act, entryIndex) => {
              const coords = act.coordinates || {};
              activities.push({
                ...act,
                id: act.id || uuidv4(),
                date: dateKey,         // ← inject the date here
                sectionIndex,
                entryIndex,
                latitude: coords.latitude ?? act.latitude,
                longitude: coords.longitude ?? act.longitude,
                coordinateProbability: coords.coordinateProbability ?? act.coordinateProbability,
              });
            });
          });
        } else if (Array.isArray(item.thingsToDo)) {
          // Legacy flat-array format has no dateKey, so leave as-is
          item.thingsToDo.forEach((act, entryIndex) => {
            const coords = act.coordinates || {};
            activities.push({
              ...act,
              id: act.id || uuidv4(),
              sectionIndex,
              entryIndex,
              latitude: coords.latitude ?? act.latitude,
              longitude: coords.longitude ?? act.longitude,
              coordinateProbability: coords.coordinateProbability ?? act.coordinateProbability,
            });
          });
        }
      } else if (item.activities) {
        // Handle the new format with 'activities' property
        if (Array.isArray(item.activities)) {
          item.activities.forEach((act, entryIndex) => {
            const coords = act.coordinates || {};
            activities.push({
              ...act,
              id: act.id || uuidv4(),
              sectionIndex,
              entryIndex,
              latitude: coords.latitude ?? act.latitude,
              longitude: coords.longitude ?? act.longitude,
              coordinateProbability: coords.coordinateProbability ?? act.coordinateProbability,
            });
          });
        }
      } else {
        // Standalone activity
        const coords = item.coordinates || {};
        activities.push({
          ...item,
          id: item.id || uuidv4(),
          sectionIndex,
          entryIndex: 0,
          latitude: coords.latitude ?? item.latitude,
          longitude: coords.longitude ?? item.longitude,
          coordinateProbability: coords.coordinateProbability ?? item.coordinateProbability,
        });
      }
    });
  } else if (typeof thingstodo === 'object') {
    // Root-level object keyed by dates
    Object.entries(thingstodo).forEach(([dateKey, acts]) => {
      safeToArray(acts).forEach((act, entryIndex) => {
        const coords = act.coordinates || {};
        activities.push({
          ...act,
          id: act.id || uuidv4(),
          date: dateKey,       // ← inject the date here too
          sectionIndex: 0,
          entryIndex,
          latitude: coords.latitude ?? act.latitude,
          longitude: coords.longitude ?? act.longitude,
          coordinateProbability: coords.coordinateProbability ?? act.coordinateProbability,
        });
      });
    });
  }

  //console.log("normalizeThingsToDo: Normalized activities:", activities);
  return activities;
};

// Utility function to normalize places to eat data - handles multiple formats
const normalizePlacesToEat = (placestoeat, locationId, dateKey) => {
  if (!Array.isArray(placestoeat) || placestoeat.length === 0) {
    return [];
  }

  let allRestaurants = [];
  placestoeat.forEach(entry => {
    // Format 1: { placestoeat: { "2025-11-18": [...] } } (new lowercase format with date keys)
    if (entry && typeof entry === 'object' && entry.placestoeat && typeof entry.placestoeat === 'object' && !Array.isArray(entry.placestoeat)) {
      Object.entries(entry.placestoeat).forEach(([date, restaurants]) => {
        if (Array.isArray(restaurants)) {
          restaurants.forEach(restaurant => {
            const coords = restaurant.coordinates || {};
            allRestaurants.push({
              ...restaurant,
              id: restaurant.id || uuidv4(),
              latitude: coords.latitude ?? restaurant.latitude,
              longitude: coords.longitude ?? restaurant.longitude,
              coordinateProbability: coords.coordinateProbability ?? restaurant.coordinateProbability,
              date: date,
              locationId: restaurant.locationId || entry.locationId || '',
            });
          });
        }
      });
    }
    // Format 2: { placesToEat: { "2025-11-18": [...] } } (camelCase format with date keys)
    else if (entry && typeof entry === 'object' && entry.placesToEat && typeof entry.placesToEat === 'object' && !Array.isArray(entry.placesToEat)) {
      Object.entries(entry.placesToEat).forEach(([date, restaurants]) => {
        if (Array.isArray(restaurants)) {
          restaurants.forEach(restaurant => {
            const coords = restaurant.coordinates || {};
            allRestaurants.push({
              ...restaurant,
              id: restaurant.id || uuidv4(),
              latitude: coords.latitude ?? restaurant.latitude,
              longitude: coords.longitude ?? restaurant.longitude,
              coordinateProbability: coords.coordinateProbability ?? restaurant.coordinateProbability,
              date: date,
              locationId: restaurant.locationId || entry.locationId || entry.destination || '',
            });
          });
        }
      });
    }
    // Format 3: { placesToEat: [...] } (direct array)
    else if (entry && typeof entry === 'object' && Array.isArray(entry.placesToEat)) {
      entry.placesToEat.forEach(restaurant => {
        const coords = restaurant.coordinates || {};
        allRestaurants.push({
          ...restaurant,
          id: restaurant.id || uuidv4(),
          latitude: coords.latitude ?? restaurant.latitude,
          longitude: coords.longitude ?? restaurant.longitude,
          coordinateProbability: coords.coordinateProbability ?? restaurant.coordinateProbability,
          locationId: restaurant.locationId || entry.locationId || entry.destination || '',
        });
      });
    }
    // Format 4: { restaurants: [...] } (legacy format)
    else if (entry && typeof entry === 'object' && Array.isArray(entry.restaurants)) {
      entry.restaurants.forEach(restaurant => {
        const coords = restaurant.coordinates || {};
        allRestaurants.push({
          ...restaurant,
          id: restaurant.id || uuidv4(),
          latitude: coords.latitude ?? restaurant.latitude,
          longitude: coords.longitude ?? restaurant.longitude,
          coordinateProbability: coords.coordinateProbability ?? restaurant.coordinateProbability,
          locationId: restaurant.locationId || entry.locationId || entry.destination || '',
        });
      });
    }
    // Format 5: entry is a single restaurant object
    else if (entry && typeof entry === 'object' && entry.name) {
      const coords = entry.coordinates || {};
      allRestaurants.push({
        ...entry,
        id: entry.id || uuidv4(),
        latitude: coords.latitude ?? entry.latitude,
        longitude: coords.longitude ?? entry.longitude,
        coordinateProbability: coords.coordinateProbability ?? entry.coordinateProbability,
      });
    }
  });

  // Deduplicate restaurants based on name, date, and locationId/coordinates
  const seenRestaurants = new Map();
  const uniqueRestaurants = [];
  
  allRestaurants.forEach(restaurant => {
    // Create a unique key based on name, date, and location/coordinates
    const name = (restaurant.name || '').toLowerCase().trim();
    const date = restaurant.date || '';
    const locId = (restaurant.locationId || '').toLowerCase().trim();
    const lat = restaurant.latitude;
    const lng = restaurant.longitude;
    
    // Use coordinates if available, otherwise use locationId
    const locationKey = (lat && lng) ? `${lat},${lng}` : locId;
    const uniqueKey = `${name}|${date}|${locationKey}`;
    
    if (!seenRestaurants.has(uniqueKey)) {
      seenRestaurants.set(uniqueKey, true);
      uniqueRestaurants.push(restaurant);
    }
  });
  
  allRestaurants = uniqueRestaurants;

  // Normalize locationId to array (supports string or string[] for travel days with multiple cities)
  const locationIds = Array.isArray(locationId) ? locationId.filter(Boolean) : (locationId ? [locationId] : []);

  // Normalize and tokenize for matching
  const normalize = v => (v || '').toString().toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  const tokenize = v => normalize(v).split(' ').filter(Boolean);
  const normDateKey = dateKey ? dateKey.split('T')[0] : '';

  // Improved fuzzy match: require all destination tokens to be present in the restaurant locationId tokens (order-insensitive)
  const isTokenSubset = (tokensA, tokensB) => tokensA.length > 0 && tokensA.every(tok => tokensB.includes(tok));

  // If no locationId or dateKey, return all restaurants
  const hasLocationId = locationIds.length > 0;
  const hasDateKey = normDateKey && normDateKey.trim();
  if (!hasLocationId && !hasDateKey) return allRestaurants;

  // Filter restaurants by improved fuzzy locationId, dateKey, or destination
  const filtered = allRestaurants.filter(restaurant => {
    const restLocIdTokens = tokenize(restaurant.locationId || '');
    const restDestTokens = tokenize(restaurant.destination || '');
    // Match if restaurant matches ANY of the provided locationIds (for travel days: departure or arrival)
    const locMatch = hasLocationId && locationIds.some(locId => {
      const normLocIdTokens = tokenize(locId);
      return isTokenSubset(normLocIdTokens, restLocIdTokens) || isTokenSubset(restLocIdTokens, normLocIdTokens);
    });
    const destMatch = hasLocationId && locationIds.some(locId => {
      const normLocIdTokens = tokenize(locId);
      return isTokenSubset(normLocIdTokens, restDestTokens) || isTokenSubset(restDestTokens, normLocIdTokens);
    });

    // Check date match - either from restaurant.date or restaurant.locationId containing date
    const dateMatch = normDateKey && (
      (restaurant.date && restaurant.date.includes(normDateKey)) ||
      (restaurant.locationId && restaurant.locationId.includes(normDateKey))
    );

    // When both locationId and dateKey are provided, require BOTH to match
    // When only one is provided, match by that criterion
    if (hasLocationId && hasDateKey) {
      // Both must match
      return (locMatch || destMatch) && dateMatch;
    } else if (hasLocationId) {
      // Only location must match
      return locMatch || destMatch;
    } else if (hasDateKey) {
      // Only date must match
      return dateMatch;
    }

    return false;
  });

  // If nothing matched, return [] if a locationId or dateKey was provided
  if (filtered.length > 0) return filtered;
  if (hasLocationId || dateKey) return [];
  return allRestaurants;
};


// Utility function to normalize itinerary structure - handles multiple formats
const normalizeItinerary = (itinerary) => {
  if (!itinerary) {
    return { itineraryArray: [], startDate: null, endDate: null, destinations: [], description: null };
  }

  // Format 1: itinerary.itinerary.itinerary (nested structure)
  if (itinerary.itinerary?.itinerary?.itinerary && Array.isArray(itinerary.itinerary.itinerary.itinerary)) {
    return {
      itineraryArray: itinerary.itinerary.itinerary.itinerary,
      startDate: itinerary.itinerary.itinerary.startDate || itinerary.startDate,
      endDate: itinerary.itinerary.itinerary.endDate || itinerary.endDate,
      destinations: itinerary.itinerary.itinerary.destinations || [],
      description: itinerary.itinerary.itinerary.description || null
    };
  }

  // Format 2: itinerary.itinerary (direct nested)
  if (itinerary.itinerary?.itinerary && Array.isArray(itinerary.itinerary.itinerary)) {
    return {
      itineraryArray: itinerary.itinerary.itinerary,
      startDate: itinerary.itinerary.startDate || itinerary.startDate,
      endDate: itinerary.itinerary.endDate || itinerary.endDate,
      destinations: itinerary.itinerary.destinations || [],
      description: itinerary.itinerary.description || null
    };
  }

  // Format 3: itinerary.itinerary is an array directly
  if (Array.isArray(itinerary.itinerary)) {
    return {
      itineraryArray: itinerary.itinerary,
      startDate: itinerary.startDate,
      endDate: itinerary.endDate,
      destinations: itinerary.destinations || [],
      description: itinerary.description || null
    };
  }

  // Format 4: itinerary is an array directly
  if (Array.isArray(itinerary)) {
    return {
      itineraryArray: itinerary,
      startDate: null,
      endDate: null,
      destinations: [],
      description: null
    };
  }

  // Default: return empty structure
  return { itineraryArray: [], startDate: null, endDate: null, destinations: [], description: null };
};

// Utility to normalize places to stay data
const normalizePlacesToStay = (placestostay, locationId, dateKey) => {
  if (!Array.isArray(placestostay) || placestostay.length === 0) {
    return [];
  }

  // Always flatten all stays from all possible formats
  let allStays = [];
  placestostay.forEach(entry => {
    // Format: { placesToStay: [{ destination: "...", accommodations: [...] }] } (new format with accommodations)
    // OR: { placesToStay: [...] } (legacy format with direct array)
    if (entry && typeof entry === 'object' && Array.isArray(entry.placesToStay)) {
      entry.placesToStay.forEach(destGroup => {
        const destination = destGroup.destination || entry.destination || '';
        const locationIdForGroup = destGroup.locationId || entry.locationId || '';
        
        // Check if this entry has accommodations array (new format)
        if (destGroup.accommodations && Array.isArray(destGroup.accommodations)) {
          destGroup.accommodations.forEach(stay => {
            const coords = stay.coordinates || {};
            allStays.push({
              ...stay,
              id: stay.id || uuidv4(),
              latitude: coords.latitude ?? stay.latitude,
              longitude: coords.longitude ?? stay.longitude,
              coordinateProbability: coords.coordinateProbability ?? stay.coordinateProbability,
              destination: destination || stay.destination || '',
              locationId: locationIdForGroup || stay.locationId || '',
            });
          });
        } 
        // Check if destGroup is a stay object itself (has name/address, legacy format)
        else if (destGroup.name && destGroup.address) {
          const coords = destGroup.coordinates || {};
          allStays.push({
            ...destGroup,
            id: destGroup.id || uuidv4(),
            latitude: coords.latitude ?? destGroup.latitude,
            longitude: coords.longitude ?? destGroup.longitude,
            coordinateProbability: coords.coordinateProbability ?? destGroup.coordinateProbability,
            destination: destination || destGroup.destination || '',
            locationId: locationIdForGroup || destGroup.locationId || '',
          });
        }
      });
    }
    // Format: { placesToStay: { "2025-09-18": [...] } } (date-keyed structure)
    else if (entry && typeof entry === 'object' && entry.placesToStay && typeof entry.placesToStay === 'object' && !Array.isArray(entry.placesToStay)) {
      const destination = entry.destination || '';
      Object.entries(entry.placesToStay).forEach(([dateKey, stays]) => {
        if (Array.isArray(stays)) {
          stays.forEach(stay => {
            const coords = stay.coordinates || {};
            allStays.push({
              ...stay,
              latitude: coords.latitude ?? stay.latitude,
              longitude: coords.longitude ?? stay.longitude,
              coordinateProbability: coords.coordinateProbability ?? stay.coordinateProbability,
              destination: destination || stay.destination || '',
              date: dateKey, // Add the date for filtering
            });
          });
        }
      });
    }
    // Format: entry is a single stay object (flat array of stays)
    else if (entry && typeof entry === 'object' && entry.name && entry.address) {
      const coords = entry.coordinates || {};
      allStays.push({
        ...entry,
        id: entry.id || uuidv4(),
        latitude: coords.latitude ?? entry.latitude,
        longitude: coords.longitude ?? entry.longitude,
        coordinateProbability: coords.coordinateProbability ?? entry.coordinateProbability,
      });
    }
    // Format: entry is an array of stays (legacy flat array)
    else if (Array.isArray(entry)) {
      entry.forEach(stay => {
        const coords = stay.coordinates || {};
        allStays.push({
          ...stay,
          id: stay.id || uuidv4(),
          latitude: coords.latitude ?? stay.latitude,
          longitude: coords.longitude ?? stay.longitude,
          coordinateProbability: coords.coordinateProbability ?? stay.coordinateProbability,
        });
      });
    }
  });

  // Normalize and tokenize for matching
  const normalize = v => (v || '').toString().toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  const tokenize = v => normalize(v).split(' ').filter(Boolean);
  const normLocIdTokens = tokenize(locationId);
  const normDateKey = dateKey ? dateKey.split('T')[0] : '';

  // Improved fuzzy match: require all destination tokens to be present in the stay locationId tokens (order-insensitive)
  const isTokenSubset = (tokensA, tokensB) => tokensA.length > 0 && tokensA.every(tok => tokensB.includes(tok));

  // If no locationId or dateKey, return all stays
  if ((!locationId || !locationId.trim()) && !dateKey) return allStays;

  // Filter stays by improved fuzzy locationId, dateKey, or destination
  const filtered = allStays.filter(stay => {
    const stayLocIdTokens = tokenize(stay.locationId || '');
    const stayDestTokens = tokenize(stay.destination || '');
    // Match if all destination tokens are in stay locationId tokens, or vice versa
    const locMatch = isTokenSubset(normLocIdTokens, stayLocIdTokens) || isTokenSubset(stayLocIdTokens, normLocIdTokens);
    const destMatch = isTokenSubset(normLocIdTokens, stayDestTokens) || isTokenSubset(stayDestTokens, normLocIdTokens);
    
    // Check date match - either from stay.date, stay.locationId containing date, or date falls within checkInDate/checkOutDate range
    let dateMatch = false;
    if (normDateKey) {
      // Check if stay has a date property that matches
      if (stay.date && stay.date.includes(normDateKey)) {
        dateMatch = true;
      }
      // Check if locationId contains the date
      else if (stay.locationId && stay.locationId.includes(normDateKey)) {
        dateMatch = true;
      }
      // Check if date falls within checkInDate to checkOutDate range
      else if (stay.checkInDate && stay.checkOutDate) {
        const checkIn = stay.checkInDate.split('T')[0];
        const checkOut = stay.checkOutDate.split('T')[0];
        dateMatch = normDateKey >= checkIn && normDateKey <= checkOut;
      }
      // Also check checkInDate alone (for single-day stays)
      else if (stay.checkInDate) {
        const checkIn = stay.checkInDate.split('T')[0];
        dateMatch = normDateKey === checkIn;
      }
    }
    
    return (
      (locationId && (locMatch || destMatch)) ||
      dateMatch
    );
  });

  // If nothing matched, return [] if a locationId or dateKey was provided
  if (filtered.length > 0) return filtered;
  if ((locationId && locationId.trim()) || dateKey) return [];
  return allStays;
};

const truncateForChatChip = (str, maxLen) => {
  const t = str != null ? String(str).trim() : '';
  if (!t) return '';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
};

/** City or area label for chat prompts from a normalized stay (destination or locationId). */
const formatStayLocationForChatPrompt = (stay) => {
  if (!stay) return null;
  const rawDest = stay.destination?.toString().trim();
  if (rawDest) {
    const cityLike = rawDest.split(/[,;|]/)[0].trim();
    if (cityLike) {
      return truncateForChatChip(cityLike, 48) || null;
    }
  }
  let loc = stay.locationId?.toString().trim();
  if (!loc) return null;
  loc = loc
    .replace(/\d{4}-\d{2}-\d{2}/g, ' ')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!loc) return null;
  const words = loc.split(' ').filter(Boolean);
  const shortened = words.slice(0, 8).join(' ');
  return truncateForChatChip(shortened, 48) || null;
};

/** Amenities may be string[], a plain string (e.g. from LLM), or other — never assume .join. */
const formatAmenitiesForDisplay = (amenities) => {
  if (amenities == null || amenities === '') {
    return null;
  }
  if (Array.isArray(amenities)) {
    const parts = amenities.map((a) => (a != null ? String(a).trim() : '')).filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (typeof amenities === 'string') {
    const t = amenities.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof amenities === 'object') {
    try {
      const s = JSON.stringify(amenities);
      return s !== '{}' ? s : null;
    } catch {
      return null;
    }
  }
  const s = String(amenities).trim();
  return s.length > 0 ? s : null;
};


// Safely coerce anything to lowercase string
const safeLower = (val) =>
  val != null
    ? val.toString().toLowerCase()
    : "";


const safeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [value];
};

const safeFlatMap = (value, callback) => {
  return safeToArray(value).flatMap(callback);
};


const MyMapComponent = ({ packitId, places, itinerary, destinations }) => {
  const mapRef = useRef(null);
  const [geocodedMarkers, setGeocodedMarkers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [geocodedItinerary, setGeocodedItinerary] = useState([]);
  const infoWindowRef = useRef(null);
  const { isLoaded, loadError } = useJsApiLoader(googleMapsOptions);
  const apiUrl = process.env.REACT_APP_API_URL;

// Helper function to build the JSON Patch payload.
// Returns an array of patch operations for latitude, longitude, and coordinateProbability.
// Helper function to build the JSON Patch payload.
// Returns an array of patch operations for latitude, longitude, and coordinateProbability.
function buildCoordinatePatch(
  section,        // "thingstodo", "placestoeat", or "placestostay"
  sectionIndex,   // Index in the root section array (0-based)
  dateKey,        // Dynamic date key (only used for non‑placestostay)
  entryIndex,     // Index within that date (or within placesToStay)
  newCoords,      // { latitude, longitude, coordinateProbability }
  usesNestedObject = false // if your coords are buried under a sub‑object
) {
  const nestedKeyMap = {
    thingstodo:   "thingsToDo",
    placestoeat:  "placesToEat",
    placestostay: "placesToStay",
  };
  const nestedKey = nestedKeyMap[section];

  let basePath;
  if (section === "placestostay") {
    // placestostay has no date layer—just section/idx/placesToStay/entryIndex
    basePath = `/${section}/${sectionIndex}/${nestedKey}/${entryIndex}`;
  } else {
    // thingstodo & placestoeat keep the date sub‑folder
    basePath = `/${section}/${sectionIndex}/${nestedKey}/${dateKey}/${entryIndex}`;
  }

  if (usesNestedObject) {
    basePath += `/coordinates`;
  }

  return [
    { op: "replace", path: `${basePath}/latitude`,              value: newCoords.latitude },
    { op: "replace", path: `${basePath}/longitude`,             value: newCoords.longitude },
    { op: "replace", path: `${basePath}/coordinateProbability`, value: newCoords.coordinateProbability }
  ];
}


// Updated updateCoordinatesInPackit function.
// Now accepts a dynamic dateKey pulled from the packit's data.
const updateCoordinatesInPackit = async ({
  packitId,
  elementType,     // Must be one of: "thingstodo", "placestoeat", "placestostay"
  sectionIndex,    // The index in the corresponding root array
  dateKey,         // Dynamically pulled from the item (e.g., item.date)
  entryIndex,      // The index within that date's array
  lat,
  lng,
  coordinateProbability = 1.0,
  usesNestedObject = false
}) => {
  console.log(
    `Updating coordinates for packitId ${packitId} for elementType '${elementType}' at section ${sectionIndex}, date ${dateKey}, entry ${entryIndex}. New coords: (${lat}, ${lng})`
  );
  try {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) {
      console.error("No token found; cannot update coordinates.");
      return;
    }
    
    // Build the patch payload dynamically using the helper function.
    const patchPayload = buildCoordinatePatch(
      elementType,
      sectionIndex,
      dateKey,
      entryIndex,
      { latitude: lat, longitude: lng, coordinateProbability },
      usesNestedObject
    );
    
    await axios.patch(
      `${apiUrl}/briefings/${packitId}`,
      patchPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json-patch+json"
        }
      }
    );
    console.log("Coordinates updated successfully for packitId", packitId);
  } catch (err) {
    console.error(`Failed to update coordinates for packitId ${packitId}:`, err);
  }
};

  
  
  
  // Utility function to fetch lat/lng from an address
  const geocodeAddress = async (address) => {
    console.log(`Calling Google Geocode API for address: ${address}`);
    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        { params: { address, key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY } }
      );
      if (response.data.results.length > 0) {
        const loc = response.data.results[0].geometry.location;
        console.log(`Google Geocode API returned:`, loc);
        return loc;
      }
      console.warn(`No geocode results for address: ${address}`);
    } catch (error) {
      console.error(`Error geocoding address ${address}:`, error);
    }
    return null;
  };
  
  useEffect(() => {
    if (isLoaded && mapRef.current && window.google) {
      infoWindowRef.current = new window.google.maps.InfoWindow({
        // initial content is empty
      });
    }
  }, [isLoaded]);


  
  // A helper function to decide which element type a record belongs to.
// You can adjust the logic below to match your schema.
// For instance, if the object has a "thingsToDo" key it is a thingstodo record;
// if it has a "coordinates" key alongside "bookingRequired" and type "Hotel", it might be placestostay;
// if it has a "restaurants" key or a known "dining" type, assume placestoeat.
const determineElementType = (place) => {
  // 1) if we explicitly passed a category, use that
  if (place.category) {
    const cat = place.category.toLowerCase();
    if (cat === 'placestostay') {
      console.log(`determineElementType: "${place.name}" category explicit "placesToStay".`);
      return 'placestostay';
    }
    if (cat === 'placestoeat') {
      console.log(`determineElementType: "${place.name}" category explicit "placesToEat".`);
      return 'placestoeat';
    }
    if (cat === 'thingstodo') {
      console.log(`determineElementType: "${place.name}" category explicit "thingsToDo".`);
      return 'thingstodo';
    }
  }

  // 2) fallback to your old heuristics
  if (place.hasOwnProperty("thingsToDo")) {
    console.log(`determineElementType: Identified "${place.name}" as thingstodo because it has a "thingsToDo" key.`);
    return "thingstodo";
  }
  if (place.type && place.type.toLowerCase().includes("hotel")) {
    console.log(`determineElementType: Identified "${place.name}" as placestostay based on type "${place.type}".`);
    return "placestostay";
  }
  if (place.type && place.type.toLowerCase().includes("dine")) {
    console.log(`determineElementType: Identified "${place.name}" as placestoeat based on type "${place.type}".`);
    return "placestoeat";
  }

  // 3) last resort
  console.log(`determineElementType: Defaulting "${place.name}" to thingstodo.`);
  return "thingstodo";
};




  const handleMarkerClick = (markerData) => {
    console.log("Rendering InfoWindow for marker:", markerData); // New log added
    // Ensure the InfoWindow exists. If it doesn't, create a new one.


    // Build the content for the InfoWindow.
    const content = `
    <div class="custom-info-window">
      <h4>${markerData.name || "No Name Provided"}</h4>
      ${markerData.address ? `<p><strong>Address:</strong> ${markerData.address}</p>` : ""}
      ${markerData.description ? `<p><strong>Description:</strong> ${markerData.description}</p>` : ""}
      ${markerData.website ? `<p><strong>Website:</strong> <a href="${markerData.website}" target="_blank" rel="noopener noreferrer">${markerData.website}</a></p>` : ""}
    </div>
  `;

  if (!infoWindowRef.current && window.google && mapRef.current) {
    infoWindowRef.current = new window.google.maps.InfoWindow({
      content: content,
      ariaLabel: "Uluru",
    });
  }
  if (!infoWindowRef.current) {
    console.error("InfoWindow instance is not available.");
    return;
  }

    infoWindowRef.current.setContent(content);



    // If the markerData has a markerInstance, use it as an anchor.
    if (markerData.markerInstance) {
      console.log("Rendering InfoWindow for marker in if statement:", infoWindowRef.current.content);
      infoWindowRef.current.open({
        anchor: markerData.markerInstance,
        map: mapRef.current,
        content: content,
        ariaLabel: "Uluru",
      });
    } else {
      // Fallback to using the geographic position.
      console.log("Rendering InfoWindow for marker in else statement:", content);
      infoWindowRef.current.setPosition(markerData.position);
      infoWindowRef.current.open(mapRef.current);
    }
  };
  
  // Build a lookup map for destination coordinates
  const buildDestinationLookup = () => {
    const destinationMap = new Map();
    destinations.forEach(dest => {
      const baseName = dest.address?.split(",")[0].trim() || dest.locationId;
      destinationMap.set(baseName.toLowerCase(), {
        lat: dest.coordinates?.latitude,
        lng: dest.coordinates?.longitude,
      });
      destinationMap.set(dest.locationId.toLowerCase(), {
        lat: dest.coordinates?.latitude,
        lng: dest.coordinates?.longitude,
      });
    });
    return destinationMap;
  };
  useEffect(() => {
    let isCancelled = false;
    if (!places || places.length === 0) return;
  
    const destinationsMap = buildDestinationLookup();
  
    const processPlace = async (place) => {
      // Check for a valid date; if missing, log a warning and skip update.
      
      // If coordinates exist and have sufficient probability, use them.
      if (
        place.lat &&
        place.lng &&
        (place.coordinateProbability === undefined || place.coordinateProbability >= 0.7)
      ) {
        console.log(`processPlace: Marker "${place.name}" using existing coordinates: lat:${place.lat}, lng:${place.lng}`);
        return { 
          ...place, 
          position: { 
            lat: parseFloat(place.lat), 
            lng: parseFloat(place.lng) 
          } 
        };
      }
      
      // Try to get coordinates from the destination lookup.
      const matched = destinationsMap.get(place.name?.toLowerCase());
      if (matched) {
        console.log(`processPlace: Marker "${place.name}" got coordinates from destination lookup:`, matched);
        return { 
          ...place, 
          position: { 
            lat: parseFloat(matched.lat), 
            lng: parseFloat(matched.lng) 
          } 
        };
      }
      
      // If an address exists, geocode it.
      if (place.address) {
        console.log(`processPlace: Marker "${place.name}" requires geocoding. Address: ${place.address}`);
        const location = await geocodeAddress(place.address);
        if (location) {
          console.log(`processPlace: Geocoding successful for "${place.name}":`, location);
          
          // Determine element type dynamically.
          const elementType = determineElementType(place);
          console.log(`processPlace: Determined elementType for "${place.name}" as "${elementType}".`);
          
          // Use the dynamic indices from the normalized record.
          const dynamicDateKey = place.date; // Must exist otherwise we already skipped the record.
          const dynamicSectionIndex = (typeof place.sectionIndex !== 'undefined') ? place.sectionIndex : 0;
          const dynamicEntryIndex = (typeof place.entryIndex !== 'undefined') ? place.entryIndex : 0;
          console.log(`processPlace: Using sectionIndex=${dynamicSectionIndex}, entryIndex=${dynamicEntryIndex} for "${place.name}" on date "${dynamicDateKey}".`);
          
          await updateCoordinatesInPackit({
            packitId,           // Unique packit identifier
            elementType,                 // Determined dynamically ("thingstodo", "placestostay", or "placestoeat")
            sectionIndex: dynamicSectionIndex,
            dateKey: dynamicDateKey,
            entryIndex: dynamicEntryIndex,
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lng),
            // Set usesNestedObject accordingly (e.g., true for placestoeat if needed)
            usesNestedObject: false      
          });
          
          return {
            ...place,
            position: {
              lat: parseFloat(location.lat),
              lng: parseFloat(location.lng)
            }
          };
        } else {
          console.warn(`processPlace: Geocoding failed for "${place.name}" with address: ${place.address}`);
        }
      }
      
      console.warn(`processPlace: Marker "${place.name}" skipped—no valid coordinates or address.`);
      return null;
    };
    
  
    const processMarkers = async () => {
      const markers = await Promise.all(places.map(processPlace));
      const validMarkers = markers.filter((marker) => marker !== null);
    
      console.log(`Valid markers after filtering: ${validMarkers.length}`);
      validMarkers.forEach((m, i) => {
        console.log(`Valid Marker #${i}: ${m.name}, Position:`, m.position);
      });
    
      if (!isCancelled) {
        setGeocodedMarkers(validMarkers);
      }
    };
    
  
    processMarkers();
  
    return () => {
      isCancelled = true;
    };
  }, [places, destinations]);
  
  
  
  
  
  useEffect(() => {
    console.log("Running map centering effect...");
  
    if (!destinations || destinations.length === 0) {
      console.log("No destinations found.");
      return;
    }
  
    // Log all destinations
    console.log("Destinations available:", destinations);
  
    const destinationsMap = buildDestinationLookup();
    console.log("Built destination lookup:", destinationsMap);
  
    // Priority 1: Find the first destination that is NOT marked as start or end (with coordinates)
    let firstValidDestination = destinations.find(dest => 
      !dest.isStart && !dest.isEnd && 
      dest.coordinates && dest.coordinates.latitude && dest.coordinates.longitude
    );
  
    // Priority 2: If no non-start/end destination found, use the first destination (even if start/end)
    if (!firstValidDestination && destinations.length > 0) {
      firstValidDestination = destinations.find(dest => 
        dest.coordinates && dest.coordinates.latitude && dest.coordinates.longitude
      );
    }
  
    // Priority 3: If still no destination with coordinates, fall back to DEFAULT_CENTER
    if (!firstValidDestination) {
      console.log("No valid destination found with coordinates, using default center.");
      return; // Will use DEFAULT_CENTER from useState initialization
    }
  
    console.log("First valid destination found:", firstValidDestination);
  
    // Extract coordinates directly from destination
    const matchedLocation = {
      lat: parseFloat(firstValidDestination.coordinates.latitude),
      lng: parseFloat(firstValidDestination.coordinates.longitude)
    };
  
    if (matchedLocation.lat && matchedLocation.lng) {
      console.log("Matched location found:", matchedLocation);
      setMapCenter(matchedLocation); // Update map center dynamically
    } else {
      console.log("Invalid coordinates in destination.");
    }
  }, [destinations]); // Removed itinerary from dependencies to prevent infinite loops


  

  // Geocode itinerary using the destination lookup map
  useEffect(() => {
    if (!itinerary || itinerary.length === 0) return;
    const destinationsMap = buildDestinationLookup();
  
    const updatedItinerary = itinerary.map((stop) => {
      let start = stop.departureLocationId 
        ? destinationsMap.get(stop.departureLocationId.toLowerCase()) 
        : null;
      let end = stop.arrivalLocationId 
        ? destinationsMap.get(stop.arrivalLocationId.toLowerCase()) 
        : null;
      // Parse coordinates to numbers if found
      if (start) {
        start = { lat: parseFloat(start.lat), lng: parseFloat(start.lng) };
      }
      if (end) {
        end = { lat: parseFloat(end.lat), lng: parseFloat(end.lng) };
      }
      // If no specific departure/arrival, fallback to locationId if available.
      if (!start && !end && stop.locationId) {
        const pos = destinationsMap.get(stop.locationId.toLowerCase());
        if (pos) {
           start = { lat: parseFloat(pos.lat), lng: parseFloat(pos.lng) };
           end = start;
        }
      }
      return { ...stop, startPosition: start, endPosition: end || start };
    }).filter(item => item.startPosition && item.endPosition);
  
    console.log("Updated itinerary: ", updatedItinerary);
    setGeocodedItinerary(updatedItinerary);
  }, [itinerary, destinations]);
  
  

  // Generate travel routes
// Generate travel routes
useEffect(() => {
  if (geocodedItinerary.length < 1 || !isLoaded || !window.google || !window.google.maps) return;

  const directionsService = new window.google.maps.DirectionsService();
  const newRoutes = [];

  const processRoute = async (start, end, transportType) => {
    if (
      !start ||
      !end ||
      typeof start.lat !== "number" ||
      typeof start.lng !== "number" ||
      typeof end.lat !== "number" ||
      typeof end.lng !== "number"
    ) {
      console.error("Invalid coordinates for route:", start, end);
      return;
    }

    const googleTravelModes = {
      Car: "DRIVING",
      Bus: "TRANSIT",
      Train: "TRANSIT",
      Subway: "TRANSIT",
      Walking: "WALKING",
      Biking: "BICYCLING",
    };

    // For modes not supported by the Directions API (e.g., Plane/Ferry)
    if (transportType === "Plane" || transportType === "Ferry") {
      newRoutes.push({
        type: transportType.toLowerCase(),
        path: [start, end],
        color: transportType === "Plane" ? "#FF0000" : "#00AAFF",
        icon: transportType === "Plane" ? "✈️" : "🚢",
      });
      return;
    }

    const travelMode = googleTravelModes[transportType] || "DRIVING";
    const request = {
      origin: start,
      destination: end,
      travelMode,
    };

    // Attach transit options if applicable
    if (travelMode === "TRANSIT") {
      let transitOptions = {};
      switch (transportType) {
        case "Bus":
          transitOptions.modes = [window.google.maps.TransitMode.BUS];
          transitOptions.routingPreference = window.google.maps.TransitRoutePreference.FEWER_TRANSFERS;
          break;
        case "Train":
          transitOptions.modes = [window.google.maps.TransitMode.TRAIN];
          transitOptions.routingPreference = window.google.maps.TransitRoutePreference.LESS_WALKING;
          break;
        case "Subway":
          transitOptions.modes = [window.google.maps.TransitMode.SUBWAY];
          transitOptions.routingPreference = window.google.maps.TransitRoutePreference.FEWER_TRANSFERS;
          break;
        default:
          transitOptions.modes = [
            window.google.maps.TransitMode.BUS,
            window.google.maps.TransitMode.SUBWAY,
            window.google.maps.TransitMode.TRAIN,
            window.google.maps.TransitMode.LIGHT_RAIL,
            window.google.maps.TransitMode.RAIL,
          ];
          transitOptions.routingPreference = window.google.maps.TransitRoutePreference.LESS_WALKING;
      }
      request.transitOptions = transitOptions;
    }

    console.log("Route Request:", request);

    try {
      const result = await directionsService.route(request);
      // Check that the response has at least one route
      if (result.status === "OK" && result.routes && result.routes.length > 0) {
        newRoutes.push({
          type: "directions",
          directions: result,
        });
      } else {
        console.error(`Directions request failed with status: ${result.status}`);
      }
    } catch (error) {
      console.error(`Error fetching route for ${transportType}:`, error);
    }
  };

  const fetchRoutes = async () => {
    for (const item of geocodedItinerary) {
      if (item.transportType) {
        console.log(`Transport Type found: ${item.transportType}`);
        console.log("From:", item.startPosition, "To:", item.endPosition);
        await processRoute(item.startPosition, item.endPosition, item.transportType);
      }
    }
    setRoutes(newRoutes);
  };

  fetchRoutes();
}, [geocodedItinerary, isLoaded]);


  


    // onLoad: save map instance and create markers
    const onLoad = useCallback((map) => {
      mapRef.current = map;
    }, []);
  
    if (loadError) return <div>Error loading Google Maps</div>;
    if (!isLoaded || !window.google || !window.google.maps) return <div>Loading Map...</div>;

    return (
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        onLoad={onLoad}
        options={{
          mapTypeControl: true,
          fullscreenControl: true,
          streetViewControl: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          tilt: 0,
          mapId: "5994bfa3e3ab80cd",
        }}
      >
        {/* Render each marker using AdvancedMarker */}
        {geocodedMarkers.map((marker, index) => (
  <AdvancedMarker
    key={index}
    map={mapRef.current}
    position={marker.position}
    category={marker.category} // <-- pass the marker's category
    name={marker.name}
    address={marker.address}
    description={marker.description}
    website={marker.website}
    categoryIcons={categoryIcons}
  />
))}


        {/* Render travel routes as before */}
        {routes.map((route, index) =>
          route.type === "directions" ? (
            <DirectionsRenderer key={index} directions={route.directions} />
          ) : (
            <Polyline
              key={index}
              path={route.path}
              options={{
                strokeColor: route.color || "#0000FF",
                strokeOpacity: 0.7,
                strokeWeight: 4,
                icons: [{
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 4,
                    strokeColor: route.color || "#0000FF",
                  },
                  offset: "50%",
                }],
              }}
            />
          )
        )}
  
        {/* Optionally, render route midpoint markers */}
        {routes.map((route, index) =>
          route.path ? (
            <AdvancedMarker
              key={`icon-${index}`}
              map={mapRef.current}
              marker={{
                name: '',
                position: route.path[Math.floor(route.path.length / 2)],
                category: "destinations",
              }}
              onClick={() => {}}
              categoryIcons={categoryIcons}
            />
          ) : null
        )}
        <Legend /> {/* Add this line */}
      </GoogleMap>
    );
  };


class MapErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Map Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Error loading map. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}

const PackIt = () => {
  // -------------------------
  // 1) All useState hooks at the top
  // -------------------------
  const [packIts, setPackIts] = useState([]);
  const [openAlertPackIt, setOpenAlertPackIt] = useState(null);
  // We'll store currentPage as 1-based so it matches the button labels
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Configurable page size
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null); // Pagination token for current page
  const [paginationTokens, setPaginationTokens] = useState({}); // Store tokens for each page: { pageNumber: token }
  const [hasMorePages, setHasMorePages] = useState(false); // Whether there are more pages to load
  const [totalPackItsCount, setTotalPackItsCount] = useState(0); // Total count - accumulated as we fetch
  const [allPackItsLoaded, setAllPackItsLoaded] = useState(false); // Track if we've loaded all packits to get accurate count
  const [isLoadingPackIts, setIsLoadingPackIts] = useState(false); 
  const [selectedPackIt, setSelectedPackIt] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [alerts, setAlerts] = useState(0);
  const [alertViewedMap, setAlertViewedMap] = useState({});
  const apiUrl = process.env.REACT_APP_API_URL;
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [filters, setFilters] = useState({
    upcoming: false,
    shared: false,
    completed: false,
    favorites: false, // already added in previous update
    active: false,    // new filter for active pack‑its
  });
  const [alertViewed, setAlertViewed] = useState(false);
  const mapRef = React.useRef(null);
  const [activeTab, setActiveTab] = useState('itinerary');
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [userPlan, setUserPlan] = useState('');
  const [packitTrialComplete, setPackitTrialComplete] = useState(false);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [backgroundPackItData, setBackgroundPackItData] = useState(null);
  const [mapContainerStyle] = useState({
    width: "100%",
    height: "500px",
  });
  const [map, setMap] = useState(null);
  // Editing state management
  const [editingPackItId, setEditingPackItId] = useState(null);
  const [editingPackIt, setEditingPackIt] = useState(null);
  // Collapse/expand state for itinerary sections
  const [collapsedSections, setCollapsedSections] = useState({});
  const [originalPackItData, setOriginalPackItData] = useState(null);
  
  
  
  // Helper function to check if a packit is being edited
  const isPackItBeingEdited = (packItId) => {
    return editingPackItId === packItId?.toString().trim();
  };

  // Toggle collapse/expand for itinerary sections
  const toggleSection = (index, sectionType, prefix = 'day') => {
    const key = `${prefix}-${index}-${sectionType}`;
    setCollapsedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Check if a section is collapsed
  const isSectionCollapsed = (index, sectionType, prefix = 'day') => {
    const key = `${prefix}-${index}-${sectionType}`;
    return collapsedSections[key] || false;
  };

  // Collapse all sections
  const collapseAllSections = () => {
    const newCollapsedState = {};
    const currentPackIt = (isPackItBeingEdited(selectedPackIt?.id) ? editingPackIt : selectedPackIt);

    // Collapse itinerary sections
    const itineraryArray = normalizeItinerary(currentPackIt?.itinerary).itineraryArray;
    itineraryArray.forEach((day, index) => {
      newCollapsedState[`day-${index}-placesToStay`] = true;
      newCollapsedState[`day-${index}-thingsToDo`] = true;
      newCollapsedState[`day-${index}-placesToEat`] = true;
    });

    // Collapse destination sections
    const destinations = Array.isArray(currentPackIt?.itinerary?.itinerary?.destinations)
      ? currentPackIt.itinerary.itinerary.destinations
      : [];
    destinations.forEach((dest, index) => {
      newCollapsedState[`dest-${index}-full`] = true;
      newCollapsedState[`dest-${index}-departureInfo`] = true;
      newCollapsedState[`dest-${index}-placesToStay`] = true;
      newCollapsedState[`dest-${index}-thingsToDo`] = true;
      newCollapsedState[`dest-${index}-placesToEat`] = true;
      newCollapsedState[`dest-${index}-alerts`] = true;
    });

    setCollapsedSections(newCollapsedState);
  };

  // Expand all sections
  const expandAllSections = () => {
    setCollapsedSections({});
  };

  // Default all sections to collapsed when a Pack-It is loaded
  useEffect(() => {
    if (selectedPackIt?.id) {
      collapseAllSections();
    }
  }, [selectedPackIt?.id]);

  const thingstodoRaw = (editingPackIt ? editingPackIt : selectedPackIt)?.thingstodo || [];
  const placestostayRawForChat = (editingPackIt ? editingPackIt : selectedPackIt)?.placestostay;
  const normalizedActivities = useMemo(
    () => normalizeThingsToDo(thingstodoRaw),
    // only recompute when the raw data actually changes
    [thingstodoRaw]
  );

  const hotelStayChatSuggestionContext = useMemo(() => {
    if (!Array.isArray(placestostayRawForChat) || placestostayRawForChat.length === 0) {
      return null;
    }
    const stays = normalizePlacesToStay(placestostayRawForChat, '', '');
    const stay = stays.find((s) => s.name && String(s.name).trim());
    if (!stay) return null;
    const hotelName = truncateForChatChip(stay.name, 52);
    const locationLabel = formatStayLocationForChatPrompt(stay);
    return { hotelName, locationLabel };
  }, [placestostayRawForChat]);

  const sampleHotelNameForChat = hotelStayChatSuggestionContext?.hotelName ?? null;

  // WebSocket / Chat
  const { socket, clientId, connectionId, setConnectionId } = useWebSocket();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(null); // Track which packitId is generating download
  const [pdfGenerationError, setPdfGenerationError] = useState({}); // Store error messages by packitId: { packitId: "error message" }
  const [downloadFormatModalPackitId, setDownloadFormatModalPackitId] = useState(null); // Show format picker for this packit
  const [downloadFormatForPackit, setDownloadFormatForPackit] = useState({}); // { packitId: 'pdf'|'csv'|'kml' } - format requested when initiating
  const [errorMessage, setErrorMessage] = useState('');
  
  // Suggested prompts to showcase chatbot capabilities (include packit name when available)
  const packitNameForSuggestions = selectedPackIt?.name || (packIts?.length > 0 ? packIts[0].name : null);
  const CHAT_SUGGESTIONS = useMemo(() => {
    const n = packitNameForSuggestions;
    const ctx = hotelStayChatSuggestionContext;
    const h = ctx?.hotelName;
    const loc = ctx?.locationLabel;
    let hotelOnlyStayLabel;
    if (h && loc && n) {
      hotelOnlyStayLabel = `Make ${h} my only place to stay in ${loc} for my ${n} trip`;
    } else if (h && loc) {
      hotelOnlyStayLabel = `Make ${h} my only place to stay in ${loc} for this trip`;
    } else if (h && n) {
      hotelOnlyStayLabel = `Make ${h} my only place to stay for my ${n} trip`;
    } else if (h) {
      hotelOnlyStayLabel = `Make ${h} my only place to stay for this trip`;
    } else if (n) {
      hotelOnlyStayLabel = `Help me use a single hotel for each stop on my ${n} trip`;
    } else {
      hotelOnlyStayLabel = 'Help me use a single hotel for each stop on this trip';
    }

    return [
      { icon: 'fa-cloud-sun', label: n ? `What's the weather like in Paris this time of year?` : "What's the weather like in Norway in the summer?", category: 'Research' },
      { icon: 'fa-history', label: n ? `What was the weather like a year ago today for the destinations in my ${n}?` : 'What was the weather like a year ago today in Paris?', category: 'Research' },
      { icon: 'fa-utensils', label: n ? `Add the best seafood restaurants close by for each night of my ${n}` : 'Add the best seafood restaurants for each night of my trip', category: 'Edit PackIt' },
      { icon: 'fa-plus', label: n ? `Add a new day to my ${n}'s itinerary` : 'Add a new day to my itinerary', category: 'Edit PackIt' },
      { icon: 'fa-suitcase', label: n ? `Add sunscreen to my ${n}'s packing list` : 'Add sunscreen to my packing list', category: 'Edit PackIt' },
      { icon: 'fa-hotel', label: hotelOnlyStayLabel, category: 'Edit PackIt' },
      { icon: 'fa-search', label: n ? `Find top-rated activities near my ${n}'s destinations` : 'Find top-rated activities near my destinations', category: 'Research' },
    ];
  }, [packitNameForSuggestions, hotelStayChatSuggestionContext]);

  // Chat request limits tracking
  const [chatRequests, setChatRequests] = useState(() => {
    // Load from localStorage on component mount
    const saved = localStorage.getItem('chatRequests');
    return saved ? JSON.parse(saved) : [];
  });
  const [updateFlash, setUpdateFlash] = useState(false);
  const [displayMode, setDisplayMode] = useState('horizontal'); // 'horizontal', 'grid', 'list', 'accordion'
  const [expandedSections, setExpandedSections] = useState({});
  const [temperatureUnit, setTemperatureUnit] = useState('F');
  const [expandedWeatherDetails, setExpandedWeatherDetails] = useState({});
  // Completion tracking for notifications
  const [packitCompletions, setPackitCompletions] = useState({}); // { packitId: completionPercentage }
  const [completionNotifications, setCompletionNotifications] = useState([]); // Array of { packitId, packitName, timestamp }
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState(null);
  const [detailModalType, setDetailModalType] = useState(null); // 'thingstodo', 'placestoeat', 'placestostay'

  // React Router
  const navigate = useNavigate();
  const location = useLocation();

    // Messages end ref for chat scrolling
  const messagesEndRef = useRef(null);
  // Ref to track the last bedrock message for scrolling to top
  const lastBedrockMessageRef = useRef(null);
  
  // Packing list scroll position preservation
  const packingListRef = useRef(null);
  const [packingListScrollPosition, setPackingListScrollPosition] = useState(0);
  
  // Preserve scroll position during packing list updates
  const preserveScrollPosition = useCallback(() => {
    if (packingListRef.current) {
      setPackingListScrollPosition(packingListRef.current.scrollTop);
    }
  }, []);
  
  const restoreScrollPosition = useCallback(() => {
    if (packingListRef.current && packingListScrollPosition > 0) {
      packingListRef.current.scrollTop = packingListScrollPosition;
    }
  }, [packingListScrollPosition]);
  
  // Restore scroll position after editing state changes
  useEffect(() => {
    if (editingPackIt && packingListScrollPosition > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        restoreScrollPosition();
      });
    }
  }, [editingPackIt, restoreScrollPosition, packingListScrollPosition]);
  
  // Removed local edits clearing - no longer needed with individual component state

  const clearChat = () => {
    setMessages([]);
  };

  // Put this above or in a separate file ExpandableCard.jsx:
const ExpandableCard = ({ title, shortDescription, expandedContent }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  return (
    <div className="expandable-card" onClick={toggleExpand}>
      <p className="expandable-title"><strong>{title}</strong></p>
      <p className="expandable-short">{shortDescription}</p>
      {isExpanded && (
        <div className="expandable-details">
          {expandedContent}
        </div>
      )}
    </div>
  );
};

const safeCuisineDisplay = (cuisine) => {
  if (Array.isArray(cuisine)) {
    return cuisine.join(", ");
  } else if (typeof cuisine === "string") {
    return cuisine; // Already a string, just return it
  } else {
    return "Unknown Cuisine"; // Default fallback
  }
};

function fuzzyMatch(str1, str2) {
  //console.log("location 1", str1);
  //console.log("location 2", str2);
  if (!str1 || !str2) return false;
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  return s1.includes(s2) || s2.includes(s1);
}

// Helper functions for different display modes
const toggleSectionExpanded = (sectionId) => {
  setExpandedSections(prev => ({
    ...prev,
    [sectionId]: !prev[sectionId]
  }));
};

// Add global display mode state
const [globalDisplayMode, setGlobalDisplayMode] = useState('horizontal');
const [weatherDisplayMode, setWeatherDisplayMode] = useState('day'); // 'day' or 'location'
const [weatherDataSourceExpanded, setWeatherDataSourceExpanded] = useState(false);

const renderDisplayModeSelector = () => (
  <div className="display-mode-selector">

    <select 
      value={globalDisplayMode} 
      onChange={(e) => setGlobalDisplayMode(e.target.value)}
      className="display-mode-select"
    >
      <option value="horizontal">Horizontal Scroll</option>
      <option value="grid">Grid Layout</option>
      <option value="list">List View</option>
      <option value="accordion">Accordion</option>
    </select>
  </div>
);

const renderWeatherDisplayModeSelector = () => (
  <div className="weather-display-mode-selector">

    <select 
      value={weatherDisplayMode} 
      onChange={(e) => setWeatherDisplayMode(e.target.value)}
      className="weather-display-mode-select"
    >
      <option value="day">By Day</option>
      <option value="location">By Location</option>
    </select>
  </div>
);

const renderTemperatureUnitSelector = () => (
  <div className="temperature-unit-selector">

    <select
      value={temperatureUnit}
      onChange={(e) => setTemperatureUnit(e.target.value)}
      className="temperature-unit-select"
    >
      <option value="F">Fahrenheit</option>
      <option value="C">Celsius</option>
    </select>
  </div>
);

const renderCollapseAllButton = () => {
  const allExpanded = Object.values(expandedWeatherDetails).every(Boolean);
  const hasWeatherData = selectedPackIt.weatherFull && selectedPackIt.weatherFull.length > 0;
  
  if (!hasWeatherData) return null;
  
  return (
    <div className="collapse-all-selector">
      <button
        className="collapse-all-button"
        onClick={() => {
          if (allExpanded) {
            // Collapse all
            const newState = {};
            selectedPackIt.weatherFull.forEach((_, index) => {
              newState[index] = false;
            });
            setExpandedWeatherDetails(newState);
          } else {
            // Expand all
            const newState = {};
            selectedPackIt.weatherFull.forEach((_, index) => {
              newState[index] = true;
            });
            setExpandedWeatherDetails(newState);
          }
        }}
      >
        <i className={`fas fa-${allExpanded ? 'compress' : 'expand'}`}></i>
        <span>{allExpanded ? 'Collapse All' : 'Expand All'}</span>
      </button>
    </div>
  );
};

const renderItemsInMode = (items, renderItem, sectionId = null) => {
  if (!items || items.length === 0) return null;

  // Use global display mode instead of local displayMode
  switch (globalDisplayMode) {
    case 'grid':
      return (
        <div className="items-grid">
          {items.map((item, index) => (
            <div key={index} className="grid-item">
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      );
    
    case 'list':
      return (
        <div className="items-list">
          {items.map((item, index) => (
            <div key={index} className="list-item">
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      );
    
    case 'accordion':
      if (!sectionId) return null;
      const isExpanded = expandedSections[sectionId];
      return (
        <div className="accordion-section">
          <button 
            className="accordion-header"
            onClick={() => toggleSectionExpanded(sectionId)}
          >
            <span>{sectionId} ({items.length} items)</span>
            <span className="accordion-icon">{isExpanded ? '−' : '+'}</span>
          </button>
          {isExpanded && (
            <div className="accordion-content">
              {items.map((item, index) => (
                <div key={index} className="accordion-item">
                  {renderItem(item, index)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    
    case 'horizontal':
    default:
      return (
        <div className="horizontal-row">
          {items.map((item, index) => (
            <div key={index} className="expandable-card">
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      );
  }
};

const acknowledgeAlerts = async (packitId) => {
  try {
    const patchPayload = [
      { op: "replace", path: "/alertsAcknowledged", value: true }
    ];
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) return;
    await axios.patch(
      `${apiUrl}/briefings/${packitId}`,
      patchPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json-patch+json"
        }
      }
    );
    console.log("Alerts acknowledged updated for", packitId);
    // Update local state so that the specific pack-it has alerts acknowledged.
    setPackIts(prevPackIts =>
      sortPackIts(prevPackIts.map(p =>
        p.id === packitId ? { ...p, alertsAcknowledged: true } : p
      ))
    );
  } catch (err) {
    console.error("Error acknowledging alerts for packitId", packitId, err);
  }
};

const handleBookmark = async (packitId) => {
  try {
    // Find the pack-it in state to determine its current favorite status.
    const packit = packIts.find((p) => p.id === packitId);
    if (!packit) {
      console.error("Pack-it not found:", packitId);
      return;
    }
    // Toggle the favorite state.
    const newFavoriteState = !packit.isFavorite;
    
    // Construct JSON Patch payload using RFC 6902 format.
    const patchPayload = [
      { op: "replace", path: "/isFavorite", value: newFavoriteState }
    ];
    
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) return;

    await axios.patch(
      `${apiUrl}/briefings/${packitId}`,
      patchPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json-patch+json" // ensures the server parses the payload correctly
        }
      }
    );
    console.log("Bookmark updated successfully for", packitId);
    
    // Update local state so that the bookmark icon reflects the new favorite status.
    setPackIts(prevPackIts =>
      sortPackIts(prevPackIts.map(p =>
        p.id === packitId ? { ...p, isFavorite: newFavoriteState } : p
      ))
    );
  } catch (error) {
    console.error("Error updating bookmark:", error);
  }
};

const handleActive = async (packitId) => {
  try {
    // Find the pack-it in state to determine its current active status.
    const packit = packIts.find((p) => p.id === packitId);
    if (!packit) {
      console.error("Pack-it not found:", packitId);
      return;
    }

    // If trying to set to active, validate the conditions
    if (!packit.isActive) {
      const today = new Date();
      const tripDate = new Date(packit.date);
      
      // Validation 1: Check if trip date is more than 2 weeks away
      const daysUntilTrip = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilTrip > 14) {
        alert("Cannot set packit to active: Trip date is more than 2 weeks away. You can only activate packits within 2 weeks of the trip date.");
        return;
      }
      
      // Validation 2: Check if any other packit is already active
      const hasActivePackit = packIts.some(p => p.id !== packitId && p.isActive);
      if (hasActivePackit) {
        alert("Cannot set packit to active: Only one packit can be active at a time. Please deactivate the current active packit first.");
        return;
      }
      
      // Validation 3: Check if trip end date is not in the past
      const endDate = packit.endDate ? new Date(packit.endDate) : tripDate;
      const daysUntilTripEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilTripEnd < 0) {
        alert("Cannot set packit to active: Trip end date has already passed.");
        return;
      }
      
      // Validation 4: Check if packit is not completed
      if (packit.isCompleted) {
        alert("Cannot set packit to active: Completed packits cannot be set to active.");
        return;
      }
    }

    // Toggle the active state.
    const newActiveState = !packit.isActive;
    
    // Construct JSON Patch payload using RFC 6902 format.
    const patchPayload = [
      { op: "replace", path: "/isActive", value: newActiveState }
    ];
    
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) return;

    await axios.patch(
      `${apiUrl}/briefings/${packitId}`,
      patchPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json-patch+json" // ensures the server parses the payload correctly
        }
      }
    );
    console.log("Active status updated successfully for", packitId);
    
    // Update local state so that the active icon reflects the new active status.
    setPackIts(prevPackIts =>
      sortPackIts(prevPackIts.map(p =>
        p.id === packitId ? { ...p, isActive: newActiveState } : p
      ))
    );
  } catch (error) {
    console.error("Error updating active status:", error);
  }
};

const handleComplete = async (packitId) => {
  try {
    // Find the pack-it in state to determine its current completed status.
    const packit = packIts.find((p) => p.id === packitId);
    if (!packit) {
      console.error("Pack-it not found:", packitId);
      return;
    }
    // Toggle the completed state.
    const newCompletedState = !packit.isCompleted;
    
    // Construct JSON Patch payload using RFC 6902 format.
    const patchPayload = [
      { op: "replace", path: "/isCompleted", value: newCompletedState }
    ];
    
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) return;

    await axios.patch(
      `${apiUrl}/briefings/${packitId}`,
      patchPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json-patch+json" // ensures the server parses the payload correctly
        }
      }
    );
    console.log("Completed status updated successfully for", packitId);
    
    // Update local state so that the completed icon reflects the new completed status.
    setPackIts(prevPackIts =>
      sortPackIts(prevPackIts.map(p =>
        p.id === packitId ? { ...p, isCompleted: newCompletedState } : p
      ))
    );
  } catch (error) {
    console.error("Error updating completed status:", error);
  }
};

const handleRefresh = async (packitId) => {
  try {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) return;
    
    // POST request to refresh the packit with specific refresh options
    const response = await axios.post(
      `${apiUrl}/briefings/${packitId}/refresh`,
      {
        alertRefresh: true,
        wxRefresh: true,
        simpleRefresh: false
      },
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      }
    );
    
    if (response.status === 200) {
      console.log("Packit refresh initiated successfully for", packitId);
      alert("Packit is being refreshed! This may take a few moments to complete.");
    }
  } catch (error) {
    console.error("Error refreshing packit:", error);
    alert("Failed to initiate packit refresh. Please try again.");
  }
};

  const handleDownloadClick = (packitId) => {
    setDownloadFormatModalPackitId(packitId);
  };

  const handleDownload = async (packitId, format = 'pdf') => {
    setDownloadFormatModalPackitId(null);
    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      if (!token) return;
      
      // Store format for when WebSocket returns (used by download helper)
      setDownloadFormatForPackit(prev => ({ ...prev, [packitId]: format }));
      
      // Clear any previous errors for this packit
      setPdfGenerationError(prev => {
        const updated = { ...prev };
        delete updated[packitId];
        return updated;
      });
      // Set loading state
      setPdfGenerating(packitId);
      
      // POST request to initiate export (async, don't wait for response)
      await axios.post(
        `${apiUrl}/briefings/${packitId}/share`,
        { packitId, format },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log(`${format.toUpperCase()} generation initiated for`, packitId, "- waiting for WebSocket notification");
    } catch (error) {
      console.error("Error initiating download:", error);
      setPdfGenerating(null);
      setPdfGenerationError(prev => ({
        ...prev,
        [packitId]: `Failed to start download: ${error.message || 'Unknown error'}`
      }));
    }
  };

  // Helper function to download file from GET request (PDF, CSV, or KML)
  const downloadFromUrl = useCallback(async (packitId, filename = null, format = 'pdf') => {
    const formatConfig = {
      pdf: { mime: 'application/pdf', ext: 'pdf' },
      csv: { mime: 'text/csv', ext: 'csv' },
      kml: { mime: 'application/vnd.google-earth.kml+xml', ext: 'kml' }
    };
    const config = formatConfig[format] || formatConfig.pdf;

    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('No authentication token');
      }
      
      const base64ToBlob = (base64, mimeType) => {
        const cleanBase64 = (typeof base64 === 'string' ? base64 : String(base64)).replace(/\s/g, '');
        const binaryString = atob(cleanBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: mimeType });
      };
      
      const response = await axios.get(
        `${apiUrl}/briefings/${packitId}/share`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { format },
          responseType: 'arraybuffer'
        }
      );
      
      const arr = new Uint8Array(response.data);
      const isRawPdf = format === 'pdf' && arr.length >= 4 && arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46;
      
      let blob;
      if (isRawPdf) {
        blob = new Blob([response.data], { type: config.mime });
      } else if (format === 'csv' || format === 'kml') {
        const text = new TextDecoder().decode(response.data);
        let base64 = text.trim();
        if (base64.startsWith('{')) {
          try {
            const json = JSON.parse(base64);
            base64 = json.body ?? json.data ?? json.base64 ?? json.base64Encoded ?? json.csv ?? json.kml ?? json;
            if (typeof base64 !== 'string') base64 = String(base64);
          } catch { /* use as-is */ }
        }
        // If response is base64, decode to blob; otherwise use raw text
        if (base64 && /^[A-Za-z0-9+/=]+$/.test(base64.replace(/\s/g, ''))) {
          blob = base64ToBlob(base64, config.mime);
        } else {
          blob = new Blob([text], { type: config.mime });
        }
      } else {
        const text = new TextDecoder().decode(response.data);
        let base64 = text.trim();
        if (base64.startsWith('{')) {
          try {
            const json = JSON.parse(base64);
            base64 = json.pdf ?? json.body ?? json.data ?? json.base64 ?? json;
            if (typeof base64 !== 'string') base64 = String(base64);
          } catch { /* use as-is */ }
        }
        if (!base64) throw new Error('Invalid response: no file data');
        blob = base64ToBlob(base64, config.mime);
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `packit-${packitId}.${config.ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`${format.toUpperCase()} downloaded successfully for`, packitId);
      setPdfGenerationError(prev => {
        const updated = { ...prev };
        delete updated[packitId];
        return updated;
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      setPdfGenerationError(prev => ({
        ...prev,
        [packitId]: `Failed to download: ${error.message || 'Unknown error'}`
      }));
    }
  }, [apiUrl]);

const handleDelete = async (packitId) => {
  try {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token) return;
    // DELETE request using the packit id:
    await axios.delete(`${apiUrl}/briefings/${packitId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log("Pack-it deleted:", packitId);
    // Update state to remove the deleted pack-it.
    setPackIts((prev) => prev.filter((p) => p.id !== packitId));
  } catch (error) {
    console.error("Error deleting packit:", error);
  }
};

const handleEdit = async (packitId) => {
  try {
    console.log("Entering edit mode for packit:", packitId);
    
    // If we're already editing this packit, do nothing
    if (editingPackItId === packitId.toString().trim()) {
      return;
    }
    
    // If we're editing a different packit, cancel the current edit first
    if (editingPackItId && editingPackItId !== packitId.toString().trim()) {
      handleCancelEdit();
    }
    
    // Get the current packit data
    const packitToEdit = selectedPackIt || packIts.find(p => p.id === packitId);
    
    if (!packitToEdit) {
      console.error("Packit not found for editing:", packitId);
      return;
    }
    
    // Store original data for cancel functionality
    setOriginalPackItData(JSON.parse(JSON.stringify(packitToEdit)));
    
    // Set editing state and ensure all packing items have IDs
    setEditingPackItId(packitId.toString().trim());
    setEditingPackIt(prev => {
      const newPackIt = JSON.parse(JSON.stringify(packitToEdit));
      
      // Simple approach - no UUID assignment needed
      
      return newPackIt;
    });
    
    console.log("Edit mode activated for packit:", packitToEdit.name);
  } catch (error) {
    console.error("Error entering edit mode:", error);
  }
};

const handleCancelEdit = () => {
  console.log("Cancelling edit mode");
  setEditingPackItId(null);
  setEditingPackIt(null);
  setOriginalPackItData(null);
};

const handleSaveEdit = async () => {
  // Declare packitId at function scope so it's available in catch block
  let packitId = null;
  
  try {
    console.log("Saving packit edits:", editingPackIt);
    console.log("EditingPackIt ID:", editingPackIt?.id);
    console.log("EditingPackIt packitid:", editingPackIt?.packitid);
    console.log("EditingPackIt packitId:", editingPackIt?.packitId);
    console.log("EditingPackIt type:", typeof editingPackIt?.id);
    
    if (!editingPackIt) {
      console.error("No packit data to save");
      alert("No changes to save.");
      return;
    }
    
    // Check for all possible ID property names
    const idProperty = editingPackIt.id || editingPackIt.packitid || editingPackIt.packitId;
    if (!idProperty) {
      console.error("No packit ID found in editingPackIt:", editingPackIt);
      alert("Packit ID is missing. Cannot save changes.");
      return;
    }
    
    // Ensure the ID is a string and not undefined
    packitId = String(idProperty);
    if (!packitId || packitId === 'undefined' || packitId === 'null') {
      console.error("Invalid packit ID:", packitId);
      alert("Invalid packit ID. Cannot save changes.");
      return;
    }
    
    // Basic validation
    if (!editingPackIt.name || editingPackIt.name.trim() === '') {
      alert("Packit name is required.");
      return;
    }
    
    if (editingPackIt.startDate && editingPackIt.endDate) {
      const startDate = new Date(editingPackIt.startDate);
      const endDate = new Date(editingPackIt.endDate);
      if (startDate > endDate) {
        alert("Start date cannot be after end date.");
        return;
      }
    }
    
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    
    if (!token) {
      console.error("No valid user token for saving packit");
      alert("Authentication required. Please log in again.");
      return;
    }
    
    const headers = {
      'Content-type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${token}`,
    };
    
    // Prepare the update payload using JSON Patch format - only send changed fields
    const updatePayload = [];
    
    // Compare with original data to only send changed fields
    if (originalPackItData) {
      // Check each field for changes
      if (editingPackIt.name !== originalPackItData.name) {
        updatePayload.push({ op: "replace", path: "/name", value: editingPackIt.name.trim() });
      }
      if (editingPackIt.description !== originalPackItData.description) {
        updatePayload.push({ op: "replace", path: "/description", value: editingPackIt.description?.trim() || '' });
      }
      if (editingPackIt.startDate !== originalPackItData.startDate) {
        updatePayload.push({ op: "replace", path: "/startDate", value: editingPackIt.startDate });
      }
      if (editingPackIt.endDate !== originalPackItData.endDate) {
        updatePayload.push({ op: "replace", path: "/endDate", value: editingPackIt.endDate });
      }
      if (editingPackIt.tripType !== originalPackItData.tripType) {
        updatePayload.push({ op: "replace", path: "/tripType", value: editingPackIt.tripType });
      }
      if (editingPackIt.isFlexibleDate !== originalPackItData.isFlexibleDate) {
        updatePayload.push({ op: "replace", path: "/isFlexibleDate", value: editingPackIt.isFlexibleDate });
      }
      if (editingPackIt.includesKids !== originalPackItData.includesKids) {
        updatePayload.push({ op: "replace", path: "/includesKids", value: editingPackIt.includesKids });
      }
      if (editingPackIt.includesPets !== originalPackItData.includesPets) {
        updatePayload.push({ op: "replace", path: "/includesPets", value: editingPackIt.includesPets });
      }
      if (editingPackIt.alertFrequency !== originalPackItData.alertFrequency) {
        updatePayload.push({ op: "replace", path: "/alertFrequency", value: editingPackIt.alertFrequency });
      }
      if (editingPackIt.packingNeeds !== originalPackItData.packingNeeds) {
        updatePayload.push({ op: "replace", path: "/packingNeeds", value: editingPackIt.packingNeeds });
      }
      if (JSON.stringify(editingPackIt.interests) !== JSON.stringify(originalPackItData.interests)) {
        updatePayload.push({ op: "replace", path: "/interests", value: editingPackIt.interests });
      }
      if (JSON.stringify(editingPackIt.checkpoints) !== JSON.stringify(originalPackItData.checkpoints)) {
        updatePayload.push({ op: "replace", path: "/checkpoints", value: editingPackIt.checkpoints });
      }
      if (editingPackIt.colorCategory !== originalPackItData.colorCategory) {
        updatePayload.push({ op: "replace", path: "/colorCategory", value: editingPackIt.colorCategory });
      }
      if (JSON.stringify(editingPackIt.itinerary) !== JSON.stringify(originalPackItData.itinerary)) {
        updatePayload.push({ op: "replace", path: "/itinerary", value: editingPackIt.itinerary });
      }
      if (JSON.stringify(editingPackIt.packingList) !== JSON.stringify(originalPackItData.packingList)) {
        updatePayload.push({ op: "replace", path: "/packingList", value: editingPackIt.packingList });
      }
      if (JSON.stringify(editingPackIt.thingstodo) !== JSON.stringify(originalPackItData.thingstodo)) {
        updatePayload.push({ op: "replace", path: "/thingstodo", value: editingPackIt.thingstodo });
      }
      if (JSON.stringify(editingPackIt.placestostay) !== JSON.stringify(originalPackItData.placestostay)) {
        updatePayload.push({ op: "replace", path: "/placestostay", value: editingPackIt.placestostay });
      }
      if (JSON.stringify(editingPackIt.placestoeat) !== JSON.stringify(originalPackItData.placestoeat)) {
        updatePayload.push({ op: "replace", path: "/placestoeat", value: editingPackIt.placestoeat });
      }
    } else {
      // Fallback: send all fields if no original data available
      updatePayload.push(
        { op: "replace", path: "/name", value: editingPackIt.name.trim() },
        { op: "replace", path: "/description", value: editingPackIt.description?.trim() || '' },
        { op: "replace", path: "/startDate", value: editingPackIt.startDate },
        { op: "replace", path: "/endDate", value: editingPackIt.endDate },
        { op: "replace", path: "/tripType", value: editingPackIt.tripType },
        { op: "replace", path: "/isFlexibleDate", value: editingPackIt.isFlexibleDate },
        { op: "replace", path: "/includesKids", value: editingPackIt.includesKids },
        { op: "replace", path: "/includesPets", value: editingPackIt.includesPets },
        { op: "replace", path: "/alertFrequency", value: editingPackIt.alertFrequency },
        { op: "replace", path: "/packingNeeds", value: editingPackIt.packingNeeds },
        { op: "replace", path: "/interests", value: editingPackIt.interests },
        { op: "replace", path: "/checkpoints", value: editingPackIt.checkpoints },
        { op: "replace", path: "/colorCategory", value: editingPackIt.colorCategory },
        { op: "replace", path: "/itinerary", value: editingPackIt.itinerary },
        { op: "replace", path: "/packingList", value: editingPackIt.packingList },
        { op: "replace", path: "/thingstodo", value: editingPackIt.thingstodo },
        { op: "replace", path: "/placestostay", value: editingPackIt.placestostay },
        { op: "replace", path: "/placestoeat", value: editingPackIt.placestoeat }
      );
    }
    
    // If no changes detected, don't send the request
    if (updatePayload.length === 0) {
      console.log("No changes detected, skipping save");
      alert("No changes to save.");
      return;
    }
    
    // Show loading state - using React state instead of DOM manipulation
    // This prevents interference with other buttons
    
    const response = await axios.patch(`${apiUrl}/briefings/${packitId}`, updatePayload, { headers });
    
    if (response.status === 200 || response.status === 204) {
      console.log("Packit updated successfully");
      
      // Update the selected packit with the new data
      setSelectedPackIt(editingPackIt);
      
      // Update the packits list
      setPackIts(prev => prev.map(p => 
        (p.id === packitId || p.packitid === packitId || p.packitId === packitId) ? { ...p, ...editingPackIt, lastEdited: new Date().toISOString() } : p
      ));
      
      // Exit edit mode
      setEditingPackItId(null);
      setEditingPackIt(null);
      setOriginalPackItData(null);
      
      console.log("Packit edit saved successfully");
      alert("Changes saved successfully!");
    } else {
      throw new Error('Failed to update packit');
    }
  } catch (error) {
    console.error("Error saving packit:", error);
    console.error("Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      packitId: packitId,
      editingPackIt: editingPackIt
    });
    
    // Reset button state - removed DOM manipulation to prevent interference
    
    if (error.response?.status === 401) {
      alert("Authentication expired. Please log in again.");
    } else if (error.response?.status === 403) {
      alert("You don't have permission to edit this packit.");
    } else if (error.response?.status === 404) {
      alert(`Packit not found (ID: ${packitId}). It may have been deleted or the ID is invalid.`);
    } else if (error.response?.status >= 500) {
      alert("Server error. Please try again later.");
    } else {
      alert(`Error saving packit: ${error.message}`);
    }
  }
};

// Helper functions for editing itinerary
const addItineraryDay = () => {
  if (!editingPackIt) return;
  
  const newDay = {
    day: (editingPackIt.itinerary?.itinerary?.itinerary?.length || 0) + 1,
    locationId: '',
    goal: '',
    date: ''
  };
  
  setEditingPackIt(prev => ({
    ...prev,
    itinerary: {
      ...prev.itinerary,
      itinerary: {
        ...prev.itinerary?.itinerary,
        itinerary: [...(prev.itinerary?.itinerary?.itinerary || []), newDay]
      }
    }
  }));
};

const removeItineraryDay = (dayIndex) => {
  if (!editingPackIt) return;
  
  setEditingPackIt(prev => ({
    ...prev,
    itinerary: {
      ...prev.itinerary,
      itinerary: {
        ...prev.itinerary?.itinerary,
        itinerary: prev.itinerary?.itinerary?.itinerary?.filter((_, index) => index !== dayIndex) || []
      }
    }
  }));
};

const updateItineraryDay = (dayIndex, field, value) => {
  if (!editingPackIt) return;
  
  setEditingPackIt(prev => ({
    ...prev,
    itinerary: {
      ...prev.itinerary,
      itinerary: {
        ...prev.itinerary?.itinerary,
        itinerary: prev.itinerary?.itinerary?.itinerary?.map((day, index) => 
          index === dayIndex ? { ...day, [field]: value } : day
        ) || []
      }
    }
  }));
};

const addPlaceToStay = (dayIndex, locationId) => {
  if (!editingPackIt) return;
  
  const newPlace = {
    id: uuidv4(), // Add unique ID
    name: 'New Place to Stay',
    type: 'Hotel',
    description: 'Enter description here',
    address: 'Enter address here',
    amenities: ['WiFi', 'Pool']
  };
  
  setEditingPackIt(prev => {
    const dayDate = prev.itinerary?.itinerary?.itinerary?.[dayIndex]?.date;
    console.log('Adding place to stay for day:', dayIndex, 'date:', dayDate, 'timestamp:', Date.now());

    // Check if there's already a placestostay entry for this destination
    const existingIndex = prev.placestostay?.findIndex(entry => 
      entry.destination === locationId
    );

    let newPlacestostay = [...(prev.placestostay || [])];

    if (existingIndex !== -1) {
      // Update existing entry
      const existingEntry = newPlacestostay[existingIndex];
      
      if (existingEntry.placesToStay && typeof existingEntry.placesToStay === 'object' && !Array.isArray(existingEntry.placesToStay)) {
        // Date-keyed structure: { placesToStay: { "2025-11-07": [...] } }
        const updatedPlacesToStay = { ...existingEntry.placesToStay };
        if (dayDate) {
          if (!updatedPlacesToStay[dayDate]) {
            updatedPlacesToStay[dayDate] = [];
          }
          // Check if this exact place already exists to prevent duplicates
          const existingPlace = updatedPlacesToStay[dayDate].find(place => place.id === newPlace.id);
          if (!existingPlace) {
            updatedPlacesToStay[dayDate].push(newPlace);
          }
        }
        newPlacestostay[existingIndex] = {
          ...existingEntry,
          placesToStay: updatedPlacesToStay
        };
      } else {
        // Add to existing placesToStay array
        const placesToStay = existingEntry.placesToStay || [];
        const existingPlace = placesToStay.find(place => place.id === newPlace.id);
        if (!existingPlace) {
          newPlacestostay[existingIndex] = {
            ...existingEntry,
            placesToStay: [...placesToStay, newPlace]
          };
        }
      }
    } else {
      // Create new entry
      if (dayDate) {
        // Create date-keyed structure
        newPlacestostay.push({
          destination: locationId,
          placesToStay: {
            [dayDate]: [newPlace]
          }
        });
      } else {
        // Create simple structure
        newPlacestostay.push({
          destination: locationId,
          placesToStay: [newPlace]
        });
      }
    }

    console.log('Updated placestostay:', newPlacestostay);
    
    return {
      ...prev,
      placestostay: newPlacestostay
    };
  });
};

const addThingToDo = (dayIndex, locationId) => {
  if (!editingPackIt) return;
  
  console.log('addThingToDo called:', { dayIndex, locationId, timestamp: Date.now() });
  
  const newActivity = {
    id: uuidv4(), // Add unique ID
    name: 'New Activity',
    type: 'Entertainment',
    description: 'Enter description here',
    address: 'Enter address here',
    website: 'https://example.com'
  };
  
  setEditingPackIt(prev => {
    // Get the date for the specific day
    const dayDate = prev.itinerary?.itinerary?.itinerary?.[dayIndex]?.date;
    console.log('Adding activity for day:', dayIndex, 'date:', dayDate);

    // Check if there's already a thingstodo entry for this destination
    const existingIndex = prev.thingstodo?.findIndex(entry => 
      entry.destination === locationId
    );

    let newThingstodo = [...(prev.thingstodo || [])];

    if (existingIndex !== -1) {
      // Update existing entry - always use date-keyed structure for consistency
      const existingEntry = newThingstodo[existingIndex];
      
      if (dayDate) {
        // Always use date-keyed structure
        const updatedThingsToDo = { ...existingEntry.thingsToDo };
        if (!updatedThingsToDo[dayDate]) {
          updatedThingsToDo[dayDate] = [];
        }
        // Check if this exact activity already exists to prevent duplicates
        const existingActivity = updatedThingsToDo[dayDate].find(act => act.id === newActivity.id);
        if (!existingActivity) {
          updatedThingsToDo[dayDate].push(newActivity);
        }
        newThingstodo[existingIndex] = {
          ...existingEntry,
          thingsToDo: updatedThingsToDo
        };
      } else {
        // Fallback to activities array if no date
        const activities = existingEntry.activities || existingEntry.thingsToDo || [];
        const existingActivity = activities.find(act => act.id === newActivity.id);
        if (!existingActivity) {
          newThingstodo[existingIndex] = {
            ...existingEntry,
            activities: [...activities, newActivity]
          };
        }
      }
    } else {
      // Create new entry
      if (dayDate) {
        // Create date-keyed structure
        newThingstodo.push({
          destination: locationId,
          thingsToDo: {
            [dayDate]: [newActivity]
          }
        });
      } else {
        // Create simple structure
        newThingstodo.push({
          destination: locationId,
          activities: [newActivity]
        });
      }
    }

    console.log('Updated thingstodo:', newThingstodo);
    
    return {
      ...prev,
      thingstodo: newThingstodo
    };
  });
};

const addPlaceToEat = (dayIndex, locationId) => {
  if (!editingPackIt) return;

  console.log('addPlaceToEat called:', { dayIndex, locationId, timestamp: Date.now() });

  const newRestaurant = {
    id: uuidv4(), // Add unique ID
    name: 'New Restaurant',
    type: 'Restaurant',
    description: 'Enter description here',
    address: 'Enter address here',
    website: 'https://example.com'
  };

  setEditingPackIt(prev => {
    // Get the date for the specific day
    const dayDate = prev.itinerary?.itinerary?.itinerary?.[dayIndex]?.date;
    console.log('Adding restaurant for day:', dayIndex, 'date:', dayDate);

    // Check if there's already a placestoeat entry for this destination
    const existingIndex = prev.placestoeat?.findIndex(entry => 
      entry.destination === locationId
    );

    let newPlacestoeat = [...(prev.placestoeat || [])];

    if (existingIndex !== -1) {
      // Update existing entry - always use date-keyed structure for consistency
      const existingEntry = newPlacestoeat[existingIndex];
      const useLowercaseKey =
        existingEntry.placestoeat &&
        typeof existingEntry.placestoeat === 'object' &&
        !Array.isArray(existingEntry.placestoeat);

      if (dayDate) {
        if (useLowercaseKey) {
          const updated = { ...existingEntry.placestoeat };
          const dayKey =
            Object.keys(updated).find((k) => String(k).split('T')[0] === String(dayDate).split('T')[0]) ||
            dayDate;
          if (!updated[dayKey]) {
            updated[dayKey] = [];
          }
          const existingRestaurant = updated[dayKey].find((rest) => rest.id === newRestaurant.id);
          if (!existingRestaurant) {
            updated[dayKey].push(newRestaurant);
          }
          newPlacestoeat[existingIndex] = { ...existingEntry, placestoeat: updated };
        } else {
          const updatedPlacesToEat = { ...existingEntry.placesToEat };
          if (!updatedPlacesToEat[dayDate]) {
            updatedPlacesToEat[dayDate] = [];
          }
          const existingRestaurant = updatedPlacesToEat[dayDate].find((rest) => rest.id === newRestaurant.id);
          if (!existingRestaurant) {
            updatedPlacesToEat[dayDate].push(newRestaurant);
          }
          newPlacestoeat[existingIndex] = {
            ...existingEntry,
            placesToEat: updatedPlacesToEat
          };
        }
      } else {
        // Fallback to restaurants array if no date
        const restaurants = existingEntry.restaurants || existingEntry.placesToEat || [];
        const existingRestaurant = restaurants.find(rest => rest.id === newRestaurant.id);
        if (!existingRestaurant) {
          newPlacestoeat[existingIndex] = {
            ...existingEntry,
            restaurants: [...restaurants, newRestaurant]
          };
        }
      }
    } else {
      // Create new entry (prefer API lowercase placestoeat key for new blocks)
      if (dayDate) {
        newPlacestoeat.push({
          destination: locationId,
          placestoeat: {
            [dayDate]: [newRestaurant]
          }
        });
      } else {
        // Create simple structure
        newPlacestoeat.push({
          destination: locationId,
          restaurants: [newRestaurant]
        });
      }
    }

    console.log('Updated placestoeat:', newPlacestoeat);
    
    return {
      ...prev,
      placestoeat: newPlacestoeat
    };
  });
};

// Remove functions for itinerary items
const removePlaceToStay = (stayIndex, dayIndex, locationId, event, stayName) => {
  if (!editingPackIt) return;
  
  // Prevent event bubbling and default behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  console.log("=== REMOVE PLACE TO STAY DEBUG ===");
  console.log("Parameters received:", { stayIndex, dayIndex, locationId, stayName });
  console.log("Current editingPackIt ID:", editingPackIt.id || editingPackIt.packitid || editingPackIt.packitId);
  console.log("Current placestostay structure:", JSON.stringify(editingPackIt.placestostay, null, 2));
  
  setEditingPackIt(prev => {
    console.log("Previous editingPackIt ID:", prev.id || prev.packitid || prev.packitId);
    const newPlacesToStay = [...(prev.placestostay || [])];
    
    // Find the correct entry and remove by name match
    let removed = false;
    for (let entryIndex = 0; entryIndex < newPlacesToStay.length && !removed; entryIndex++) {
      const entry = newPlacesToStay[entryIndex];
      console.log(`Checking entry ${entryIndex}:`, entry);
      
      if (entry.placesToStay && Array.isArray(entry.placesToStay)) {
        console.log(`Entry ${entryIndex} has ${entry.placesToStay.length} stays`);
        console.log(`Stays in entry ${entryIndex}:`, entry.placesToStay.map((stay, idx) => `${idx}: ${stay.name}`));
        
        // Find the stay by name match (case insensitive)
        const targetName = stayName?.toUpperCase() || '';
        const stayToRemoveIndex = entry.placesToStay.findIndex(stay => 
          stay.name?.toUpperCase() === targetName
        );
        
        if (stayToRemoveIndex !== -1) {
          console.log(`Found stay "${entry.placesToStay[stayToRemoveIndex].name}" at index ${stayToRemoveIndex} in entry ${entryIndex}`);
          console.log(`Target name: "${targetName}"`);
          console.log(`Found name: "${entry.placesToStay[stayToRemoveIndex].name.toUpperCase()}"`);
          
          // Remove the specific stay by name
          const removedStay = entry.placesToStay.splice(stayToRemoveIndex, 1);
          console.log(`Removed stay:`, removedStay);
          console.log(`Remaining stays:`, entry.placesToStay.map((stay, idx) => `${idx}: ${stay.name}`));
          
          // If no more stays in this entry, remove the entire entry
          if (entry.placesToStay.length === 0) {
            console.log(`No more stays in entry ${entryIndex}, removing entire entry`);
            newPlacesToStay.splice(entryIndex, 1);
          }
          removed = true; // Mark as removed and exit loop
        } else {
          console.log(`No stay found with name "${targetName}" in entry ${entryIndex}`);
          
          // Additional debugging: show detailed comparison
          console.log(`Detailed name comparison for entry ${entryIndex}:`);
          entry.placesToStay.forEach((stay, idx) => {
            console.log(`  Stay ${idx}: "${stay.name}" (upper: "${stay.name?.toUpperCase()}")`);
            console.log(`  Target: "${targetName}"`);
            console.log(`  Match: ${stay.name?.toUpperCase() === targetName}`);
          });
          
          // Fallback: try partial name matching for common variations
          if (targetName && targetName.length > 3) {
            console.log(`Trying partial name matching for "${targetName}"`);
            const partialMatchIndex = entry.placesToStay.findIndex(stay => {
              const stayNameUpper = stay.name?.toUpperCase() || '';
              // Check if either name contains the other (for partial matches)
              return stayNameUpper.includes(targetName) || targetName.includes(stayNameUpper);
            });
            
            if (partialMatchIndex !== -1) {
              console.log(`Found partial match: "${entry.placesToStay[partialMatchIndex].name}" for target "${targetName}"`);
              console.log(`Removing stay at index ${partialMatchIndex} in entry ${entryIndex}`);
              
              // Remove the specific stay by partial match
              const removedStay = entry.placesToStay.splice(partialMatchIndex, 1);
              console.log(`Removed stay:`, removedStay);
              console.log(`Remaining stays:`, entry.placesToStay.map((stay, idx) => `${idx}: ${stay.name}`));
              
              // If no more stays in this entry, remove the entire entry
              if (entry.placesToStay.length === 0) {
                console.log(`No more stays in entry ${entryIndex}, removing entire entry`);
                newPlacesToStay.splice(entryIndex, 1);
              }
              removed = true; // Mark as removed and exit loop
            }
          }
        }
      }
    }
    
    if (!removed) {
      console.log(`Could not find stay with name "${stayName}" in any entry`);
      console.log("Available entries:", newPlacesToStay.map((entry, idx) => 
        `Entry ${idx}: ${entry.placesToStay?.length || 0} stays`
      ));
      console.log("Available stay names:", newPlacesToStay.flatMap(entry => 
        entry.placesToStay?.map(stay => stay.name) || []
      ));
      
      // Final fallback: try to remove by stayIndex if provided
      if (stayIndex !== undefined && stayIndex >= 0) {
        console.log(`Final fallback: trying to remove by stayIndex ${stayIndex}`);
        for (let entryIndex = 0; entryIndex < newPlacesToStay.length && !removed; entryIndex++) {
          const entry = newPlacesToStay[entryIndex];
          if (entry.placesToStay && Array.isArray(entry.placesToStay) && entry.placesToStay.length > stayIndex) {
            console.log(`Removing stay at index ${stayIndex} from entry ${entryIndex}`);
            const removedStay = entry.placesToStay.splice(stayIndex, 1);
            console.log(`Removed stay by index:`, removedStay);
            console.log(`Remaining stays:`, entry.placesToStay.map((stay, idx) => `${idx}: ${stay.name}`));
            
            // If no more stays in this entry, remove the entire entry
            if (entry.placesToStay.length === 0) {
              console.log(`No more stays in entry ${entryIndex}, removing entire entry`);
              newPlacesToStay.splice(entryIndex, 1);
            }
            removed = true;
            break;
          }
        }
      }
    }
    
    const updated = {
      ...prev,
      placestostay: newPlacesToStay
    };
    
    console.log("Updated editingPackIt ID:", updated.id || updated.packitid || updated.packitId);
    console.log("Final placestostay structure:", JSON.stringify(updated.placestostay, null, 2));
    console.log("Removal successful:", removed);
    console.log("=== END DEBUG ===");
    
    // Reset the flag after a longer delay to prevent rapid successive calls
    window.removePlaceToStayTimeout = setTimeout(() => {
      window.removingPlaceToStay = null;
      window.removePlaceToStayTimeout = null;
    }, 1000);
    
    return updated;
  });
};

// Simplified delete function that works with both old and new structures
const removePlaceToStaySimple = (stayIndex, dayIndex, locationId, event, stayName) => {
  if (!editingPackIt) return;
  
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  console.log('removePlaceToStaySimple called:', { stayIndex, dayIndex, locationId, stayName });
  
  setEditingPackIt(prev => {
    const dayDate = prev.itinerary?.itinerary?.itinerary?.[dayIndex]?.date;
    console.log('Removing place to stay for day:', dayIndex, 'date:', dayDate, 'stayIndex:', stayIndex);

    const newPlacesToStay = [...(prev.placestostay || [])];
    let removed = false;
    
    // Try to find and remove by index first (more reliable)
    for (let entryIndex = 0; entryIndex < newPlacesToStay.length && !removed; entryIndex++) {
      const entry = newPlacesToStay[entryIndex];
      
      if (entry.placesToStay && Array.isArray(entry.placesToStay)) {
        // Old array structure
        if (stayIndex !== undefined && stayIndex < entry.placesToStay.length) {
          const removedStay = entry.placesToStay.splice(stayIndex, 1);
          console.log(`Removed stay from array structure:`, removedStay);
          removed = true;
          
          if (entry.placesToStay.length === 0) {
            newPlacesToStay.splice(entryIndex, 1);
          }
          break;
        }
      } else if (entry.placesToStay && typeof entry.placesToStay === 'object' && !Array.isArray(entry.placesToStay)) {
        // New date-keyed structure
        if (dayDate && entry.placesToStay[dayDate] && Array.isArray(entry.placesToStay[dayDate])) {
          const stays = entry.placesToStay[dayDate];
          if (stayIndex !== undefined && stayIndex < stays.length) {
            const removedStay = stays.splice(stayIndex, 1);
            console.log(`Removed stay from date-keyed structure (${dayDate}):`, removedStay);
            removed = true;
            
            if (stays.length === 0) {
              delete entry.placesToStay[dayDate];
            }
            
            if (Object.keys(entry.placesToStay).length === 0) {
              newPlacesToStay.splice(entryIndex, 1);
            }
            break;
          }
        }
      }
    }
    
    // Fallback: try to remove by name if index-based removal failed
    if (!removed && stayName) {
      console.log('Index-based removal failed, trying name-based removal for:', stayName);
      for (let entryIndex = 0; entryIndex < newPlacesToStay.length && !removed; entryIndex++) {
        const entry = newPlacesToStay[entryIndex];
        
        if (entry.placesToStay && Array.isArray(entry.placesToStay)) {
          const targetName = stayName?.toUpperCase() || '';
          const stayToRemoveIndex = entry.placesToStay.findIndex(stay => 
            stay.name?.toUpperCase() === targetName
          );
          
          if (stayToRemoveIndex !== -1) {
            const removedStay = entry.placesToStay.splice(stayToRemoveIndex, 1);
            console.log(`Removed stay by name from array structure:`, removedStay);
            removed = true;
            
            if (entry.placesToStay.length === 0) {
              newPlacesToStay.splice(entryIndex, 1);
            }
            break;
          }
        } else if (entry.placesToStay && typeof entry.placesToStay === 'object' && !Array.isArray(entry.placesToStay)) {
          const dateKeys = Object.keys(entry.placesToStay);
          for (const dateKey of dateKeys) {
            const stays = entry.placesToStay[dateKey];
            if (Array.isArray(stays)) {
              const targetName = stayName?.toUpperCase() || '';
              const stayToRemoveIndex = stays.findIndex(stay => 
                stay.name?.toUpperCase() === targetName
              );
              
              if (stayToRemoveIndex !== -1) {
                const removedStay = stays.splice(stayToRemoveIndex, 1);
                console.log(`Removed stay by name from date-keyed structure (${dateKey}):`, removedStay);
                removed = true;
                
                if (stays.length === 0) {
                  delete entry.placesToStay[dateKey];
                }
                
                if (Object.keys(entry.placesToStay).length === 0) {
                  newPlacesToStay.splice(entryIndex, 1);
                }
                break;
              }
            }
          }
        }
      }
    }
    
    console.log('Removal successful:', removed);
    
    return {
      ...prev,
      placestostay: newPlacesToStay
    };
  });
};

const removeThingToDo = (activityIndex, dayIndex, locationId, event, activityName) => {
  if (!editingPackIt) return;
  
  // Prevent event bubbling and default behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  // Prevent duplicate calls with a more robust mechanism
  const callKey = `removeThingToDo_${activityIndex}_${dayIndex}_${activityName}`;
  if (window.removingThingToDo === callKey) {
    console.log("Already processing removeThingToDo for this specific item, skipping duplicate call");
    return;
  }
  
  // Set the flag immediately to prevent any subsequent calls
  window.removingThingToDo = callKey;
  
  // Also use a more aggressive debounce mechanism
  if (window.removeThingToDoTimeout) {
    clearTimeout(window.removeThingToDoTimeout);
  }
  
  console.log("=== REMOVE THING TO DO DEBUG ===");
  console.log("Parameters received:", { activityIndex, dayIndex, locationId, activityName });
  console.log("Current editingPackIt ID:", editingPackIt.id || editingPackIt.packitid || editingPackIt.packitId);
  console.log("Current editingPackIt thingstodo:", JSON.stringify(editingPackIt.thingstodo, null, 2));
  console.log("=== END DEBUG ===");
  
  setEditingPackIt(prev => {
    console.log("Previous editingPackIt ID:", prev.id || prev.packitid || prev.packitId);
    console.log("Previous thingstodo structure:", JSON.stringify(prev.thingstodo, null, 2));
    const newThingsToDo = [...(prev.thingstodo || [])];
    
    // Handle the nested structure: thingstodo[0].thingsToDo[date][activityIndex]
    let removed = false;
    
    if (newThingsToDo.length > 0 && newThingsToDo[0].thingsToDo) {
      const thingsToDoObj = newThingsToDo[0].thingsToDo;
      
      // Get the correct date based on dayIndex
      const dateKeys = Object.keys(thingsToDoObj).sort(); // Sort dates chronologically
      console.log(`Available dates: ${dateKeys.join(', ')}`);
      console.log(`Looking for dayIndex ${dayIndex} in ${dateKeys.length} dates`);
      
      // Support both 0-based and 1-based indexing for dayIndex
      let actualDayIndex = dayIndex;
      let indexingType = 'unknown';
      
      if (dayIndex !== undefined && dayIndex > 0 && dayIndex <= dateKeys.length) {
        // Convert 1-based indexing to 0-based
        actualDayIndex = dayIndex - 1;
        indexingType = '1-based';
        console.log(`Converted 1-based dayIndex ${dayIndex} to 0-based index ${actualDayIndex}`);
      } else if (dayIndex !== undefined && dayIndex >= 0 && dayIndex < dateKeys.length) {
        // Already 0-based indexing
        actualDayIndex = dayIndex;
        indexingType = '0-based';
        console.log(`Using 0-based dayIndex ${dayIndex}`);
      } else {
        console.log(`Invalid dayIndex ${dayIndex} - must be between 0-${dateKeys.length-1} (0-based) or 1-${dateKeys.length} (1-based)`);
      }
      
      if (dayIndex !== undefined && actualDayIndex >= 0 && actualDayIndex < dateKeys.length) {
        const targetDate = dateKeys[actualDayIndex];
        console.log(`Target date for dayIndex ${dayIndex} (actual: ${actualDayIndex}): ${targetDate}`);
        
        if (thingsToDoObj[targetDate] && Array.isArray(thingsToDoObj[targetDate])) {
          console.log(`Target date ${targetDate} has ${thingsToDoObj[targetDate].length} activities`);
          console.log(`Activities before removal:`, thingsToDoObj[targetDate].map((act, idx) => `${idx}: ${act.name}`));
          
          // Find the activity by name match (case insensitive)
          const targetName = activityName?.toUpperCase() || '';
          const activityToRemoveIndex = thingsToDoObj[targetDate].findIndex(activity => 
            activity.name?.toUpperCase() === targetName
          );
          
          if (activityToRemoveIndex !== -1) {
            console.log(`Found activity "${thingsToDoObj[targetDate][activityToRemoveIndex].name}" at index ${activityToRemoveIndex}`);
            console.log(`Target name: "${targetName}"`);
            console.log(`Found name: "${thingsToDoObj[targetDate][activityToRemoveIndex].name.toUpperCase()}"`);
            
            // Remove the specific activity by name
            const removedActivity = thingsToDoObj[targetDate].splice(activityToRemoveIndex, 1);
            console.log(`Removed activity:`, removedActivity);
            console.log(`Activities after removal:`, thingsToDoObj[targetDate].map((act, idx) => `${idx}: ${act.name}`));
            
            // If no more activities for this date, remove the date key
            if (thingsToDoObj[targetDate].length === 0) {
              console.log(`No more activities for date ${targetDate}, removing date key`);
              delete thingsToDoObj[targetDate];
            }
            removed = true;
          } else {
            console.log(`No activity found with name "${targetName}" in date ${targetDate}`);
            console.log(`Available activities:`, thingsToDoObj[targetDate].map(act => act.name));
            
            // Fallback: search through all dates to find the activity
            console.log(`Searching through all dates for activity "${targetName}"`);
            for (const searchDate of dateKeys) {
              if (searchDate !== targetDate && thingsToDoObj[searchDate] && Array.isArray(thingsToDoObj[searchDate])) {
                const foundIndex = thingsToDoObj[searchDate].findIndex(activity => 
                  activity.name?.toUpperCase() === targetName
                );
                if (foundIndex !== -1) {
                  console.log(`Found activity "${targetName}" in date ${searchDate} at index ${foundIndex}`);
                  const removedActivity = thingsToDoObj[searchDate].splice(foundIndex, 1);
                  console.log(`Removed activity from ${searchDate}:`, removedActivity);
                  
                  // If no more activities for this date, remove the date key
                  if (thingsToDoObj[searchDate].length === 0) {
                    console.log(`No more activities for date ${searchDate}, removing date key`);
                    delete thingsToDoObj[searchDate];
                  }
                  removed = true;
                  break;
                }
              }
            }
          }
        } else {
          console.log(`No activities found for target date ${targetDate}`);
        }
      } else {
        console.log(`Invalid dayIndex ${dayIndex} or no dates available (actualDayIndex: ${actualDayIndex}, indexingType: ${indexingType})`);
      }
      
      if (!removed) {
        console.log(`Could not find activity at index ${activityIndex} in any date`);
        console.log("Available dates and their activity counts:", 
          dateKeys.map(date => `${date}: ${thingsToDoObj[date]?.length || 0} activities`));
        console.log("Full thingsToDo structure:", JSON.stringify(thingsToDoObj, null, 2));
        
        // Final fallback: search through all dates for the activity by name
        if (activityName) {
          console.log(`Final fallback: searching for activity "${activityName}" across all dates`);
          const targetName = activityName.toUpperCase();
          for (const searchDate of dateKeys) {
            if (thingsToDoObj[searchDate] && Array.isArray(thingsToDoObj[searchDate])) {
              const foundIndex = thingsToDoObj[searchDate].findIndex(activity => 
                activity.name?.toUpperCase() === targetName
              );
              if (foundIndex !== -1) {
                console.log(`Found activity "${activityName}" in date ${searchDate} at index ${foundIndex}`);
                const removedActivity = thingsToDoObj[searchDate].splice(foundIndex, 1);
                console.log(`Removed activity from ${searchDate}:`, removedActivity);
                
                // If no more activities for this date, remove the date key
                if (thingsToDoObj[searchDate].length === 0) {
                  console.log(`No more activities for date ${searchDate}, removing date key`);
                  delete thingsToDoObj[searchDate];
                }
                removed = true;
                break;
              }
            }
          }
        }
      }
    }
    
    const updated = {
      ...prev,
      thingstodo: newThingsToDo
    };
    
    console.log("Updated editingPackIt ID:", updated.id || updated.packitid || updated.packitId);
    console.log("Final thingstodo structure after removal:", JSON.stringify(updated.thingstodo, null, 2));
    console.log("Removal successful:", removed);
    
    // Reset the flag after a longer delay to prevent rapid successive calls
    window.removeThingToDoTimeout = setTimeout(() => {
      window.removingThingToDo = null;
      window.removeThingToDoTimeout = null;
    }, 1000);
    
    return updated;
  });
};

const removePlaceToEat = (restaurantIndex, dayIndex, locationId, event, restaurantName) => {
  if (!editingPackIt) return;
  
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  console.log('removePlaceToEat called:', { restaurantIndex, dayIndex, locationId, restaurantName });
  
  setEditingPackIt(prev => {
    const dayDate = prev.itinerary?.itinerary?.itinerary?.[dayIndex]?.date;
    console.log('Removing restaurant for day:', dayIndex, 'date:', dayDate, 'restaurantIndex:', restaurantIndex);
    console.log('Current placestoeat structure:', JSON.stringify(prev.placestoeat, null, 2));

    const newPlacesToEat = [...(prev.placestoeat || [])];
    let removed = false;
    
    // Try to find and remove by index first (more reliable)
    for (let entryIndex = 0; entryIndex < newPlacesToEat.length && !removed; entryIndex++) {
      const entry = newPlacesToEat[entryIndex];
      console.log(`Checking entry ${entryIndex}:`, entry);
      
      // Handle different possible structures
      if (entry.restaurants && Array.isArray(entry.restaurants)) {
        // Structure: { restaurants: [...] }
        if (restaurantIndex !== undefined && restaurantIndex < entry.restaurants.length) {
          const removedRestaurant = entry.restaurants.splice(restaurantIndex, 1);
          console.log(`Removed restaurant from restaurants array:`, removedRestaurant);
          removed = true;
          
          if (entry.restaurants.length === 0) {
            newPlacesToEat.splice(entryIndex, 1);
          }
          break;
        }
      } else if (entry.placesToEat && Array.isArray(entry.placesToEat)) {
        // Structure: { placesToEat: [...] }
        if (restaurantIndex !== undefined && restaurantIndex < entry.placesToEat.length) {
          const removedRestaurant = entry.placesToEat.splice(restaurantIndex, 1);
          console.log(`Removed restaurant from placesToEat array:`, removedRestaurant);
          removed = true;
          
          if (entry.placesToEat.length === 0) {
            newPlacesToEat.splice(entryIndex, 1);
          }
          break;
        }
      } else if (entry.placesToEat && typeof entry.placesToEat === 'object' && !Array.isArray(entry.placesToEat)) {
        // New date-keyed structure: { placesToEat: { "2025-11-07": [...] } }
        const normDay = dayDate ? String(dayDate).split('T')[0] : '';
        let dayKey = null;
        if (normDay) {
          dayKey = Object.keys(entry.placesToEat).find((k) => String(k).split('T')[0] === normDay) || null;
        }
        if (dayKey && Array.isArray(entry.placesToEat[dayKey])) {
          const restaurants = entry.placesToEat[dayKey];
          if (restaurantIndex !== undefined && restaurantIndex < restaurants.length) {
            const removedRestaurant = restaurants.splice(restaurantIndex, 1);
            console.log(`Removed restaurant from date-keyed placesToEat (${dayKey}):`, removedRestaurant);
            removed = true;

            if (restaurants.length === 0) {
              delete entry.placesToEat[dayKey];
            }

            if (Object.keys(entry.placesToEat).length === 0) {
              newPlacesToEat.splice(entryIndex, 1);
            }
            break;
          }
        }
      } else if (entry.placestoeat && Array.isArray(entry.placestoeat)) {
        if (restaurantIndex !== undefined && restaurantIndex < entry.placestoeat.length) {
          const removedRestaurant = entry.placestoeat.splice(restaurantIndex, 1);
          console.log('Removed restaurant from placestoeat array:', removedRestaurant);
          removed = true;
          if (entry.placestoeat.length === 0) {
            newPlacesToEat.splice(entryIndex, 1);
          }
          break;
        }
      } else if (entry.placestoeat && typeof entry.placestoeat === 'object' && !Array.isArray(entry.placestoeat)) {
        // API shape: { placestoeat: { "2026-03-13": [...] } } (matches normalizePlacesToEat format 1)
        const normDay = dayDate ? String(dayDate).split('T')[0] : '';
        let dayKey = null;
        if (normDay) {
          dayKey = Object.keys(entry.placestoeat).find((k) => String(k).split('T')[0] === normDay) || null;
        }
        if (dayKey && Array.isArray(entry.placestoeat[dayKey])) {
          const restaurants = entry.placestoeat[dayKey];
          if (restaurantIndex !== undefined && restaurantIndex < restaurants.length) {
            const removedRestaurant = restaurants.splice(restaurantIndex, 1);
            console.log(`Removed restaurant from date-keyed placestoeat (${dayKey}):`, removedRestaurant);
            removed = true;

            if (restaurants.length === 0) {
              delete entry.placestoeat[dayKey];
            }

            if (Object.keys(entry.placestoeat).length === 0) {
              newPlacesToEat.splice(entryIndex, 1);
            }
            break;
          }
        }
      }
    }
    
    // Fallback: try to remove by name if index-based removal failed
    if (!removed && restaurantName) {
      console.log('Index-based removal failed, trying name-based removal for:', restaurantName);
      for (let entryIndex = 0; entryIndex < newPlacesToEat.length && !removed; entryIndex++) {
        const entry = newPlacesToEat[entryIndex];
        
        if (entry.restaurants && Array.isArray(entry.restaurants)) {
          // Structure: { restaurants: [...] }
          const targetName = restaurantName?.toUpperCase() || '';
          const restaurantToRemoveIndex = entry.restaurants.findIndex(restaurant => 
            restaurant.name?.toUpperCase() === targetName
          );
          
          if (restaurantToRemoveIndex !== -1) {
            const removedRestaurant = entry.restaurants.splice(restaurantToRemoveIndex, 1);
            console.log(`Removed restaurant by name from restaurants array:`, removedRestaurant);
            removed = true;
            
            if (entry.restaurants.length === 0) {
              newPlacesToEat.splice(entryIndex, 1);
            }
            break;
          }
        } else if (entry.placesToEat && Array.isArray(entry.placesToEat)) {
          // Structure: { placesToEat: [...] }
          const targetName = restaurantName?.toUpperCase() || '';
          const restaurantToRemoveIndex = entry.placesToEat.findIndex(restaurant => 
            restaurant.name?.toUpperCase() === targetName
          );
          
          if (restaurantToRemoveIndex !== -1) {
            const removedRestaurant = entry.placesToEat.splice(restaurantToRemoveIndex, 1);
            console.log(`Removed restaurant by name from placesToEat array:`, removedRestaurant);
            removed = true;
            
            if (entry.placesToEat.length === 0) {
              newPlacesToEat.splice(entryIndex, 1);
            }
            break;
          }
        } else if (entry.placesToEat && typeof entry.placesToEat === 'object' && !Array.isArray(entry.placesToEat)) {
          // Date-keyed structure: { placesToEat: { "2025-11-07": [...] } }
          const dateKeys = Object.keys(entry.placesToEat);
          for (const dateKey of dateKeys) {
            const restaurants = entry.placesToEat[dateKey];
            if (Array.isArray(restaurants)) {
              const targetName = restaurantName?.toUpperCase() || '';
              const restaurantToRemoveIndex = restaurants.findIndex(restaurant => 
                restaurant.name?.toUpperCase() === targetName
              );
              
              if (restaurantToRemoveIndex !== -1) {
                const removedRestaurant = restaurants.splice(restaurantToRemoveIndex, 1);
                console.log(`Removed restaurant by name from date-keyed structure (${dateKey}):`, removedRestaurant);
                removed = true;
                
                if (restaurants.length === 0) {
                  delete entry.placesToEat[dateKey];
                }
                
                if (Object.keys(entry.placesToEat).length === 0) {
                  newPlacesToEat.splice(entryIndex, 1);
                }
                break;
              }
            }
          }
        } else if (entry.placestoeat && Array.isArray(entry.placestoeat)) {
          const targetName = restaurantName?.toUpperCase() || '';
          const restaurantToRemoveIndex = entry.placestoeat.findIndex(
            (restaurant) => restaurant.name?.toUpperCase() === targetName
          );
          if (restaurantToRemoveIndex !== -1) {
            const removedRestaurant = entry.placestoeat.splice(restaurantToRemoveIndex, 1);
            console.log('Removed restaurant by name from placestoeat array:', removedRestaurant);
            removed = true;
            if (entry.placestoeat.length === 0) {
              newPlacesToEat.splice(entryIndex, 1);
            }
            break;
          }
        } else if (entry.placestoeat && typeof entry.placestoeat === 'object' && !Array.isArray(entry.placestoeat)) {
          const normDay = dayDate ? String(dayDate).split('T')[0] : '';
          const dateKeys = Object.keys(entry.placestoeat);
          const keysToScan = normDay
            ? dateKeys.filter((k) => String(k).split('T')[0] === normDay)
            : dateKeys;
          for (const dateKey of keysToScan.length ? keysToScan : dateKeys) {
            const restaurants = entry.placestoeat[dateKey];
            if (Array.isArray(restaurants)) {
              const targetName = restaurantName?.toUpperCase() || '';
              let restaurantToRemoveIndex = restaurants.findIndex(
                (restaurant) => restaurant.name?.toUpperCase() === targetName
              );
              if (restaurantToRemoveIndex === -1 && locationId && targetName) {
                restaurantToRemoveIndex = restaurants.findIndex((restaurant) => {
                  const nameMatch = restaurant.name?.toUpperCase() === targetName;
                  const loc = (restaurant.locationId || '').toLowerCase();
                  const want = (locationId || '').toLowerCase();
                  return nameMatch && loc && want && (loc.includes(want) || want.includes(loc));
                });
              }
              if (restaurantToRemoveIndex !== -1) {
                const removedRestaurant = restaurants.splice(restaurantToRemoveIndex, 1);
                console.log(`Removed restaurant by name from date-keyed placestoeat (${dateKey}):`, removedRestaurant);
                removed = true;
                if (restaurants.length === 0) {
                  delete entry.placestoeat[dateKey];
                }
                if (Object.keys(entry.placestoeat).length === 0) {
                  newPlacesToEat.splice(entryIndex, 1);
                }
                break;
              }
            }
          }
        }
      }
    }
    
    console.log('Restaurant removal successful:', removed);
    
    return {
      ...prev,
      placestoeat: newPlacesToEat
    };
  });
};

// Update functions for itinerary items
const updatePlaceToStay = (stayId, field, value) => {
  if (!editingPackIt) return;
  
  console.log('updatePlaceToStay called:', { stayId, field, value });
  
  setEditingPackIt(prev => {
    const newPlacestostay = prev.placestostay?.map((stayGroup) => {
      // Handle different data structures
      if (stayGroup.placesToStay && Array.isArray(stayGroup.placesToStay)) {
        return {
          ...stayGroup,
          placesToStay: stayGroup.placesToStay.map((stay) => {
            // Match by unique ID
            if (stay.id === stayId) {
              console.log(`Updating stay with ID ${stayId}:`, stay);
              return { ...stay, [field]: value };
            }
            return stay;
          })
        };
      } else if (stayGroup.placesToStay && typeof stayGroup.placesToStay === 'object' && !Array.isArray(stayGroup.placesToStay)) {
        // Date-keyed structure
        const updatedPlacesToStay = {};
        Object.keys(stayGroup.placesToStay).forEach(dateKey => {
          updatedPlacesToStay[dateKey] = stayGroup.placesToStay[dateKey].map((stay) => {
            if (stay.id === stayId) {
              console.log(`Updating stay with ID ${stayId} for date ${dateKey}:`, stay);
              return { ...stay, [field]: value };
            }
            return stay;
          });
        });
        return {
          ...stayGroup,
          placesToStay: updatedPlacesToStay
        };
      }
      return stayGroup;
    }) || [];
    
    console.log('Updated placestostay:', newPlacestostay);
    return { ...prev, placestostay: newPlacestostay };
  });
};

const updateThingToDo = (activityId, field, value) => {
  if (!editingPackIt) return;
  
  console.log('updateThingToDo called:', { activityId, field, value });
  
  setEditingPackIt(prev => {
    const newThingstodo = prev.thingstodo?.map((activityGroup) => {
      // Handle different data structures
      if (activityGroup.thingsToDo && typeof activityGroup.thingsToDo === 'object' && !Array.isArray(activityGroup.thingsToDo)) {
        // Date-keyed structure: { thingsToDo: { "2025-11-07": [...] } }
        const updatedThingsToDo = {};
        Object.keys(activityGroup.thingsToDo).forEach(dateKey => {
          updatedThingsToDo[dateKey] = activityGroup.thingsToDo[dateKey].map((activity) => {
            if (activity.id === activityId) {
              console.log(`Updating activity with ID ${activityId} for date ${dateKey}:`, activity);
              return { ...activity, [field]: value };
            }
            return activity;
          });
        });
        return { ...activityGroup, thingsToDo: updatedThingsToDo };
      } 
      else if (activityGroup.activities && Array.isArray(activityGroup.activities)) {
        // New structure: { activities: [...] }
        return {
          ...activityGroup,
          activities: activityGroup.activities.map((activity) => {
            if (activity.id === activityId) {
              console.log(`Updating activity with ID ${activityId}:`, activity);
              return { ...activity, [field]: value };
            }
            return activity;
          })
        };
      } 
      else if (activityGroup.thingsToDo && Array.isArray(activityGroup.thingsToDo)) {
        // Legacy structure: { thingsToDo: [...] }
        return {
          ...activityGroup,
          thingsToDo: activityGroup.thingsToDo.map((activity) => {
            if (activity.id === activityId) {
              console.log(`Updating activity with ID ${activityId}:`, activity);
              return { ...activity, [field]: value };
            }
            return activity;
          })
        };
      }
      else if (activityGroup.id === activityId) {
        // Standalone activity structure
        console.log('Updating standalone activity with ID:', activityId);
        return { ...activityGroup, [field]: value };
      }
      
      return activityGroup;
    }) || [];
    
    console.log('Updated thingstodo:', newThingstodo);
    return { ...prev, thingstodo: newThingstodo };
  });
};

const updatePlaceToEat = (restaurantId, field, value) => {
  if (!editingPackIt) return;
  
  console.log('updatePlaceToEat called:', { restaurantId, field, value });
  
  setEditingPackIt(prev => {
    const newPlacestoeat = prev.placestoeat?.map((restaurantGroup) => {
      // Handle different data structures
      if (restaurantGroup.restaurants && Array.isArray(restaurantGroup.restaurants)) {
        return {
          ...restaurantGroup,
          restaurants: restaurantGroup.restaurants.map((restaurant) => {
            // Match by unique ID
            if (restaurant.id === restaurantId) {
              console.log(`Updating restaurant with ID ${restaurantId}:`, restaurant);
              return { ...restaurant, [field]: value };
            }
            return restaurant;
          })
        };
      } else if (restaurantGroup.placesToEat && Array.isArray(restaurantGroup.placesToEat)) {
        return {
          ...restaurantGroup,
          placesToEat: restaurantGroup.placesToEat.map((restaurant) => {
            // Match by unique ID
            if (restaurant.id === restaurantId) {
              console.log(`Updating restaurant with ID ${restaurantId}:`, restaurant);
              return { ...restaurant, [field]: value };
            }
            return restaurant;
          })
        };
      } else if (restaurantGroup.placesToEat && typeof restaurantGroup.placesToEat === 'object' && !Array.isArray(restaurantGroup.placesToEat)) {
        // Date-keyed structure
        const updatedPlacesToEat = {};
        Object.keys(restaurantGroup.placesToEat).forEach(dateKey => {
          updatedPlacesToEat[dateKey] = restaurantGroup.placesToEat[dateKey].map((restaurant) => {
            if (restaurant.id === restaurantId) {
              console.log(`Updating restaurant with ID ${restaurantId} for date ${dateKey}:`, restaurant);
              return { ...restaurant, [field]: value };
            }
            return restaurant;
          });
        });
        return {
          ...restaurantGroup,
          placesToEat: updatedPlacesToEat
        };
      } else if (restaurantGroup.placestoeat && Array.isArray(restaurantGroup.placestoeat)) {
        return {
          ...restaurantGroup,
          placestoeat: restaurantGroup.placestoeat.map((restaurant) => {
            if (restaurant.id === restaurantId) {
              return { ...restaurant, [field]: value };
            }
            return restaurant;
          })
        };
      } else if (restaurantGroup.placestoeat && typeof restaurantGroup.placestoeat === 'object' && !Array.isArray(restaurantGroup.placestoeat)) {
        const updated = {};
        Object.keys(restaurantGroup.placestoeat).forEach((dateKey) => {
          updated[dateKey] = restaurantGroup.placestoeat[dateKey].map((restaurant) => {
            if (restaurant.id === restaurantId) {
              console.log(`Updating restaurant with ID ${restaurantId} for date ${dateKey} (placestoeat):`, restaurant);
              return { ...restaurant, [field]: value };
            }
            return restaurant;
          });
        });
        return { ...restaurantGroup, placestoeat: updated };
      }
      return restaurantGroup;
    }) || [];
    
    console.log('Updated placestoeat:', newPlacestoeat);
    return { ...prev, placestoeat: newPlacestoeat };
  });
};

const addPackingItem = (category, subcategory = null) => {
  if (!editingPackIt) return;
  
  const newItem = {
    item: '',
    quantity: 1,
    notes: '',
    confidenceLevel: null,
    usageDays: []
  };
  
  setEditingPackIt(prev => {
    const newState = structuredClone(prev);
    
    if (subcategory) {
      if (!newState.packingList.packingList[category]) {
        newState.packingList.packingList[category] = {};
      }
      if (!newState.packingList.packingList[category][subcategory]) {
        newState.packingList.packingList[category][subcategory] = [];
      }
      newState.packingList.packingList[category][subcategory].push(newItem);
    } else {
      if (!newState.packingList.packingList[category]) {
        newState.packingList.packingList[category] = [];
      }
      newState.packingList.packingList[category].push(newItem);
    }
    
    return newState;
  });
};

const removePackingItem = (category, itemIndex, subcategory = null) => {
  if (!editingPackIt) return;
  
  setEditingPackIt(prev => {
    const newState = structuredClone(prev);
    
    if (subcategory) {
      if (newState.packingList.packingList[category]?.[subcategory]) {
        newState.packingList.packingList[category][subcategory].splice(itemIndex, 1);
      }
    } else {
      if (newState.packingList.packingList[category]) {
        newState.packingList.packingList[category].splice(itemIndex, 1);
      }
    }
    
    return newState;
  });
};

let callCounter = 0;
let renderCounter = 0;

// Add render tracking
console.log(`🎨 PackIt component render #${++renderCounter} at ${Date.now()}`);

// Simple approach - no refs needed

// Removed complex local state management - now using individual component state

// Optimized input component to prevent re-renders
const PackingInput = React.memo(({ 
  category, 
  itemIndex, 
  field, 
  defaultValue, 
  subcategory, 
  type = 'text', 
  placeholder, 
  className, 
  min,
  onRemove 
}) => {
  const [localValue, setLocalValue] = useState(defaultValue);

  // Update local value when defaultValue changes (from props)
  useEffect(() => {
    setLocalValue(defaultValue);
  }, [defaultValue]);

  const handleChange = useCallback((e) => {
    const newValue = type === 'number' ? parseInt(e.target.value) || 1 : e.target.value;
    setLocalValue(newValue);
    // Don't call updateLocalPackingEdit here - just update local state
  }, [type]);

  const handleBlur = useCallback(() => {
    // Only update main state on blur
    updatePackingItem(category, itemIndex, field, localValue, subcategory);
  }, [category, itemIndex, field, localValue, subcategory]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }, []);

  return (
    <div className="packing-item-edit-row">
      <input
        type={type}
        id={`${category}-${subcategory || 'main'}-${itemIndex}-${field}`}
        name={`${category}-${subcategory || 'main'}-${itemIndex}-${field}`}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        min={min}
        aria-label={`${field} for ${category} item ${itemIndex + 1}`}
      />
      {onRemove && (
        <button
          className="remove-item-button"
          onClick={() => onRemove(category, itemIndex, subcategory)}
          title="Remove Item"
        >
          <i className="fas fa-trash"></i>
        </button>
      )}
    </div>
  );
});

// Optimized textarea component to prevent re-renders
const PackingTextarea = React.memo(({ 
  category, 
  itemIndex, 
  field, 
  defaultValue, 
  subcategory, 
  placeholder, 
  className 
}) => {
  const [localValue, setLocalValue] = useState(defaultValue);

  // Update local value when defaultValue changes (from props)
  useEffect(() => {
    setLocalValue(defaultValue);
  }, [defaultValue]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Auto-resize textarea to prevent layout shifts
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  }, []);

  const handleBlur = useCallback(() => {
    // Only update main state on blur
    updatePackingItem(category, itemIndex, field, localValue, subcategory);
  }, [category, itemIndex, field, localValue, subcategory]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.target.blur();
    }
  }, []);

  return (
    <textarea
      id={`${category}-${subcategory || 'main'}-${itemIndex}-${field}`}
      name={`${category}-${subcategory || 'main'}-${itemIndex}-${field}`}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      rows="2"
      style={{ minHeight: '40px', maxHeight: '200px' }}
      aria-label={`${field} for ${category} item ${itemIndex + 1}`}
    />
  );
});

const updatePackingItem = (category, itemIndex, field, value, subcategory = null) => {
  if (!editingPackIt) return;
  
  console.log('updatePackingItem called:', { category, itemIndex, field, value, subcategory });
  
  // Use structuredClone for minimal updates - only clone what's necessary
  setEditingPackIt(prev => {
    const newState = structuredClone(prev);
    
    // Navigate to the specific item and update only that field
    if (subcategory) {
      if (newState.packingList?.packingList?.[category]?.[subcategory]?.[itemIndex]) {
        newState.packingList.packingList[category][subcategory][itemIndex][field] = value;
      }
    } else {
      if (newState.packingList?.packingList?.[category]?.[itemIndex]) {
        newState.packingList.packingList[category][itemIndex][field] = value;
      }
    }
    
    return newState;
  });
};

// Removed clearLocalPackingEdits - no longer needed with individual component state

// Helper functions for editing destinations
const addDestination = () => {
  if (!editingPackIt) return;
  
  const newDestination = {
    locationId: '',
    address: '',
    name: ''
  };
  
  setEditingPackIt(prev => ({
    ...prev,
    itinerary: {
      ...prev.itinerary,
      itinerary: {
        ...prev.itinerary?.itinerary,
        destinations: [...(prev.itinerary?.itinerary?.destinations || []), newDestination]
      }
    }
  }));
};

const removeDestination = (destIndex) => {
  if (!editingPackIt) return;
  
  setEditingPackIt(prev => ({
    ...prev,
    itinerary: {
      ...prev.itinerary,
      itinerary: {
        ...prev.itinerary?.itinerary,
        destinations: prev.itinerary?.itinerary?.destinations?.filter((_, index) => index !== destIndex) || []
      }
    }
  }));
};

const updateDestination = (destIndex, field, value) => {
  if (!editingPackIt) return;
  
  setEditingPackIt(prev => ({
    ...prev,
    itinerary: {
      ...prev.itinerary,
      itinerary: {
        ...prev.itinerary?.itinerary,
        destinations: prev.itinerary?.itinerary?.destinations?.map((dest, index) => 
          index === destIndex ? { ...dest, [field]: value } : dest
        ) || []
      }
    }
  }));
};



// Helper function to close alert modal and acknowledge alerts
const closeAlertModal = async (packitId) => {
  console.log('closeAlertModal called for:', packitId);
  
  // Find the pack-it in state
  const packIt = packIts.find((p) => p.id === packitId);
  console.log('closeAlertModal - packIt found:', packIt?.id, 'alertsAcknowledged:', packIt?.alertsAcknowledged);
  
  // If alerts have not been acknowledged, acknowledge them
  if (packIt && !packIt.alertsAcknowledged) {
    console.log('Acknowledging alerts for:', packitId);
    try {
      await acknowledgeAlerts(packitId);
      console.log('Alerts acknowledged successfully for:', packitId);
    } catch (error) {
      console.error('Error acknowledging alerts:', error);
    }
  } else {
    console.log('Alerts already acknowledged or no packIt found');
  }
  
  // Use background data if available, otherwise fetch full Pack-It details
  console.log('closeAlertModal - backgroundPackItData:', backgroundPackItData);
  console.log('closeAlertModal - packitId:', packitId);
  
  if (backgroundPackItData && backgroundPackItData.id === packitId) {
    console.log('Using background Pack-It data for display:', backgroundPackItData);
    setSelectedPackIt(backgroundPackItData);
    setBackgroundPackItData(null); // Clear background data
  } else {
    console.log('No background data available, fetching full details...');
    try {
      console.log('Fetching full Pack-It details for display after closing alerts');
      const fullDetails = await fetchFullPackItDetails(packitId);
      console.log('Full Pack-It details loaded for display:', fullDetails);
      setSelectedPackIt(fullDetails);
    } catch (error) {
      console.error('Error fetching full Pack-It details:', error);
    }
  }
  
  setOpenAlertPackIt(null);
  setIsLoadingAlerts(false);
};

const handleAlert = async (packitId) => {
  if (openAlertPackIt === packitId) {
    // If already open, close it and acknowledge alerts if needed
    await closeAlertModal(packitId);
  } else {
    setOpenAlertPackIt(packitId);
    setIsLoadingAlerts(true);
    
    // Quick fetch for just alerts data
    try {
      console.log('Fetching alerts for Pack-It:', packitId);
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      
      if (token) {
        // Fetch just the health safety data for alerts
        const response = await axios.get(`${apiUrl}/briefings/${packitId}?healthsafety=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const alertData = response.data;
        console.log('Fetched alert data:', alertData);
        console.log('Fetched alert data - healthsafety:', alertData.healthsafety);
        console.log('Fetched alert data - healthSafetyAlerts:', alertData.healthSafetyAlerts);
        
        // Create a minimal selectedPackIt object with just the alert data
        const minimalPackIt = {
          id: alertData.packitId,
          name: alertData.name,
          healthsafety: alertData.healthsafety,
          healthSafetyAlerts: alertData.healthSafetyAlerts,
          alertsAcknowledged: alertData.alertsAcknowledged
        };
        console.log('Created minimalPackIt:', minimalPackIt);
        setSelectedPackIt(minimalPackIt);
        
        // Background fetch for full Pack-It details after alerts are displayed
        // This will prepare the full data for when user closes the alerts modal
        fetchFullPackItDetails(packitId)
          .then((fullDetails) => {
            console.log('Background fetch completed for full details:', fullDetails);
            // Preserve the health safety data from the initial alert fetch
            const updatedDetails = {
              ...fullDetails,
              healthsafety: minimalPackIt.healthsafety || fullDetails.healthsafety,
              healthSafetyAlerts: minimalPackIt.healthSafetyAlerts || fullDetails.healthSafetyAlerts,
              alertsAcknowledged: minimalPackIt.alertsAcknowledged
            };
            console.log('Updated details with preserved health safety:', updatedDetails);
            // Store the full details in background state for when modal closes
            setBackgroundPackItData(updatedDetails);
            console.log('Full Pack-It details stored in background for when user closes alerts modal');
          })
          .catch((error) => {
            console.error('Background fetch failed:', error);
            // Keep the minimal data if background fetch fails
          });
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      // Clear selectedPackIt so modal shows summary data
      setSelectedPackIt(null);
    } finally {
      setIsLoadingAlerts(false);
    }
  }
};


  // -------------------------
  // 2) useMemo for filteredPackIts
  // -------------------------
  const filteredPackIts = useMemo(() => {
    if (!packIts || packIts.length === 0) {
      console.log('filteredPackIts - No packIts to filter');
      return [];
    }
    
    const filtered = packIts.filter((packIt) => {
      // Search match (already provided)
      const searchMatch =
        (packIt.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (packIt.location || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (!searchMatch) return false;
  
      // If "Upcoming" is checked
      if (filters.upcoming) {
        const isUpcoming = new Date(packIt.date) > new Date();
        if (!isUpcoming) return false;
      }
  
      // If "Shared" is checked
      if (filters.shared && !packIt.isShared) return false;
  
      // If "Completed" is checked
      if (filters.completed && !packIt.isCompleted) return false;
  
      // If "Favorites" is checked, only include pack‑its that are favorited.
      if (filters.favorites && !packIt.isFavorite) return false;
  
      // If "Active" is checked, only include pack‑its that are active.
      if (filters.active && !packIt.isActive) return false;
  
      return true;
    });
    
    console.log('filteredPackIts - Filtered', packIts.length, 'packits to', filtered.length, 'packits (searchTerm:', searchTerm, 'filters:', filters, ')');
    return filtered;
  }, [packIts, searchTerm, filters]);
  

  // -------------------------
  // 3) Pagination: For API pagination, packIts already contains only the current page
  // -------------------------
  const currentItems = filteredPackIts; // API returns only current page
  const totalPages = itemsPerPage > 0 ? Math.ceil((totalPackItsCount || filteredPackIts.length) / itemsPerPage) : 1;

  // Handle page clicks - fetch from API
  const handlePageChange = async (pageNumber) => {
    // Determine which pagination token to use
    let tokenToUse = null;
    
    if (pageNumber === 1) {
      // Going to page 1 - no token needed, start from beginning
      tokenToUse = null;
    } else if (pageNumber === currentPage + 1) {
      // Going forward one page - use current page's lastEvaluatedKey
      tokenToUse = lastEvaluatedKey;
    } else if (paginationTokens[pageNumber - 1]) {
      // Going to a previously visited page - use the token stored for the previous page
      tokenToUse = paginationTokens[pageNumber - 1];
    } else {
      // Cannot jump to this page directly - need to fetch sequentially
      console.warn(`Cannot jump to page ${pageNumber} directly. Starting from page 1.`);
      // Reset and fetch from beginning
      setPaginationTokens({});
      setLastEvaluatedKey(null);
      tokenToUse = null;
      pageNumber = 1; // Reset to page 1
    }
    
    setCurrentPage(pageNumber);
    
    // Fetch the new page
    await fetchPackIts(pageNumber, tokenToUse);
    
    // Scroll to top of the pack-it list items when page changes
    // Try multiple scrollable containers to ensure we scroll the right one
    const scrollableContainers = [
      document.querySelector('.pack-its-list-items'),
      document.querySelector('.pack-its-list'),
      document.querySelector('.main-content-area'),
      document.querySelector('.main-content')
    ];
    
    scrollableContainers.forEach(container => {
      if (container) {
        container.scrollTop = 0;
      }
    });
    
    // Also scroll window to top as fallback
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to handle page size change
  const handlePageSizeChange = async (newPageSize) => {
    // Clear all pagination state first - this is critical to prevent duplicates
    setPaginationTokens({});
    setLastEvaluatedKey(null);
    setAllPackItsLoaded(false);
    setTotalPackItsCount(0);
    setCurrentPage(1);
    
    // Update page size state
    setItemsPerPage(newPageSize);
    
    // Fetch with the new page size directly (don't rely on state update)
    if (newPageSize === 0) {
      // Fetch all packits
      await fetchAllPackIts();
    } else {
      // Pass the new page size directly to fetchPackIts to avoid stale state
      await fetchPackIts(1, null, newPageSize);
    }
  };

  // Function to fetch all packits at once
  const fetchAllPackIts = async () => {
    setIsLoadingPackIts(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      
      if (!token) {
        console.error('No valid user token. Not fetching packits.');
        setIsLoadingPackIts(false);
        return;
      }
      
      let allPackIts = [];
      let lastKey = null;
      let pageNum = 1;
      
      // Fetch all pages
      do {
        let url = `${apiUrl}/briefings?limit=100`; // Use larger limit to reduce requests
        if (lastKey) {
          url += `&lastEvaluatedKey=${encodeURIComponent(lastKey)}`;
        }
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const items = response.data.items || response.data.Items || [];
        lastKey = response.data.lastEvaluatedKey || null;
        
        if (items && items.length > 0) {
          const newPackIts = items.map((item) => {
            // Calculate total alerts from healthSafetyAlerts if available
            let calculatedTotalAlerts = item.totalAlerts || 0;
            const alertsData = item.healthsafety?.healthSafetyAlerts || item.healthSafetyAlerts || [];
            if (Array.isArray(alertsData) && alertsData.length > 0) {
              if (Array.isArray(alertsData[0]?.alerts)) {
                // New format: [{ alerts: [...] }, { alerts: [...] }]
                calculatedTotalAlerts = alertsData.flatMap((block) => safeToArray(block.alerts)).length;
              } else {
                // Legacy format: array of alert objects directly
                calculatedTotalAlerts = alertsData.filter(alert => 
                  alert && 
                  Object.keys(alert).length > 0 && 
                  (alert.severity || alert.message || alert.title || alert.description)
                ).length;
              }
            }
            
            return {
              id: item.packitId,
              name: item.name || 'Untitled',
              location: '',
              description: item.description,
              date: item.startDate || new Date().toISOString(),
              endDate: item.endDate || null,
              lastEdited: item.lastEdited || item.updatedAt || item.modifiedAt || item.lastModified || item.startDate || new Date().toISOString(),
              isShared: item.isShared || false,
              isCompleted: item.isCompleted || false,
              isActive: item.isActive || false,
              isFavorite: item.isFavorite || false,
              alertsAcknowledged: item.alertsAcknowledged || false,
              colorCategory: item.colorCategory || 'blue',
              totalAlerts: calculatedTotalAlerts,
              hasActiveAlerts: item.hasActiveAlerts || false,
              healthsafety: item.healthsafety || null,
              healthSafetyAlerts: item.healthSafetyAlerts || null,
              // Include packit picture ID - check multiple possible locations
              packitPictureId: item.packitPictureId || item.packit_picture_id || item.packitPicture || null,
            };
          });
          
          allPackIts = [...allPackIts, ...newPackIts];
          pageNum++;
          
          // Safety limit
          if (pageNum > 100) {
            console.warn('Reached safety limit while fetching all packits');
            break;
          }
        } else {
          break;
        }
      } while (lastKey);
      
      console.log('Fetched all packits, total count:', allPackIts.length);
      
      // Deduplicate packits by id
      const uniquePackIts = allPackIts.filter((packit, index, self) => 
        index === self.findIndex(p => p.id === packit.id)
      );
      
      if (uniquePackIts.length !== allPackIts.length) {
        console.warn(`Removed ${allPackIts.length - uniquePackIts.length} duplicate packits when fetching all packits`);
      }
      
      const validPackIts = uniquePackIts.filter(p => p !== null);
      setPackIts(sortPackIts(validPackIts));
      setTotalPackItsCount(validPackIts.length);
      setAllPackItsLoaded(true);
      setLastEvaluatedKey(null);
      setHasMorePages(false);
    } catch (error) {
      console.error('Error fetching all packits:', error);
    } finally {
      setIsLoadingPackIts(false);
    }
  };

  // Function to fetch total count of packits
  const fetchTotalPackItsCount = async () => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      
      if (!token) {
        console.error('No valid user token.');
        return;
      }
      
      // Fetch all packits without limit to get accurate count
      let totalCount = 0;
      let lastKey = null;
      let pageNum = 1;
      
      do {
        let url = `${apiUrl}/briefings?limit=100`; // Use larger limit to reduce requests
        if (lastKey) {
          url += `&lastEvaluatedKey=${encodeURIComponent(lastKey)}`;
        }
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const items = response.data.items || response.data.Items || [];
        if (items) {
          totalCount += items.length;
          lastKey = response.data.lastEvaluatedKey || null;
          pageNum++;
          
          // Safety limit to prevent infinite loops
          if (pageNum > 100) {
            console.warn('Reached safety limit while counting packits');
            break;
          }
        } else {
          break;
        }
      } while (lastKey);
      
      setTotalPackItsCount(totalCount);
      setAllPackItsLoaded(true);
      console.log('Total packits count:', totalCount);
    } catch (error) {
      console.error('Error fetching total packits count:', error);
    }
  };

  // -------------------------
  // 4) Effects, utilities, etc.
  // -------------------------
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
    });
  }, [location]);

  useEffect(() => {
    // Reset state when component mounts (when navigating to the page)
    console.log('PackIt component mounted/navigated to - resetting state and fetching packits');
    setPackIts([]);
    setSearchTerm('');
    setFilters({
      upcoming: false,
      shared: false,
      completed: false,
      favorites: false,
      active: false
    });
    setCurrentPage(1);
    setPaginationTokens({});
    setLastEvaluatedKey(null);
    setTotalPackItsCount(0);
    setAllPackItsLoaded(false);
    setIsLoadingPackIts(true);
    
    // Fetch packits after resetting state
    fetchPackIts(1, null);
  }, []); // Run once on mount

  // Helper function to show browser notification when packit reaches 100% completion
  const showCompletionNotification = (packitId, packitName) => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return;
    }
    
    if (Notification.permission === 'granted') {
      const notification = new Notification('Packit Complete!', {
        body: `${packitName} is now 100% complete and ready for your trip!`,
        icon: '/favicon.ico', // Use app icon
        tag: `packit-complete-${packitId}`, // Prevent duplicate notifications
        requireInteraction: false
      });
      
      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
      
      // Handle notification click to focus window
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  // Helper function to detect completion transitions and trigger notifications
  const checkAndNotifyCompletion = (packitId, newCompletion, packitName) => {
    const previousCompletion = packitCompletions[packitId] || 0;
    
    // Only notify if transitioning from <100% to 100%
    if (previousCompletion < 100 && newCompletion === 100) {
      // Show browser notification (if permission granted)
      showCompletionNotification(packitId, packitName);
      
      // Always show in-app banner notification
      setCompletionNotifications(prev => {
        // Check if notification already exists for this packit
        const exists = prev.some(notif => notif.packitId === packitId);
        if (!exists) {
          return [...prev, { packitId, packitName, timestamp: Date.now() }];
        }
        return prev;
      });
    }
    
    // Update completion state
    setPackitCompletions(prev => ({
      ...prev,
      [packitId]: newCompletion
    }));
  };
  
  // Function to dismiss completion notification banner
  const dismissCompletionNotification = (packitId) => {
    setCompletionNotifications(prev => prev.filter(notif => notif.packitId !== packitId));
  };

  // Function to open detail modal
  const openDetailModal = (item, type) => {
    setSelectedDetailItem(item);
    setDetailModalType(type);
    setDetailModalOpen(true);
  };

  // Function to close detail modal
  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedDetailItem(null);
    setDetailModalType(null);
  };

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          setNotificationPermission(permission);
        });
      } else {
        setNotificationPermission(Notification.permission);
      }
    }
  }, []);

  // Auto-dismiss completion notifications after 10 seconds
  useEffect(() => {
    const timers = completionNotifications.map(notif => {
      return setTimeout(() => {
        dismissCompletionNotification(notif.packitId);
      }, 10000); // 10 seconds
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [completionNotifications]);

  useEffect(() => {
    if (socket) {
      socket.onmessage = async (event) => {
        try {
          // Log the raw message data
          console.log('Raw WebSocket message received: ', event.data);

          // Parse the message data (single JSON.parse since event.data is already a string)
          const data = JSON.parse(event.data);

          // Log the parsed data to ensure it's in the expected format
          console.log('Parsed WebSocket message: ', data);

          // Ensure the parsed data is structured as expected
          if (data && typeof data === 'object' && data.type) {
            console.log('WebSocket message type: ', data.type);
            console.log('WebSocket data: ', data);

            // Handle answer messages (they may not have clientId)
            if (data.type === 'answer') {
              console.log('Processing answer message');
              console.log('Answer data:', data.data);
              setIsWaitingForResponse(false);
              setHasError(false);
              setErrorMessage('');
              const bedrockMessage = {
                sender: 'bedrock',
                text: data.data.answer,
              };
              console.log('Adding message to chat:', bedrockMessage);
              setMessages((prevMessages) => [...prevMessages, bedrockMessage]);
            }
            // Handle messages that require clientId matching
            else if (data.clientId && data.clientId === connectionId) {
              console.log('WebSocket clientId matches connectionId');
              if (data.type === 'packitCreated') {
                // Handle initial create to populate list
                const newPackIt = {
                  id: data.packitId,
                  name: data.name || 'New Pack-It',
                  location: '', // or data.location if available
                  date: data.startDate || new Date().toISOString(),
                  endDate: data.endDate || null,
                  lastEdited: data.lastEdited || data.updatedAt || data.modifiedAt || data.lastModified || new Date().toISOString(),
                  isShared: data.isShared || false,
                  isCompleted: data.isCompleted || false,
                  isActive: data.isActive || false,
                };
                // Insert at the top, then re-sort
                setPackIts((prev) => sortPackIts([newPackIt, ...prev]));
              } else if (data.type === 'updatepackit') {
                // Flag the pack‑it as updated
                setPackIts((prevPackIts) =>
                  sortPackIts(prevPackIts.map((p) =>
                    p.id === data.packitId ? { ...p, hasUpdate: true } : p
                  ))
                );
              }
            }

            // Handle packitUpdated messages (these don't have clientId)
            if (data.type === 'packitUpdated') {
              console.log('Processing packitUpdated message for packitId:', data.packitId);
              const { packitId } = data;

              // Update the pack-its list first (set the update flag and update lastEdited time)
              setPackIts((prevPackIts) =>
                sortPackIts(prevPackIts.map((p) =>
                  p.id === packitId ? { 
                    ...p, 
                    hasUpdate: true,
                    lastEdited: new Date().toISOString()
                  } : p
                ))
              );

              // For non-selected Pack-Its, fetch updated data to update alert counts
              if (!selectedPackIt || (selectedPackIt.id !== packitId && selectedPackIt.packitId !== packitId)) {
                console.log('Fetching updated data for non-selected pack-it:', packitId);
                fetchFullPackItDetails(packitId)
                  .then((newDetails) => {
                    // Calculate total alerts from the new health safety data
                    const alertsData = newDetails.healthsafety?.healthSafetyAlerts || [];
                    let totalAlerts = 0;
                    if (Array.isArray(alertsData) && alertsData.length > 0) {
                      if (Array.isArray(alertsData[0].alerts)) {
                        totalAlerts = alertsData.flatMap((block) => safeToArray(block.alerts)).length;
                      } else {
                        totalAlerts = alertsData.filter(alert => 
                          alert && 
                          Object.keys(alert).length > 0 && 
                          (alert.severity || alert.message || alert.title || alert.description)
                        ).length;
                      }
                    }

                    // Calculate completion percentage and check for notification
                    const completion = calculatePackItCompletion(newDetails);
                    const packitName = newDetails.name || packIts.find(p => p.id === packitId)?.name || 'Packit';
                    checkAndNotifyCompletion(packitId, completion, packitName);

                    // Update the pack-its list with new alert counts and header fields (name, description)
                    setPackIts((prevPackIts) =>
                      sortPackIts(prevPackIts.map((p) =>
                        p.id === packitId 
                      ? {
                        ...p,
                        hasUpdate: false,
                        healthsafety: newDetails.healthsafety,
                        totalAlerts: totalAlerts,
                        alertsAcknowledged: false,
                        name: newDetails.name ?? p.name,
                        description: newDetails.description ?? p.description,
                        location: newDetails.location ?? p.location,
                        date: newDetails.startDate ?? newDetails.date ?? p.date,
                        packitPictureId: newDetails.packitPictureId ?? p.packitPictureId,
                      }
                      : p
                      ))
                    );
                  })
                  .catch((err) =>
                    console.error("Failed to update non-selected pack-it details.", err)
                  );
              }

              // If the currently selected pack-it is the one updated,
              // fetch the latest details and update the selected pack-it state.
              if (
                selectedPackIt &&
                (selectedPackIt.id || selectedPackIt.packitId) === packitId
              ) {
                console.log('Fetching updated details for selected pack-it:', packitId);
                fetchFullPackItDetails(packitId)
                  .then((newDetails) => {
                    console.log('Updated pack-it details received:', newDetails);
                    setSelectedPackIt(newDetails);
                    
                    // Show visual feedback that update was received
                    setUpdateFlash(true);
                    setTimeout(() => setUpdateFlash(false), 1000);
                    
                    // Calculate total alerts from the new health safety data
                    const alertsData = newDetails.healthsafety?.healthSafetyAlerts || [];
                    let totalAlerts = 0;
                    if (Array.isArray(alertsData) && alertsData.length > 0) {
                      if (Array.isArray(alertsData[0].alerts)) {
                        totalAlerts = alertsData.flatMap((block) => safeToArray(block.alerts)).length;
                      } else {
                        totalAlerts = alertsData.filter(alert => 
                          alert && 
                          Object.keys(alert).length > 0 && 
                          (alert.severity || alert.message || alert.title || alert.description)
                        ).length;
                      }
                    }

                    // Calculate completion percentage and check for notification
                    const completion = calculatePackItCompletion(newDetails);
                    const packitName = newDetails.name || packIts.find(p => p.id === packitId)?.name || 'Packit';
                    checkAndNotifyCompletion(packitId, completion, packitName);

                    // Update the pack-its list with new data, alert counts, and header fields (name, description)
                    setPackIts((prevPackIts) =>
                      sortPackIts(prevPackIts.map((p) =>
                        p.id === packitId 
                    ? {
                      ...p,
                      hasUpdate: false,                    // clear any "updated" flag
                      healthsafety: newDetails.healthsafety,
                      totalAlerts: totalAlerts,            // update alert count for button
                      alertsAcknowledged: false,           // mark new alerts as un‑acknowledged so badge turns red
                      name: newDetails.name ?? p.name,
                      description: newDetails.description ?? p.description,
                      location: newDetails.location ?? p.location,
                      date: newDetails.startDate ?? newDetails.date ?? p.date,
                      packitPictureId: newDetails.packitPictureId ?? p.packitPictureId,
                    }
                    : p
                      ))
                    );
                  })
                  .catch((err) =>
                    console.error("Failed to update selected pack-it details", err)
                  );
              }
            }
            
            // Handle packitExportGenerated and packitPDFGenerated messages (PDF, CSV, KML)
            const isExportMessage = data.type === 'packitExportGenerated' || data.type === 'packitPDFGenerated';
            if (isExportMessage) {
              console.log('Received export message:', data.type, data);
              const { packitId, status, error, message } = data;
              const filename = data.filename ?? data.pdfFilename;
              
              if (status === 'completed') {
                console.log('Export generation completed for', packitId);
                setPdfGenerating(null);
                setPdfGenerationError(prev => {
                  const updated = { ...prev };
                  delete updated[packitId];
                  return updated;
                });
                // Infer format from filename extension or fall back to stored format
                let format = downloadFormatForPackit[packitId];
                if (!format && filename) {
                  const ext = filename.split('.').pop()?.toLowerCase();
                  if (ext === 'csv' || ext === 'kml' || ext === 'pdf') format = ext;
                }
                format = format || 'pdf';
                const ext = format === 'pdf' ? 'pdf' : format === 'csv' ? 'csv' : 'kml';
                const downloadFilename = filename || `packit-${packitId}.${ext}`;
                downloadFromUrl(packitId, downloadFilename, format);
              } else if (status === 'error') {
                console.error('Export generation failed:', error || message);
                setPdfGenerating(null);
                setPdfGenerationError(prev => ({
                  ...prev,
                  [packitId]: error || message || 'Failed to generate export'
                }));
              }
            }
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };
    }
  }, [socket, connectionId, selectedPackIt, downloadFromUrl, downloadFormatForPackit]);
  

  function PackingCategory({ title, items }) {
    // If no items, don't render anything
    if (!items || items.length === 0) {
      return null;
    }
  
    return (
      <div className="packing-category">
        <h4>{title}</h4>
        <div className="packing-grid">
          {items.map((item, i) => {
            // Normalize item to handle both string and object formats
            const normalizedItem = typeof item === 'string' 
              ? { item: item, quantity: 1, notes: '', confidenceLevel: null, usageDays: [] }
              : {
                  item: item.item || item.name || '',
                  quantity: item.quantity != null ? item.quantity : 1,
                  notes: item.notes || '',
                  confidenceLevel: item.confidenceLevel != null ? item.confidenceLevel : null,
                  usageDays: Array.isArray(item.usageDays) ? item.usageDays : []
                };
            
            // Helper function to highlight quantities in parentheses
            const highlightQuantities = (text) => {
              if (!text) return text;
              // Match text in parentheses (e.g., "(7 pairs)", "(1-2 pairs)", etc.)
              const parts = text.split(/(\([^)]+\))/g);
              return parts.map((part, idx) => {
                if (part.startsWith('(') && part.endsWith(')')) {
                  return <span key={idx} className="quantity-highlight">{part}</span>;
                }
                return part;
              });
            };
            
            return (
              <div className="packing-item-card" key={i}>
                <div className="pic-header">
                  <strong>{highlightQuantities(normalizedItem.item)}</strong>
                </div>
                <p className="pic-notes">
                  <em>Notes:</em> {normalizedItem.notes || ''}
                </p>
                {/* Confidence Level (optional) */}
                {normalizedItem.confidenceLevel != null && (
                  <p className="pic-confidence">
                    <em>Confidence:</em>{' '}
                    {(normalizedItem.confidenceLevel * 100).toFixed(0)}%
                  </p>
                )}
                {/* Usage Days (optional) */}
                {normalizedItem.usageDays && normalizedItem.usageDays.length > 0 && (
                  <p className="pic-usage">
                    <em>Usage Days:</em> {normalizedItem.usageDays.join(', ')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  
  const markers = [
    {
      position: { lat: 33.7413, lng: -118.1038 },
      title: "Location 1"
    }
  ];

  // Utility to sort packits so the active one is on top, then by last edited time (newest first)
  const sortPackIts = (list) => {
    return list.slice().sort((a, b) => {
      // Active trips always come first
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      
      // For non-active trips, sort by last edited time (newest first)
      // Use lastEdited if available, otherwise fall back to date (creation date)
      const aTime = new Date(a.lastEdited || a.date);
      const bTime = new Date(b.lastEdited || b.date);
      return bTime - aTime; // Newest first
    });
  };

  // Utility to deeply remove alertsAcknowledged except at the root
  function stripNestedAlertsAcknowledged(obj, isRoot = true) {
    if (Array.isArray(obj)) {
      return obj.map(item => stripNestedAlertsAcknowledged(item, false));
    }
    if (obj && typeof obj === 'object') {
      const newObj = {};
      for (const key in obj) {
        if (key === 'alertsAcknowledged' && !isRoot) continue;
        newObj[key] = stripNestedAlertsAcknowledged(obj[key], false);
      }
      return newObj;
    }
    return obj;
  }

  const fetchPackIts = async (pageNumber = 1, paginationToken = null, pageSize = null) => {
    setIsLoadingPackIts(true);
    try {
      const session = await fetchAuthSession();
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();

      if (!token) {
        console.error('No valid user token. Not fetching packits.');
        setIsLoadingPackIts(false);
        return;
      }

      // Use provided pageSize or current itemsPerPage state
      const effectivePageSize = pageSize !== null ? pageSize : itemsPerPage;
      
      // Build URL with pagination parameters
      // The API returns lastEvaluatedKey already URL-encoded, so we use it directly in the URL
      let url = `${apiUrl}/briefings?limit=${effectivePageSize > 0 ? effectivePageSize : 100}`;
      
      if (paginationToken) {
        // lastEvaluatedKey is already URL-encoded from API, append directly to URL
        // Don't use URLSearchParams here as it would double-encode it
        url += `&lastEvaluatedKey=${paginationToken}`;
      }
      
      // Fetch only the summary list - full details are fetched on-demand when a Pack-It is selected
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = response.data.items || response.data.Items || [];
      // The API returns lastEvaluatedKey - it's already URL-encoded in the response
      // We'll store it as-is and decode it when we use it in the next API call
      const newLastEvaluatedKey = response.data.lastEvaluatedKey || null;
      
      if (!items || items.length === 0) {
        console.warn('fetchPackIts - No items in API response');
        setPackIts([]);
        setLastEvaluatedKey(null);
        setHasMorePages(false);
        setTotalPackItsCount(0);
        setAllPackItsLoaded(true);
        setIsLoadingPackIts(false);
        return;
      }
      
      const newPackIts = items.map((item) => {
        // Calculate total alerts from healthSafetyAlerts if available
        let calculatedTotalAlerts = item.totalAlerts || 0;
        const alertsData = item.healthsafety?.healthSafetyAlerts || item.healthSafetyAlerts || [];
        if (Array.isArray(alertsData) && alertsData.length > 0) {
          if (Array.isArray(alertsData[0]?.alerts)) {
            // New format: [{ alerts: [...] }, { alerts: [...] }]
            calculatedTotalAlerts = alertsData.flatMap((block) => safeToArray(block.alerts)).length;
          } else {
            // Legacy format: array of alert objects directly
            calculatedTotalAlerts = alertsData.filter(alert => 
              alert && 
              Object.keys(alert).length > 0 && 
              (alert.severity || alert.message || alert.title || alert.description)
            ).length;
          }
        }
        
        return {
          id: item.packitId,
          name: item.name || 'Untitled',
          location: '',
          description: item.description,
          date: item.startDate || new Date().toISOString(),
          endDate: item.endDate || null,
          lastEdited: item.lastEdited || item.updatedAt || item.modifiedAt || item.lastModified || item.startDate || new Date().toISOString(),
          isShared: item.isShared || false,
          isCompleted: item.isCompleted || false,
          isActive: item.isActive || false,
          isFavorite: item.isFavorite || false,
          alertsAcknowledged: item.alertsAcknowledged || false,
          colorCategory: item.colorCategory || 'blue',
          // Calculate total alerts from healthSafetyAlerts data
          totalAlerts: calculatedTotalAlerts,
          hasActiveAlerts: item.hasActiveAlerts || false,
          // Include health safety data for alert counts
          healthsafety: item.healthsafety || null,
          healthSafetyAlerts: item.healthSafetyAlerts || null,
          // Include packit picture ID - check multiple possible locations
          packitPictureId: item.packitPictureId || item.packit_picture_id || item.packitPicture || null,
        };
      });

      // Deduplicate packits by id to prevent duplicates
      const uniquePackIts = newPackIts.filter((packit, index, self) => 
        index === self.findIndex(p => p.id === packit.id)
      );
      
      if (uniquePackIts.length !== newPackIts.length) {
        console.warn(`Removed ${newPackIts.length - uniquePackIts.length} duplicate packits from page ${pageNumber}`);
      }

      // Set the summary list immediately - full details are fetched only when a Pack-It is selected
      const validPackIts = uniquePackIts.filter(p => p !== null && p.id);
      
      console.log('fetchPackIts - Setting packIts state with', validPackIts.length, 'packits');
      console.log('fetchPackIts - Packit names:', validPackIts.map(p => p.name));
      
      const sortedPackIts = sortPackIts(validPackIts);
      
      console.log('fetchPackIts - About to set packIts state with', sortedPackIts.length, 'sorted packits');
      console.log('fetchPackIts - First packit:', sortedPackIts[0] ? { id: sortedPackIts[0].id, name: sortedPackIts[0].name } : 'none');
      console.log('fetchPackIts - All packit IDs:', sortedPackIts.map(p => p.id));
      
      // Set state - this should trigger a re-render
      setPackIts(sortedPackIts);
      
      console.log('fetchPackIts - State updated, packIts.length should be:', sortedPackIts.length);

      // Update pagination state - store lastEvaluatedKey as-is (URL-encoded from API)
      setLastEvaluatedKey(newLastEvaluatedKey);
      setHasMorePages(!!newLastEvaluatedKey);
      
      // Store pagination token for this page (store URL-encoded version as-is)
      if (newLastEvaluatedKey) {
        setPaginationTokens(prev => ({
          ...prev,
          [pageNumber]: newLastEvaluatedKey // Store as-is (already URL-encoded from API)
        }));
      }
      
      // Update total count: if no more pages, we know the exact count
      if (!newLastEvaluatedKey) {
        // This is the last page, so we know the exact total
        const exactTotal = (pageNumber - 1) * (effectivePageSize || 100) + validPackIts.length;
        setTotalPackItsCount(exactTotal);
        setAllPackItsLoaded(true);
      } else {
        // Still more pages, estimate total (only if we have a page size)
        if (effectivePageSize > 0) {
          setTotalPackItsCount((pageNumber - 1) * effectivePageSize + validPackIts.length + effectivePageSize);
        } else {
          // Fetching all - continue accumulating
          setTotalPackItsCount(prev => prev + validPackIts.length);
        }
        setAllPackItsLoaded(false);
      }

      // Example: random number for alerts
      //setAlerts(Math.floor(Math.random() * 5));
    } catch (error) {
      console.error('Error fetching pack-its:', error);
    } finally {
      setIsLoadingPackIts(false);
    }
  };

  // Put this function near the top of your file, below imports
  const fetchFullPackItDetails = async (packitId) => {
    try {
      // Use the authenticated request utility with automatic token refresh
      const response = await makeAuthenticatedRequest(`${apiUrl}/briefings/${packitId}`, {
        method: 'GET'
      });

      // The server response should have the full itinerary, weather, packing list, etc.
      const data = response.data;
      const cleaned = stripNestedAlertsAcknowledged(data, true);
      return cleaned;
    } catch (error) {
      console.error('Error in fetchFullPackItDetails:', error);
      handleAuthError(error, 'fetching pack-it details');
      throw error;
    }
  };

  const fetchUserInfo = async () => {
    setIsLoadingUserInfo(true);
    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        console.error('Failed to retrieve user information.');
        return;
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      const response = await axios.get(url, { headers });
      const data = response.data[0];
      console.log('Plan Found:', data.plan);
      console.log('Trials Completed:', data.packitTrialComplete);

      setUserPlan(data.plan || '');
      setPackitTrialComplete(data.packitTrialComplete || false);
    } catch (error) {
      console.error('Error fetching user information:', error);
    } finally {
      setIsLoadingUserInfo(false);
    }
  };

  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  const handleSubscriptionUpgrade = () => {
    ReactGA.event({
      category: 'User',
      action: 'Manage Subscription Clicked',
    });
    navigate('/plan-subscription');
  };

  const UpgradeMessage = () => (
    <div className="upgrade-message">
      <p>Upgrade your plan to create more Pack-its!</p>
      <button
        className="subscription-button"
        onClick={handleSubscriptionUpgrade}
      >
        Upgrade Now
      </button>
    </div>
  );

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const handleCreateNew = () => {
    // Only Globe Trotter can create packits (Basic has trial, Adventurer cannot)
    if (userPlan !== 'Globe Trotter' && !(userPlan === 'Basic' && !packitTrialComplete)) {
      // Show upgrade message instead of navigating
      return;
    }
    // Goes to the create-pack-it form
    navigate('/create-pack-it');
  };

  const handlePackItSelect = async (packIt, event) => {
    // If the click came from a button, do nothing.
    if (event.target.closest('button')) {
      return;
    }
  
    // Check both possible property names for the selected pack-it.
    const isSelected =
      selectedPackIt &&
      (selectedPackIt.id === packIt.id || selectedPackIt.packitId === packIt.id);
  
    if (isSelected) {
      setSelectedPackIt(null);
      return;
    }
  
    // Clear any update flag and then fetch the full details.
    setPackIts((prev) =>
      sortPackIts(prev.map((p) =>
        p.id === packIt.id ? { ...p, hasUpdate: false } : p
      ))
    );
  
    try {
      const fullPackItData = await fetchFullPackItDetails(packIt.id);
      console.log('FULL PACK-IT DATA:', fullPackItData);
      
      // Calculate total alerts from healthSafetyAlerts if available
      let calculatedTotalAlerts = fullPackItData.totalAlerts || 0;
      const alertsData = fullPackItData.healthsafety?.healthSafetyAlerts || fullPackItData.healthSafetyAlerts || [];
      if (Array.isArray(alertsData) && alertsData.length > 0) {
        if (Array.isArray(alertsData[0]?.alerts)) {
          // New format: [{ alerts: [...] }, { alerts: [...] }]
          calculatedTotalAlerts = alertsData.flatMap((block) => safeToArray(block.alerts)).length;
        } else {
          // Legacy format: array of alert objects directly
          calculatedTotalAlerts = alertsData.filter(alert => 
            alert && 
            Object.keys(alert).length > 0 && 
            (alert.severity || alert.message || alert.title || alert.description)
          ).length;
        }
      }
      
      // Update totalAlerts in the full packit data
      const packItWithAlerts = {
        ...fullPackItData,
        totalAlerts: calculatedTotalAlerts
      };
      
      console.log('Setting selectedPackIt with packitPictureId:', fullPackItData.packitPictureId);
      setSelectedPackIt(packItWithAlerts);
      
      // Also update the packIts array with the correct alert count and packitPictureId
      setPackIts((prev) =>
        sortPackIts(prev.map((p) =>
          p.id === packIt.id ? { 
            ...p, 
            totalAlerts: calculatedTotalAlerts,
            packitPictureId: fullPackItData.packitPictureId || p.packitPictureId
          } : p
        ))
      );
      
      // Calculate completion percentage and update state (but don't notify on selection, only on updates)
      const completion = calculatePackItCompletion(fullPackItData);
      setPackitCompletions(prev => ({
        ...prev,
        [packIt.id]: completion
      }));
    } catch (err) {
      console.error('Error fetching Pack-It details:', err);
      handleAuthError(err, 'loading pack-it details');
    }
  };
  
  // Helper to check if a value exists and is not empty.
// Helper to check if a value exists and is not empty.
const existsAndNotEmpty = (val) => {
  if (!val) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "object") return Object.keys(val).length > 0;
  return true;
};

function calculatePackItCompletion(packIt) {
  if (!packIt) return 0;

  // 1. Extract itinerary and get all unique dates
  const normalizedItinerary = normalizeItinerary(packIt.itinerary);
  const itineraryArray = normalizedItinerary.itineraryArray || [];
  
  if (itineraryArray.length === 0) return 0;

  // Extract unique dates from itinerary days
  // Calculate dates from startDate + day number if date is not directly available
  const tripStartDate = packIt.startDate || normalizedItinerary.startDate;
  const dates = itineraryArray.map((day, index) => {
    if (day.date) {
      // Use date directly if available, normalize to YYYY-MM-DD format
      const dateStr = day.date.includes('T') ? day.date.split('T')[0] : day.date;
      return dateStr;
    } else if (tripStartDate) {
      // Calculate date from startDate and day number
      const startDate = new Date(tripStartDate);
      const dayNumber = day.day || (index + 1);
      const calculatedDate = new Date(startDate);
      calculatedDate.setDate(startDate.getDate() + dayNumber - 1);
      return calculatedDate.toISOString().split('T')[0];
    }
    return null;
  }).filter(Boolean); // Remove null values

  if (dates.length === 0) return 0;
  
  console.log('[Completion] Itinerary dates extracted:', dates);

  // 2. For each date, check if places to stay exist
  let daysWithPlacesToStay = 0;
  dates.forEach(date => {
    const staysForDate = normalizePlacesToStay(packIt.placestostay, null, date);
    if (staysForDate && staysForDate.length > 0) {
      daysWithPlacesToStay++;
    }
  });

  // 3. For each date, check if places to eat exist
  let daysWithPlacesToEat = 0;
  dates.forEach(date => {
    const restaurantsForDate = normalizePlacesToEat(packIt.placestoeat, null, date);
    if (restaurantsForDate && restaurantsForDate.length > 0) {
      daysWithPlacesToEat++;
    }
  });

  // 4. For each date, check if things to do exist
  let daysWithThingsToDo = 0;
  const allThingsToDo = normalizeThingsToDo(packIt.thingstodo);
  console.log('[Completion] All normalized things to do:', allThingsToDo.length, 'activities');
  console.log('[Completion] Activity dates:', allThingsToDo.map(a => a.date).filter(Boolean));
  
  dates.forEach(date => {
    // Normalize date to YYYY-MM-DD format (handle both ISO strings and YYYY-MM-DD strings)
    const dateStr = date.includes('T') ? date.split('T')[0] : date;
    
    const activitiesForDate = allThingsToDo.filter(activity => {
      if (activity.date) {
        // Normalize activity date to YYYY-MM-DD format
        // Handle both ISO strings and plain YYYY-MM-DD strings
        let activityDate = activity.date;
        if (activityDate.includes('T')) {
          activityDate = activityDate.split('T')[0];
        }
        // Also handle if dateKey might have timezone info or other formats
        activityDate = activityDate.split(' ')[0]; // Remove any time component
        
        const matches = activityDate === dateStr;
        if (matches) {
          console.log(`[Completion] ✓ Matched activity for date ${dateStr}:`, activity.name || activity.item || 'unnamed');
        }
        return matches;
      }
      return false;
    });
    
    if (activitiesForDate && activitiesForDate.length > 0) {
      daysWithThingsToDo++;
      console.log(`[Completion] ✓ Day ${dateStr} has ${activitiesForDate.length} thing(s) to do`);
    } else {
      console.log(`[Completion] ✗ Day ${dateStr} has NO things to do`);
    }
  });
  
  console.log(`[Completion] Total days with things to do: ${daysWithThingsToDo} out of ${dates.length} days`);

  // 5. Health & Safety (counts as 1 if exists, 0 if not)
  let healthSafetyExists = false;
  if (packIt.healthsafety) {
    // Check for healthSafetyAlerts array with items
    if (Array.isArray(packIt.healthsafety.healthSafetyAlerts) && packIt.healthsafety.healthSafetyAlerts.length > 0) {
      // Verify that at least one alert block has alerts
      healthSafetyExists = packIt.healthsafety.healthSafetyAlerts.some(alertBlock => {
        // Check if alertBlock has nested alerts array
        if (Array.isArray(alertBlock.alerts) && alertBlock.alerts.length > 0) {
          return true;
        }
        // Or if alertBlock itself is an alert object (has alertType or severity)
        if (alertBlock.alertType || alertBlock.severity) {
          return true;
        }
        return false;
      });
    }
    // Also check for direct healthSafetyAlerts at root level (alternative structure)
    if (!healthSafetyExists && Array.isArray(packIt.healthSafetyAlerts) && packIt.healthSafetyAlerts.length > 0) {
      healthSafetyExists = true;
    }
  }
  const healthSafetyCount = healthSafetyExists ? 1 : 0;

  // 6. Weather (counts as 1 if exists, 0 if not)
  const weatherExists =
    (Array.isArray(packIt.weatherFull) && packIt.weatherFull.length > 0) ||
    (Array.isArray(packIt.weatherPartial) && packIt.weatherPartial.length > 0);
  const weatherCount = weatherExists ? 1 : 0;

  // 7. Packing List (counts as 1 if exists, 0 if not)
  let packingListExists = false;
  if (packIt.packingList && packIt.packingList.classification !== 'custom') {
    const packingListData = packIt.packingList.packingList || packIt.packingList;
    if (packingListData && typeof packingListData === 'object') {
      const hasData = Object.keys(packingListData).some(key => {
        if (key === 'classification') return false;
        const category = packingListData[key];
        if (Array.isArray(category) && category.length > 0) {
          return true;
        }
        if (typeof category === 'object' && !Array.isArray(category)) {
          return Object.values(category).some(subcat => 
            Array.isArray(subcat) && subcat.length > 0
          );
        }
        return false;
      });
      packingListExists = hasData;
    }
  }
  const packingListCount = packingListExists ? 1 : 0;

  // Calculate completion
  // Total = (number of days * 3) + 1 (health & safety) + 1 (weather) + 1 (packing list)
  const totalPossible = (dates.length * 3) + 3;
  
  // Completed = days with stays + days with things to do + days with places to eat + health & safety + weather + packing list
  const completed = daysWithPlacesToStay + daysWithThingsToDo + daysWithPlacesToEat + healthSafetyCount + weatherCount + packingListCount;
  
  const completionPercentage = Math.round((completed / totalPossible) * 100);
  
  console.log(`[Completion] Calculation summary:`, {
    totalDays: dates.length,
    daysWithPlacesToStay,
    daysWithThingsToDo,
    daysWithPlacesToEat,
    healthSafetyCount,
    weatherCount,
    packingListCount,
    completed,
    totalPossible,
    completionPercentage: `${completionPercentage}%`
  });
  
  return completionPercentage;
}







  // -------------------------
  // 5) Chat logic
  // -------------------------

  // Scroll to top of latest bedrock response instead of bottom
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === 'bedrock') {
        // Scroll to top of the last bedrock message when it arrives
        setTimeout(() => {
          if (lastBedrockMessageRef.current) {
            lastBedrockMessageRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 150); // Small delay to ensure DOM is updated
      } else if (lastMessage.sender === 'user') {
        // For user messages, scroll to bottom as before
        scrollToBottom();
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if user has remaining chat requests
  const checkChatRequestLimit = () => {
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000); // 1 hour in milliseconds
    
    // Filter requests from the last hour and clean up old ones
    const recentRequests = chatRequests.filter(timestamp => timestamp > oneHourAgo);
    
    // If we filtered out old requests, update localStorage
    if (recentRequests.length !== chatRequests.length) {
      setChatRequests(recentRequests);
      localStorage.setItem('chatRequests', JSON.stringify(recentRequests));
    }
    
    // Determine limits based on user plan
    const maxRequests = userPlan === 'Basic' ? 5 : 20;
    const remaining = Math.max(0, maxRequests - recentRequests.length);
    
    return remaining > 0;
  };

  // Add a new chat request timestamp
  const addChatRequest = () => {
    const now = Date.now();
    const newRequests = [...chatRequests, now];
    setChatRequests(newRequests);
    // Save to localStorage
    localStorage.setItem('chatRequests', JSON.stringify(newRequests));
  };

  const handleSendMessage = async (messageText) => {
    const textToSend = (typeof messageText === 'string' ? messageText : inputMessage ?? '').trim();
    if (textToSend) {
      // Check message length limit (1000 characters - enough for detailed questions with descriptions/links)
      const MAX_MESSAGE_LENGTH = 1000;
      if (textToSend.length > MAX_MESSAGE_LENGTH) {
        setHasError(true);
        setErrorMessage(`Message is too long. Please limit your message to ${MAX_MESSAGE_LENGTH} characters. Current length: ${textToSend.length}`);
        return;
      }
      
      // Check request limits before proceeding
      if (!checkChatRequestLimit()) {
        const maxRequests = userPlan === 'Basic' ? 5 : 20;
        const limitMessage = userPlan === 'Basic' 
          ? `You've reached your limit of ${maxRequests} requests per hour. Upgrade your plan to increase your limit!`
          : `You've reached your limit of ${maxRequests} requests per hour. Please try again later.`;
        
        setHasError(true);
        setErrorMessage(limitMessage);
        return;
      }

      const userMessage = { sender: 'user', text: textToSend };
      setMessages((prev) => [...prev, userMessage]);
      const currentMessage = textToSend;
      if (!messageText) setInputMessage('');
      
      // Add this request to our tracking
      addChatRequest();

      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();

      if (!token) {
        alert('You need to be logged in to submit a request.');
        return;
      }

      const headers = {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      };

      // Append list of PackIt IDs and names to the question
      const packitsText = packIts.length > 0 
        ? '\n\nMy PackIts: ' + packIts.map(packit => `${packit.id}-${packit.name}`).join(', ')
        : '';

      const payload = {
        question: currentMessage + packitsText,
        ClientID: connectionId,
      };

      setHasError(false);
      setErrorMessage('');
      setIsWaitingForResponse(true);

      // Set a timeout for the response
      const timeoutId = setTimeout(() => {
        setIsWaitingForResponse(false);
        setHasError(true);
        setErrorMessage('Request timed out. Please try again.');
      }, 120000); // 2 minute timeout

      try {
        const response = await axios.post(`${apiUrl}/chatbot`, payload, {
          headers,
        });

        // For an async Lambda, 202 is normal; for sync, 200 is normal
        if (response.status === 202 || response.status === 200) {
          // Everything good, wait for WebSocket answer
          // Clear timeout when we get a successful response
          clearTimeout(timeoutId);
        } else {
          clearTimeout(timeoutId);
          setIsWaitingForResponse(false);
          setHasError(true);
          setErrorMessage('Failed to send message. Please try again.');
          throw new Error('Failed to create question');
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Error creating question:', error);
        setIsWaitingForResponse(false);
        setHasError(true);
        setErrorMessage('Failed to send message. Please check your connection and try again.');
      }
    }
  };

  function PackingCategory({ title, items = [], category, subcategory = null, packIt }) { // Default to empty array
    if (!Array.isArray(items) || items.length === 0) { 
      return null; 
    }
  
    return (
      <div className="packing-category">
        <div className="packing-category-header">
          <h4 className="packing-category-title">{title}</h4>
          <div className="packing-category-actions">
            <span className="packing-category-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                                           {isPackItBeingEdited(packIt.id) && (
              <button
                className="add-item-button"
                onClick={() => addPackingItem(category, subcategory)}
                title={`Add ${title} Item`}
              >
                <i className="fas fa-plus"></i>
                Add
              </button>
            )}
          </div>
        </div>
        <div className="packing-items-list">
          {items.map((item, i) => {
            // Normalize item to handle both string and object formats
            const normalizedItem = typeof item === 'string' 
              ? { item: item }
              : {
                  item: item.item || item.name || '',
                  quantity: item.quantity,
                  notes: item.notes,
                  confidenceLevel: item.confidenceLevel,
                  usageDays: item.usageDays
                };
            
            // Helper function to highlight quantities in parentheses
            const highlightQuantities = (text) => {
              if (!text) return text;
              // Match text in parentheses (e.g., "(7 pairs)", "(1-2 pairs)", etc.)
              const parts = text.split(/(\([^)]+\))/g);
              return parts.map((part, idx) => {
                if (part.startsWith('(') && part.endsWith(')')) {
                  return <span key={idx} className="quantity-highlight">{part}</span>;
                }
                return part;
              });
            };
            
            return (
              <div className="packing-item" key={`${category}-${subcategory || 'main'}-${i}`}>
                {isPackItBeingEdited(packIt.id) ? (
                  <div className="packing-item-edit">
                    <PackingInput
                      category={category}
                      itemIndex={i}
                      field="item"
                      defaultValue={normalizedItem.item || ''}
                      subcategory={subcategory}
                      type="text"
                      placeholder="Item name"
                      className="edit-input"
                      onRemove={removePackingItem}
                    />
                    <PackingInput
                      category={category}
                      itemIndex={i}
                      field="quantity"
                      defaultValue={normalizedItem.quantity || 1}
                      subcategory={subcategory}
                      type="number"
                      placeholder="Qty"
                      className="edit-input quantity-input"
                      min="1"
                    />
                    <PackingTextarea
                      category={category}
                      itemIndex={i}
                      field="notes"
                      defaultValue={normalizedItem.notes || ''}
                      subcategory={subcategory}
                      placeholder="Notes (optional)"
                      className="edit-textarea"
                    />
                  </div>
                ) : (
                  <>
                    <input 
                      type="checkbox" 
                      id={`${category}-${subcategory || 'main'}-${i}-checkbox`}
                      name={`${category}-${subcategory || 'main'}-${i}-checkbox`}
                      className="packing-item-checkbox"
                      defaultChecked={false}
                      aria-label={`Mark ${normalizedItem.item || 'item'} as packed`}
                    />
                    <div className="packing-item-content">
                      <div className="packing-item-name">
                        {highlightQuantities(normalizedItem.item)}
                      </div>
                      <div className="packing-item-notes">
                        {normalizedItem.notes || ''}
                      </div>
                      {(normalizedItem.confidenceLevel != null || (normalizedItem.usageDays && normalizedItem.usageDays.length > 0)) && (
                        <div className="packing-item-meta">
                          {normalizedItem.confidenceLevel != null && (
                            <span className="confidence-badge">
                              {(normalizedItem.confidenceLevel * 100).toFixed(0)}% confidence
                            </span>
                          )}
                          {normalizedItem.usageDays && normalizedItem.usageDays.length > 0 && (
                            <span className="usage-badge">
                              Days: {normalizedItem.usageDays.join(', ')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  
  // Nicely format chat messages with paragraphs / bullet points
  const formatChatMessage = (text) => {
    const lines = text.split(/(?<=\n)|(?=\d\.\s)/);
    const formattedLines = [];

    lines.forEach((line, index) => {
      if (/^\d\.\s/.test(line)) {
        // numbered list
        formattedLines.push(
          <div key={index} className="chat-list-item">
            {line.trim()}
          </div>
        );
      } else if (line.trim() === '') {
        // empty line => spacer
        formattedLines.push(
          <div key={`${index}-spacer`} className="chat-spacer">
            &nbsp;
          </div>
        );
      } else {
        // plain text => paragraph
        formattedLines.push(
          <p key={index} className="chat-paragraph">
            {line.trim()}
          </p>
        );
      }
    });

    return formattedLines;
  };

  // -------------------------
  // 6) Return (UI) - Modern Layout Structure
  // -------------------------
  return (
    <div className="App pack-it-page">
      {/* Modern Header/Banner */}
      <Header currentPage="pack-it" />

      {/* Main Content Area */}
      <div className="main-content">
        <div className="content-header">
          <div className="header-content">
            <div className="header-title-row">
              <h1>Trip Pack-it</h1>
            </div>
            <div className="header-actions-row">
              <div className="header-actions-left">
                <input
                  type="text"
                  id="search-packits"
                  name="search-packits"
                  placeholder="Search pack-its..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  aria-label="Search pack-its"
                />

                <div className="filter-container">
                  <button
                    className={`filter-button modern-icon-button ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <i className="fas fa-filter"></i>
                    <span>Filters</span>
                  </button>

                  {showFilters && (
                    <div className="filter-dropdown">
                      <div className="filter-checkboxes">
                        <label>
                          <input
                            type="checkbox"
                            id="filter-upcoming"
                            name="filter-upcoming"
                            checked={filters.upcoming}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                upcoming: e.target.checked,
                              })
                            }
                          />
                          Upcoming
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            id="filter-shared"
                            name="filter-shared"
                            checked={filters.shared}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                shared: e.target.checked,
                              })
                            }
                          />
                          Shared
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            id="filter-completed"
                            name="filter-completed"
                            checked={filters.completed}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                completed: e.target.checked,
                              })
                            }
                          />
                          Completed
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            id="filter-favorites"
                            name="filter-favorites"
                            checked={filters.favorites}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                favorites: e.target.checked,
                              })
                            }
                          />
                          Favorites
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            id="filter-active"
                            name="filter-active"
                            checked={filters.active}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                active: e.target.checked,
                              })
                            }
                          />
                          Active
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                
                {!isLoadingUserInfo && userPlan === 'Globe Trotter' ? (
                  <button
                    className="create-note-button-circle2 modern-icon-button"
                    onClick={handleCreateNew}
                    title="Create New Pack-It"
                  >
                    <i className="fas fa-plus"></i>
                    <span>Create Pack-It</span>
                  </button>
                ) : !isLoadingUserInfo && (userPlan === 'Basic' && !packitTrialComplete) ? (
                  <button
                    className="create-note-button-circle2 modern-icon-button"
                    onClick={handleCreateNew}
                    title="Create New Pack-It (Trial)"
                  >
                    <i className="fas fa-plus"></i>
                    <span>Create Pack-It</span>
                  </button>
                ) : !isLoadingUserInfo ? (
                  <UpgradeMessage />
                ) : null}
              </div>
              <div className="header-actions-right">
                <button
                  className="toggle-chat-button modern-icon-button"
                  onClick={() => {
                    const newMinimizedState = !isChatMinimized;
                    setIsChatMinimized(newMinimizedState);
                    if (newMinimizedState) {
                      setIsChatExpanded(false);
                    }
                  }}
                >
                  <i className={`fas ${isChatMinimized ? 'fa-comments' : 'fa-eye-slash'}`}></i>
                  <span>{isChatMinimized ? 'Show Chat' : 'Hide Chat'}</span>
                </button>
                {!isChatMinimized && (
                  <button
                    className="expand-chat-button modern-icon-button"
                    onClick={() => {
                      setIsChatExpanded(!isChatExpanded);
                    }}
                  >
                    <i className={`fas ${isChatExpanded ? 'fa-compress' : 'fa-expand'}`}></i>
                    <span>{isChatExpanded ? 'Collapse Chat' : 'Expand Chat'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={`main-content-area ${isChatMinimized ? 'chat-hidden' : ''} ${isChatExpanded ? 'chat-expanded' : ''}`}>
          {!isChatExpanded && (
          <div className="pack-its-list">
          <div className="pack-its-list-items">
          {console.log('Rendering packits - currentItems.length:', currentItems.length, 'filteredPackIts.length:', filteredPackIts.length, 'packIts.length:', packIts.length)}
          {currentItems.length === 0 && isLoadingPackIts && (
            <div className="loading-container">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading Pack-its...</span>
            </div>
          )}
          {currentItems.length === 0 && !isLoadingPackIts && packIts.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-suitcase-rolling"></i>
              <h3>No Pack-its Yet</h3>
              <p>Add your first Pack-it to start organizing!</p>
              <button className="create-packit-btn modern-icon-button primary" onClick={handleCreateNew}>
                <i className="fas fa-plus-circle"></i>
                <span>Add Pack-it</span>
              </button>
            </div>
          ) : currentItems.length === 0 && !isLoadingPackIts && packIts.length > 0 ? (
            <div className="empty-state">
              <i className="fas fa-suitcase-rolling"></i>
              <h3>No Pack-its Match Your Filters</h3>
              <p>Try adjusting your search or filters to see more results.</p>
            </div>
          ) : null}
          {currentItems.map((packIt, index) => {
              // Get the ID from the selected pack-it, accounting for both "id" and "packitId"
              // Compute completion percentage only if this pack-it is selected.
              const isSelected = selectedPackIt && ((selectedPackIt.id || selectedPackIt.packitId) === packIt.id);
              const completionPercentage = isSelected ? calculatePackItCompletion(selectedPackIt) : 0;

              const statusLabel = packIt.isActive ? 'Active'
                                    : packIt.isCompleted ? 'Completed'
                                    : 'Upcoming';

              // Create a unique key using multiple identifiers
              const uniqueKey = packIt.id || packIt.packitid || packIt.packitId || `packit-${index}`;

              // Helper function to get ordered destinations from itinerary
              const getOrderedDestinations = () => {
                const currentPackIt = isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt;
                const normalized = normalizeItinerary(currentPackIt?.itinerary);
                const itineraryArray = normalized.itineraryArray || [];
                
                const destinationOrder = [];
                const seenDestinations = new Set();
                
                // Extract destinations in order from itinerary
                itineraryArray.forEach(day => {
                  // For travel days, check both departure and arrival
                  if (day.checkpointType === 'travel day') {
                    if (day.departureLocationId && !seenDestinations.has(day.departureLocationId)) {
                      destinationOrder.push(day.departureLocationId);
                      seenDestinations.add(day.departureLocationId);
                    }
                    if (day.arrivalLocationId && !seenDestinations.has(day.arrivalLocationId)) {
                      destinationOrder.push(day.arrivalLocationId);
                      seenDestinations.add(day.arrivalLocationId);
                    }
                  } else if (day.locationId && !seenDestinations.has(day.locationId)) {
                    // For fun days, use locationId
                    destinationOrder.push(day.locationId);
                    seenDestinations.add(day.locationId);
                  }
                });
                
                return destinationOrder;
              };

              return (
                <div
                  className={`pack-it-card
                    ${packIt.isActive ? 'pack-it-active' : ''}
                    ${packIt.isCompleted ? 'pack-it-completed' : ''}
                    ${packIt.hasUpdate ? 'pack-it-has-update' : ''}
                  `}
                  key={uniqueKey}
                >
                  <div
  className={`pack-it-header ${selectedPackIt?.id === packIt.id ? 'selected' : ''}`}
  onClick={(e) => handlePackItSelect(packIt, e)}
>
                    {(() => {
                      const pictureId = packIt.packitPictureId;
                      console.log('Rendering packit card:', packIt.id, 'packitPictureId:', pictureId, 'type:', typeof pictureId);
                      // Display image if packitPictureId exists and is not null
                      if (pictureId && pictureId !== null && pictureId !== 'null' && pictureId.trim() !== '') {
                        return (
                          <div className="pack-it-image-container">
                            <img 
                              src={`/images/packit_pics/${pictureId}`}
                              alt={packIt.name}
                              className="pack-it-image"
                              onError={(e) => {
                                console.error('Failed to load packit image:', `/images/packit_pics/${pictureId}`);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('Successfully loaded packit image:', `/images/packit_pics/${pictureId}`);
                              }}
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <div className="pack-it-summary">
                    <h3>
                      <span
                        className={`color-indicator-packit ${packIt.colorCategory}`}

                      ></span>
                      {packIt.name}
                    </h3>
                      {packIt.description && (
                        <p className="pack-it-description">{packIt.description}</p>
                      )}
                      <p>{packIt.location}</p>
                      <p>{new Date(packIt.date).toLocaleDateString()}</p>
                      <div className="status-label">{statusLabel}</div>
                    </div>

                    {/* New status bar container to the left of action buttons */}
                    <div className="pack-it-actions-wrapper">
                    {/* Show status bar when the full pack-it is selected */}
        {/* Only render the status bar for the selected pack-it */}
        {isSelected && (
          <div className="packit-status-bar-container" style={{ width: '100%', marginBottom: '8px' }}>
            <div
              className="packit-status-bar"
              style={{ width: `${completionPercentage}%` }}
              title={`Package is ${completionPercentage}% complete`}
            ></div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Progress: {completionPercentage}%
            </div>
          </div>
        )}

                    <div className="pack-it-actions">
                      {isPackItBeingEdited(packIt.id) ? (
                        <>
                          <button
                            type="button"
                            className="icon-button save-icon modern-icon-button"
                            onClick={handleSaveEdit}
                            title="Save Changes"
                          >
                            <i className="fas fa-save"></i>
                          </button>
                          <button
                            type="button"
                            className="icon-button cancel-icon modern-icon-button"
                            onClick={handleCancelEdit}
                            title="Cancel Changes"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="icon-button refresh-icon modern-icon-button"
                            onClick={() => handleRefresh(packIt.id)}
                            title="Refresh"
                            style={{ opacity: '1', color: 'inherit' }}
                          >
                            <i className="fas fa-sync-alt" style={{ opacity: '1', color: 'inherit' }}></i>
                            <span className="fa-fallback" style={{ display: 'none', fontSize: '14px' }}>↻</span>
                          </button>                    
                          <button
                            type="button"
                            className={`icon-button bookmark-icon note-button-favorite ${packIt.isFavorite ? "favorited" : ""}`}
                            onClick={() => handleBookmark(packIt.id)}
                            title="Favorite"
                          >
                          </button>
                          <button
                            type="button"
                            className={`icon-button active-icon modern-icon-button ${packIt.isActive ? "active" : ""}`}
                            onClick={() => handleActive(packIt.id)}
                            title={packIt.isActive ? "Set Inactive" : "Set Active"}
                            style={packIt.isActive ? { color: '#007bff' } : {}}
                          >
                            <i className="fas fa-play-circle"></i>
                          </button>
                          <button
                            type="button"
                            className={`icon-button complete-icon modern-icon-button ${packIt.isCompleted ? "completed" : ""}`}
                            onClick={() => handleComplete(packIt.id)}
                            title={packIt.isCompleted ? "Mark Incomplete" : "Mark Complete"}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            type="button"
                            className={`icon-button download-icon modern-icon-button ${userPlan === 'Basic' ? 'disabled' : ''} ${pdfGenerating === packIt.id ? 'generating' : ''}`}
                            onClick={() => userPlan === 'Basic' ? null : handleDownloadClick(packIt.id)}
                            title={
                              userPlan === 'Basic' 
                                ? 'Upgrade to download' 
                                : pdfGenerating === packIt.id 
                                  ? 'Generating...' 
                                  : 'Download'
                            }
                            disabled={userPlan === 'Basic' || pdfGenerating === packIt.id}
                          >
                            {pdfGenerating === packIt.id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-download"></i>
                            )}
                          </button>
                          {pdfGenerationError[packIt.id] && (
                            <div className="pdf-error-message" style={{ 
                              color: 'var(--error-600)', 
                              fontSize: '12px', 
                              marginTop: '4px',
                              maxWidth: '200px'
                            }}>
                              {pdfGenerationError[packIt.id]}
                            </div>
                          )}
                          {selectedPackIt && (selectedPackIt.id === packIt.id || selectedPackIt.packitId === packIt.id) && (
                            <button
                              type="button"
                              className="icon-button edit-icon modern-icon-button"
                              onClick={() => handleEdit(packIt.id)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        className="icon-button delete-icon modern-icon-button"
                        onClick={() => handleDelete(packIt.id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                      {(() => {
                        // Calculate total alerts - use selectedPackIt if available (more accurate), otherwise use packIt from list
                        let totalAlerts = 0;
                        const packItToUse = (selectedPackIt && (selectedPackIt.id === packIt.id || selectedPackIt.packitId === packIt.id)) 
                          ? selectedPackIt 
                          : packIt;
                        
                        // Try to calculate from healthSafetyAlerts if available
                        const alertsData = packItToUse.healthsafety?.healthSafetyAlerts || packItToUse.healthSafetyAlerts || [];
                        if (Array.isArray(alertsData) && alertsData.length > 0) {
                          if (Array.isArray(alertsData[0]?.alerts)) {
                            // New format: [{ alerts: [...] }, { alerts: [...] }]
                            totalAlerts = alertsData.flatMap((block) => safeToArray(block.alerts)).length;
                          } else {
                            // Legacy format: array of alert objects directly
                            totalAlerts = alertsData.filter(alert => 
                              alert && 
                              Object.keys(alert).length > 0 && 
                              (alert.severity || alert.message || alert.title || alert.description)
                            ).length;
                          }
                        } else {
                          // Fallback to totalAlerts property if healthSafetyAlerts not available
                          totalAlerts = packItToUse.totalAlerts || 0;
                        }
                        
                        const hasActiveAlerts = packItToUse.hasActiveAlerts || false;
                        
                        // Badge color logic based on alertsAcknowledged:
                        // - Green: no alerts
                        // - Grey: has alerts and acknowledged
                        // - Red: has alerts and not acknowledged
                        let badgeColor = "green";
                        if (totalAlerts > 0) {
                          if (packItToUse.alertsAcknowledged) {
                            badgeColor = "#888"; // Acknowledged alerts - always grey
                          } else {
                            badgeColor = "red"; // All unacknowledged alerts - red
                          }
                        }

                        return (
                          <button
                            type="button"
                            className="icon-button alert-button modern-icon-button"
                            onClick={() => handleAlert(packIt.id)}
                            title="Alerts"
                          >
                            <div className="alert-button-content">
                              <i className="fas fa-exclamation-triangle"></i>
                              <span className="alert-text">Alerts</span>
                              {totalAlerts > 0 && (
                                <span className="alert-badge-inline" style={{ backgroundColor: badgeColor }}>
                                  {totalAlerts}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })()}
                    </div>

                    </div>
                  </div>

                  {isSelected && (
                  <div className={`pack-it-details ${updateFlash ? 'update-flash' : ''}`}>
                    {/* Completion Notification Banners */}
                    {completionNotifications
                      .filter(notif => notif.packitId === packIt.id || notif.packitId === packIt.packitId)
                      .map((notif, idx) => (
                        <div key={`${notif.packitId}-${notif.timestamp}`} className="completion-banner">
                          <div className="completion-banner-content">
                            <i className="fas fa-check-circle"></i>
                            <span className="completion-banner-text">
                              <strong>{notif.packitName}</strong> is now 100% complete and ready for your trip!
                            </span>
                            <button
                              className="completion-banner-dismiss"
                              onClick={() => dismissCompletionNotification(notif.packitId)}
                              aria-label="Dismiss notification"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    
                    {/* 1) Tab Buttons */}
                    <div className="tab-buttons">
                      <button
                        onClick={() => setActiveTab('itinerary')}
                        className={activeTab === 'itinerary' ? 'active' : ''}
                      >
                        Itinerary
                      </button>
                      <button
                        onClick={() => setActiveTab('destination')}
                        className={activeTab === 'destination' ? 'active' : ''}
                      >
                        By Destination
                      </button>
                      <button
                        onClick={() => setActiveTab('weather')}
                        className={activeTab === 'weather' ? 'active' : ''}
                      >
                        Weather
                      </button>
                      <button
                        onClick={() => setActiveTab('packing')}
                        className={activeTab === 'packing' ? 'active' : ''}
                      >
                        Packing List
                      </button>
                      <button
                        onClick={() => setActiveTab('map')}
                        className={activeTab === 'map' ? 'active' : ''}
                      >
                        Map
                      </button>

                    </div>

                                         {/* Tab Content */}
                     {activeTab === 'itinerary' && (
                       <div className="tab-content itinerary-content">
                         <div className="itinerary-header-section">
                           <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <h3 className="itinerary-header">Your Trip Itinerary</h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <button
                                className="collapse-expand-button"
                                onClick={collapseAllSections}
                                title="Collapse All Sections"
                              >
                                <i className="fas fa-compress-alt"></i>
                                Collapse All
                              </button>
                              <button
                                className="collapse-expand-button"
                                onClick={expandAllSections}
                                title="Expand All Sections"
                              >
                                <i className="fas fa-expand-alt"></i>
                                Expand All
                              </button>
                              {renderDisplayModeSelector()}
                            </div>
                          </div>
                          {/* Destination Breadcrumb */}
                          {(() => {
                            const destinations = getOrderedDestinations();
                            if (destinations.length > 0) {
                              return (
                                <div className="destination-breadcrumb" style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                                  <span style={{ fontWeight: '500' }}>Destinations: </span>
                                  {destinations.map((dest, idx) => (
                                    <span key={idx}>
                                      <span style={{ fontWeight: '500' }}>{dest}</span>
                                      {idx < destinations.length - 1 && <span style={{ margin: '0 8px', color: '#999' }}>→</span>}
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}
                           {isPackItBeingEdited(packIt.id) && (
                             <button
                               className="add-day-button modern-icon-button"
                               onClick={addItineraryDay}
                               title="Add Day"
                             >
                               <i className="fas fa-plus"></i>
                               Add Day
                             </button>
                           )}
                         </div>
                        <div className={`itinerary-list ${userPlan === 'Basic' ? 'basic-plan-blur' : ''}`}>
                          {(() => {
                            const currentPackIt = isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt;
                            const normalized = normalizeItinerary(currentPackIt?.itinerary);
                            const itineraryArray = normalized.itineraryArray || [];
                            
                            if (!Array.isArray(itineraryArray) || itineraryArray.length === 0) {
                              return (
                                <div className="no-items">
                                  <p>No itinerary data available</p>
                                </div>
                              );
                            }

                            return itineraryArray.map((day, index) => {
                             // Calculate the correct date based on trip start date and day number
                             const tripStartDate = new Date(selectedPackIt.startDate || normalized.startDate || selectedPackIt.itinerary?.itinerary?.startDate);
                             const dayNumber = day.day || (index + 1);
                             const dayDate = new Date(tripStartDate);
                             dayDate.setDate(tripStartDate.getDate() + dayNumber - 1);
                             
                             const isDayCollapsed = isSectionCollapsed(index, 'full');
                             return (
                               <div key={index} className="itinerary-day-card">
                                 <div
                                   className="day-header day-header-clickable"
                                   onClick={() => toggleSection(index, 'full')}
                                   role="button"
                                   tabIndex={0}
                                   onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(index, 'full'); } }}
                                   title={isDayCollapsed ? 'Expand day' : 'Collapse day'}
                                   style={{ cursor: 'pointer' }}
                                 >
                                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                     <button
                                       type="button"
                                       className="collapse-button"
                                       onClick={(e) => { e.stopPropagation(); toggleSection(index, 'full'); }}
                                       title={isDayCollapsed ? 'Expand day' : 'Collapse day'}
                                       aria-label={isDayCollapsed ? 'Expand day' : 'Collapse day'}
                                     >
                                       <i className={`fas fa-chevron-${isDayCollapsed ? 'right' : 'down'}`}></i>
                                     </button>
                                     <div>
                                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <div className="day-number">Day {dayNumber}</div>
                                       <div className="checkpoint-type">
                                         {isPackItBeingEdited(packIt.id) && (!day.locationId || day.locationId.trim() === '') ? (
                                          <input
                                            type="text"
                                            id={`itinerary-day-${index}-location`}
                                            name={`itinerary-day-${index}-location`}
                                            value={day.locationId || ''}
                                            onChange={(e) => updateItineraryDay(index, 'locationId', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder="Enter location"
                                            className="edit-input"
                                            aria-label={`Location for day ${index + 1}`}
                                          />
                                         ) : (
                                           <span>
                                             {day.checkpointType === 'travel day' && day.departureLocationId && day.arrivalLocationId
                                               ? (() => {
                                                   // If departure and arrival are the same, just show the location
                                                   if (day.departureLocationId === day.arrivalLocationId) {
                                                     return day.departureLocationId;
                                                   }
                                                   
                                                   // Helper to format ISO 8601 duration (PT2H30M) to readable format
                                                   const formatDuration = (durationStr) => {
                                                     if (!durationStr || !durationStr.startsWith('PT')) return null;
                                                     const hoursMatch = durationStr.match(/(\d+)H/);
                                                     const minutesMatch = durationStr.match(/(\d+)M/);
                                                     const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
                                                     const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
                                                     if (hours > 0 && minutes > 0) {
                                                       return `~${hours}h ${minutes}m`;
                                                     } else if (hours > 0) {
                                                       return `~${hours}h`;
                                                     } else if (minutes > 0) {
                                                       return `~${minutes}m`;
                                                     }
                                                     return null;
                                                   };
                                                   
                                                   const duration = formatDuration(day.enrouteTimeEstimate);
                                                   const transportType = day.transportType || '';
                                                   const parts = [`${day.departureLocationId} → ${day.arrivalLocationId}`];
                                                   
                                                   if (transportType) {
                                                     parts.push(transportType);
                                                   }
                                                   if (duration) {
                                                     parts.push(`(${duration} est.)`);
                                                   }
                                                   
                                                   return parts.join(' • ');
                                                 })()
                                               : (day.locationId || 'Unknown Location')
                                             }
                                           </span>
                                         )}
                                       </div>
                                     </div>
                                     <div className="day-date">
                                       {dayDate.toLocaleDateString(undefined, {
                                         weekday: 'long',
                                         month: 'short',
                                         day: 'numeric',
                                         year: 'numeric',
                                       })}
                                     </div>
                                   </div>
                                   </div>
                                   {isPackItBeingEdited(packIt.id) && (
                                     <button
                                       className="remove-day-button"
                                       onClick={(e) => { e.stopPropagation(); removeItineraryDay(index); }}
                                       title="Remove Day"
                                     >
                                       <i className="fas fa-trash"></i>
                                     </button>
                                   )}
                                 </div>
                                 {!isDayCollapsed && (
                                 <div className="itinerary-body">
                                   {isPackItBeingEdited(packIt.id) ? (
                                     <div className="edit-goal-section">
                                       <label>Goal:</label>
                                      <textarea
                                        id={`itinerary-day-${index}-goal`}
                                        name={`itinerary-day-${index}-goal`}
                                        value={day.goal || ''}
                                        onChange={(e) => updateItineraryDay(index, 'goal', e.target.value)}
                                        placeholder="Enter day goal..."
                                        className="edit-textarea"
                                        aria-label={`Goal for day ${index + 1}`}
                                      />
                                     </div>
                                   ) : (
                                     day.goal && (
                                       <p className="goal-text">
                                         <strong>Goal:</strong> {day.goal}
                                       </p>
                                     )
                                   )}
                                   
                                   {/* Places to Stay for this day */}
                                   {(() => {
                                     // Filter by date only - no locationId filter so moved items show regardless of location
                                     const dayPlacesToStay = normalizePlacesToStay(
                                       (isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt).placestostay,
                                       null,
                                       day.date
                                     );
                                     return (
                                       <div className="row-container">
                                         <div className="section-header">
                                           <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                             <button
                                               onClick={() => toggleSection(index, 'placesToStay')}
                                               className="collapse-button"
                                               title={isSectionCollapsed(index, 'placesToStay') ? 'Expand' : 'Collapse'}
                                             >
                                               <i className={`fas fa-chevron-${isSectionCollapsed(index, 'placesToStay') ? 'right' : 'down'}`}></i>
                                             </button>
                                             <h4>Places to Stay</h4>
                                           </div>
                                           {isPackItBeingEdited(packIt.id) && (
                                             <button
                                               className="add-item-button"
                                               onClick={() => addPlaceToStay(index, day.locationId)}
                                               title="Add Place to Stay"
                                             >
                                               <i className="fas fa-plus"></i>
                                               Add Stay
                                             </button>
                                           )}
                                         </div>
                                         {!isSectionCollapsed(index, 'placesToStay') && (
                                           dayPlacesToStay.length > 0 ? (
                                             renderItemsInMode(
                                               dayPlacesToStay,
                                               (stay, sIndex) => (
                                                 <div 
                                                   className="item-card-clickable" 
                                                   onClick={() => !isPackItBeingEdited(packIt.id) && openDetailModal(stay, 'placestostay')}
                                                   style={{ cursor: isPackItBeingEdited(packIt.id) ? 'default' : 'pointer' }}
                                                 >
                                                   {!isPackItBeingEdited(packIt.id) && (
                                                     <div className="item-details-badge" title="Click for full details" aria-label="Click for full details">
                                                       <i className="fas fa-info-circle"></i>
                                                     </div>
                                                   )}
                                                   <div className="expandable-title">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <input
                                                         type="text"
                                                         value={stay.name || ''}
                                                         onChange={(e) => updatePlaceToStay(stay.id, 'name', e.target.value)}
                                                         placeholder="Place name"
                                                         className="edit-input"
                                                       />
                                                     ) : (
                                                       stay.name
                                                     )}
                                                     {isPackItBeingEdited(packIt.id) && (
                                                       <button
                                                         className="remove-item-button"
                                                         onClick={(e) => {
                                                           e.stopPropagation();
                                                           removePlaceToStaySimple(sIndex, index, day.locationId, e, stay.name);
                                                         }}
                                                         title="Remove Place to Stay"
                                                       >
                                                         <i className="fas fa-trash"></i>
                                                       </button>
                                                     )}
                                                   </div>
                                                   <div className="expandable-short">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <textarea
                                                         value={stay.description || ''}
                                                         onChange={(e) => updatePlaceToStay(stay.id, 'description', e.target.value)}
                                                         placeholder="Description"
                                                         className="edit-textarea"
                                                         rows="2"
                                                       />
                                                     ) : (
                                                       stay.description || stay.type
                                                     )}
                                                   </div>
                                                   <div className="expandable-details">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <>
                                                         <input
                                                           type="text"
                                                           value={stay.address || ''}
                                                           onChange={(e) => updatePlaceToStay(stay.id, 'address', e.target.value)}
                                                           placeholder="Address"
                                                           className="edit-input"
                                                         />
                                                         <input
                                                           type="text"
                                                           value={stay.type || ''}
                                                           onChange={(e) => updatePlaceToStay(stay.id, 'type', e.target.value)}
                                                           placeholder="Type (hotel, hostel, etc.)"
                                                           className="edit-input"
                                                         />
                                                       </>
                                                     ) : (
                                                       <>
                                                         {(() => {
                                                           const amenityLine = formatAmenitiesForDisplay(stay.amenities);
                                                           return amenityLine ? (
                                                             <p><em>Amenities:</em> {amenityLine}</p>
                                                           ) : null;
                                                         })()}
                                                         {stay.address && (
                                                           <p><em>Address:</em> {stay.address}</p>
                                                         )}
                                                       </>
                                                     )}
                                                   </div>
                                                 </div>
                                               ),
                                               `Day ${index + 1} - Places to Stay`
                                             )
                                           ) : (
                                             <div className="no-items">
                                               <p>No places to stay for this day</p>
                                             </div>
                                           )
                                         )}
                                       </div>
                                     );
                                   })()}

                                   {/* Things to Do for this day */}
                                   {(() => {
                                     // Filter by date only - no locationId filter so moved items show regardless of location
                                     const dayActivities = normalizedActivities.filter(activity => {
                                       const dateMatch = activity.date === day.date || activity.date === day.date?.split('T')[0];
                                       return dateMatch;
                                     });
                                     return (
                                       <div className="row-container">
                                         <div className="section-header">
                                           <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                             <button
                                               onClick={() => toggleSection(index, 'thingsToDo')}
                                               className="collapse-button"
                                               title={isSectionCollapsed(index, 'thingsToDo') ? 'Expand' : 'Collapse'}
                                             >
                                               <i className={`fas fa-chevron-${isSectionCollapsed(index, 'thingsToDo') ? 'right' : 'down'}`}></i>
                                             </button>
                                             <h4>Things to Do</h4>
                                           </div>
                                           {isPackItBeingEdited(packIt.id) && (
                                             <button
                                               className="add-item-button"
                                               onClick={() => addThingToDo(index, day.locationId)}
                                               title="Add Activity"
                                             >
                                               <i className="fas fa-plus"></i>
                                               Add Activity
                                             </button>
                                           )}
                                         </div>
                                         {!isSectionCollapsed(index, 'thingsToDo') && (
                                           dayActivities.length > 0 ? (
                                             renderItemsInMode(
                                               dayActivities,
                                               (activity, aIndex) => (
                                                 <div 
                                                   className="item-card-clickable" 
                                                   onClick={() => !isPackItBeingEdited(packIt.id) && openDetailModal(activity, 'thingstodo')}
                                                   style={{ cursor: isPackItBeingEdited(packIt.id) ? 'default' : 'pointer' }}
                                                 >
                                                   {!isPackItBeingEdited(packIt.id) && (
                                                     <div className="item-details-badge" title="Click for full details" aria-label="Click for full details">
                                                       <i className="fas fa-info-circle"></i>
                                                     </div>
                                                   )}
                                                   <div className="expandable-title">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <input
                                                         type="text"
                                                         value={activity.name || ''}
                                                         onChange={(e) => updateThingToDo(activity.id, 'name', e.target.value)}
                                                         placeholder="Activity name"
                                                         className="edit-input"
                                                       />
                                                     ) : (
                                                       activity.name
                                                     )}
                                                     {isPackItBeingEdited(packIt.id) && (
                                                       <button
                                                         className="remove-item-button"
                                                         onClick={(e) => {
                                                           e.stopPropagation();
                                                           removeThingToDo(aIndex, index, day.locationId, e, activity.name);
                                                         }}
                                                         title="Remove Activity"
                                                       >
                                                         <i className="fas fa-trash"></i>
                                                       </button>
                                                     )}
                                                   </div>
                                                   <div className="expandable-short">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <textarea
                                                         value={activity.description || ''}
                                                         onChange={(e) => updateThingToDo(activity.id, 'description', e.target.value)}
                                                         placeholder="Description"
                                                         className="edit-textarea"
                                                         rows="2"
                                                       />
                                                     ) : (
                                                       activity.description || activity.type
                                                     )}
                                                   </div>
                                                   <div className="expandable-details">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <>
                                                         <input
                                                           type="text"
                                                           value={activity.address || ''}
                                                           onChange={(e) => updateThingToDo(activity.id, 'address', e.target.value)}
                                                           placeholder="Address"
                                                           className="edit-input"
                                                         />
                                                         <input
                                                           type="text"
                                                           value={activity.website || ''}
                                                           onChange={(e) => updateThingToDo(activity.id, 'website', e.target.value)}
                                                           placeholder="Website"
                                                           className="edit-input"
                                                         />
                                                       </>
                                                     ) : (
                                                       <>
                                                         {activity.address && (
                                                           <p><em>Address:</em> {activity.address}</p>
                                                         )}
                                                         {activity.website && (
                                                           <p><em>Website:</em> <a href={activity.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{activity.website}</a></p>
                                                         )}
                                                       </>
                                                     )}
                                                   </div>
                                                 </div>
                                               ),
                                               `Day ${index + 1} - Things to Do`
                                             )
                                           ) : (
                                             <div className="no-items">
                                               <p>No activities for this day</p>
                                             </div>
                                           )
                                         )}
                                       </div>
                                     );
                                   })()}

                                   {/* Places to Eat for this day */}
                                   {(() => {
                                     const currentPackIt = isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt;
                                     // Filter by date only - no locationId filter so moved items show regardless of location
                                     const dayPlacesToEat = normalizePlacesToEat(
                                       currentPackIt?.placestoeat,
                                       null,
                                       day.date
                                     );

                                     return (
                                       <div className="row-container">
                                         <div className="section-header">
                                           <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                             <button
                                               onClick={() => toggleSection(index, 'placesToEat')}
                                               className="collapse-button"
                                               title={isSectionCollapsed(index, 'placesToEat') ? 'Expand' : 'Collapse'}
                                             >
                                               <i className={`fas fa-chevron-${isSectionCollapsed(index, 'placesToEat') ? 'right' : 'down'}`}></i>
                                             </button>
                                             <h4>Places to Eat</h4>
                                           </div>
                                           {isPackItBeingEdited(packIt.id) && (
                                             <button
                                               className="add-item-button"
                                               onClick={() => addPlaceToEat(index, day.locationId)}
                                               title="Add Restaurant"
                                             >
                                               <i className="fas fa-plus"></i>
                                               Add Restaurant
                                             </button>
                                           )}
                                         </div>
                                         {!isSectionCollapsed(index, 'placesToEat') && (
                                           dayPlacesToEat.length > 0 ? (
                                             renderItemsInMode(
                                               dayPlacesToEat,
                                               (restaurant, rIndex) => (
                                                 <div 
                                                   className="item-card-clickable" 
                                                   onClick={() => !isPackItBeingEdited(packIt.id) && openDetailModal(restaurant, 'placestoeat')}
                                                   style={{ cursor: isPackItBeingEdited(packIt.id) ? 'default' : 'pointer' }}
                                                 >
                                                   {!isPackItBeingEdited(packIt.id) && (
                                                     <div className="item-details-badge" title="Click for full details" aria-label="Click for full details">
                                                       <i className="fas fa-info-circle"></i>
                                                     </div>
                                                   )}
                                                   <div className="expandable-title">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <input
                                                         type="text"
                                                         value={restaurant.name || ''}
                                                         onChange={(e) => updatePlaceToEat(restaurant.id, 'name', e.target.value)}
                                                         placeholder="Restaurant name"
                                                         className="edit-input"
                                                       />
                                                     ) : (
                                                       restaurant.name
                                                     )}
                                                     {isPackItBeingEdited(packIt.id) && (
                                                       <button
                                                         className="remove-item-button"
                                                         onClick={(e) => {
                                                           e.stopPropagation();
                                                           removePlaceToEat(rIndex, index, day.locationId, e, restaurant.name);
                                                         }}
                                                         title="Remove Restaurant"
                                                       >
                                                         <i className="fas fa-trash"></i>
                                                       </button>
                                                     )}
                                                   </div>
                                                   <div className="expandable-short">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <textarea
                                                         value={restaurant.description || ''}
                                                         onChange={(e) => updatePlaceToEat(restaurant.id, 'description', e.target.value)}
                                                         placeholder="Description"
                                                         className="edit-textarea"
                                                         rows="2"
                                                       />
                                                     ) : (
                                                       restaurant.description || restaurant.type
                                                     )}
                                                   </div>
                                                   <div className="expandable-details">
                                                     {isPackItBeingEdited(packIt.id) ? (
                                                       <>
                                                         <input
                                                           type="text"
                                                           value={restaurant.address || ''}
                                                           onChange={(e) => updatePlaceToEat(restaurant.id, 'address', e.target.value)}
                                                           placeholder="Address"
                                                           className="edit-input"
                                                         />
                                                         <input
                                                           type="text"
                                                           value={restaurant.website || ''}
                                                           onChange={(e) => updatePlaceToEat(restaurant.id, 'website', e.target.value)}
                                                           placeholder="Website"
                                                           className="edit-input"
                                                         />
                                                       </>
                                                     ) : (
                                                       <>
                                                         {restaurant.address && (
                                                           <p><em>Address:</em> {restaurant.address}</p>
                                                         )}
                                                         {restaurant.website && (
                                                           <p><em>Website:</em> <a href={restaurant.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{restaurant.website}</a></p>
                                                         )}
                                                       </>
                                                     )}
                                                   </div>
                                                 </div>
                                               ),
                                               `Day ${index + 1} - Places to Eat`
                                             )
                                           ) : (
                                             <div className="no-items">
                                               <p>No restaurants for this day</p>
                                             </div>
                                           )
                                         )}
                                       </div>
                                     );
                                   })()}
                                 </div>
                                 )}
                               </div>
                            );
                            });
                          })()}
                          {(() => {
                            const currentPackIt = isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt;
                            const normalized = normalizeItinerary(currentPackIt?.itinerary);
                            return userPlan === 'Basic' && normalized.itineraryArray.length > 3 && (
                              <div className="basic-plan-upgrade-message">
                                <p>Upgrade to see all days of your trip</p>
                                <button onClick={() => window.location.href = '/plan-subscription'}>
                                  Upgrade Now
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}





                     {activeTab === 'map' && (
                       <div className="tab-content map-content">
                         <div className="map-header-section">
                           <div style={{ marginBottom: '8px' }}>
                             <h3 className="map-header">Trip Map</h3>
                           </div>
                           {/* Destination Breadcrumb */}
                           {(() => {
                             const destinations = getOrderedDestinations();
                             if (destinations.length > 0) {
                               return (
                                 <div className="destination-breadcrumb" style={{ marginTop: '0px', fontSize: '14px', color: '#666' }}>
                                   <span style={{ fontWeight: '500' }}>Destinations: </span>
                                   {destinations.map((dest, idx) => (
                                     <span key={idx}>
                                       <span style={{ fontWeight: '500' }}>{dest}</span>
                                       {idx < destinations.length - 1 && <span style={{ margin: '0 8px', color: '#999' }}>→</span>}
                                     </span>
                                   ))}
                                 </div>
                               );
                             }
                             return null;
                           })()}
                         </div>
                         <MapErrorBoundary>
                           <MyMapComponent
                             packitId={selectedPackIt.packitId}
                             places={[
                               // THINGS TO DO
                               ...normalizedActivities.map(item => ({
                                 name: item.name,
                                 address: item.address,
                                 lat: item.latitude  ?? item.coordinates?.latitude,
                                 lng: item.longitude ?? item.coordinates?.longitude,
                                 coordinateProbability: item.coordinateProbability,
                                 category: "thingsToDo",
                                 description: item.description,
                                 website: item.website,
                                 date: item.date,
                                 sectionIndex: item.sectionIndex,
                                 entryIndex: item.entryIndex
                               })),

                              // PLACES TO EAT
                              ...(() => {
                                const allRestaurants = normalizePlacesToEat(selectedPackIt?.placestoeat);
                                return allRestaurants.map((r, index) => ({
                                  name: r.name,
                                  address: r.address,
                                  lat: r.latitude ?? r.coordinates?.latitude,
                                  lng: r.longitude ?? r.coordinates?.longitude,
                                  coordinateProbability: r.coordinateProbability,
                                  category: "placesToEat",
                                  description: r.description,
                                  website: r.website,
                                  date: r.date,
                                  sectionIndex: r.sectionIndex || 0,
                                  entryIndex: r.entryIndex || index
                                }));
                              })(),

                               // PLACES TO STAY
                               ...(Array.isArray(selectedPackIt.placestostay) ? selectedPackIt.placestostay.flatMap((ps, sectionIndex) => {
                                 const dateKey = ps.tripDates?.checkIn;
                                 return (ps.placesToStay || []).map((stay, entryIndex) => ({
                                   name: stay.name,
                                   address: stay.address,
                                   lat: stay.latitude  ?? stay.coordinates?.latitude,
                                   lng: stay.longitude ?? stay.coordinates?.longitude,
                                   coordinateProbability: stay.coordinateProbability,
                                   category: "placesToStay",
                                   description: stay.description,
                                   website: stay.website,
                                   date: dateKey,
                                   sectionIndex,
                                   entryIndex
                                 }));
                               }) : []),
                             ]}
                            itinerary={(() => {
                              const normalized = normalizeItinerary(selectedPackIt?.itinerary);
                              return normalized.itineraryArray || [];
                            })()}
                            destinations={(() => {
                              const normalized = normalizeItinerary(selectedPackIt?.itinerary);
                              return normalized.destinations || [];
                            })()}
                           />
                         </MapErrorBoundary>
                       </div>
                     )}



{/* DESTINATIONS TAB */}
{activeTab === 'destination' && (
  <div className={`tab-content destination-content ${userPlan === 'Basic' ? 'basic-plan-blur' : ''}`}>
    <div className="destination-header-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h3 className="destination-header">Your Destinations</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="collapse-expand-button" onClick={collapseAllSections} title="Collapse All Sections">
            <i className="fas fa-compress-alt"></i>
            Collapse All
          </button>
          <button className="collapse-expand-button" onClick={expandAllSections} title="Expand All Sections">
            <i className="fas fa-expand-alt"></i>
            Expand All
          </button>
          {renderDisplayModeSelector()}
        </div>
      </div>
      {/* Destination Breadcrumb */}
      {(() => {
        const destinations = getOrderedDestinations();
        if (destinations.length > 0) {
          return (
            <div className="destination-breadcrumb" style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              <span style={{ fontWeight: '500' }}>Destinations: </span>
              {destinations.map((dest, idx) => (
                <span key={idx}>
                  <span style={{ fontWeight: '500' }}>{dest}</span>
                  {idx < destinations.length - 1 && <span style={{ margin: '0 8px', color: '#999' }}>→</span>}
                </span>
              ))}
            </div>
          );
        }
        return null;
      })()}
      {isPackItBeingEdited(packIt.id) && (
        <button
          className="add-destination-button modern-icon-button"
          onClick={() => addDestination()}
          title="Add Destination"
        >
          <i className="fas fa-plus"></i>
          Add Destination
        </button>
      )}
    </div>

    {(Array.isArray((isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt).itinerary?.itinerary?.destinations) 
      ? (isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt).itinerary.itinerary.destinations 
      : []
    ).map((destObj, i) => {
      const destLocId = destObj.locationId || 'Unknown';
      const destAddress = destObj.address || 'No Address';

      // 1) Identify all days referencing this destination
      const normalizedItinerary = normalizeItinerary(selectedPackIt?.itinerary);
      const itineraryDays = normalizedItinerary.itineraryArray || [];
      const daysForThisDest = itineraryDays.filter((day) => {
        // match either locationId, departureLocationId, or arrivalLocationId
        return (
          (day.locationId || '').toLowerCase() === destLocId.toLowerCase() ||
          (day.departureLocationId || '').toLowerCase() === destLocId.toLowerCase() ||
          (day.arrivalLocationId || '').toLowerCase() === destLocId.toLowerCase()
        );
      });

      // 2) Gather all date-keys for those days
      const allDatesForDest = daysForThisDest.map((d) => d.date);

      // 3) Build array of "Things to Do"
      const relevantActivities = normalizedActivities.filter(activity => {
        // If destination info is provided, it must match;
        // otherwise, allow the activity to appear as long as its date is in the destination's itinerary days.
        const matchesDestination = activity.destination
          ? (activity.destination || '').toLowerCase() === destLocId.toLowerCase()
          : true;
        return matchesDestination && allDatesForDest.includes(activity.date);
      });





      // 4) Places to Stay
      let placesToStay = normalizePlacesToStay(
        selectedPackIt.placestostay,
        destLocId,
        allDatesForDest[0]  // Use the first date for this destination
      );
      

      

      // 5) Places to Eat
      // Get all restaurants for this destination, considering all dates
      let placesToEat = [];
      if (allDatesForDest.length > 0) {
        // If we have dates, get restaurants for each date
        allDatesForDest.forEach(date => {
          const dateStr = date?.split('T')[0];
          const restaurantsForDate = normalizePlacesToEat(selectedPackIt?.placestoeat, destLocId, dateStr);
          placesToEat.push(...restaurantsForDate);
        });
        // Remove duplicates based on restaurant id
        const seenIds = new Set();
        placesToEat = placesToEat.filter(rest => {
          if (seenIds.has(rest.id)) return false;
          seenIds.add(rest.id);
          return true;
        });
      } else {
        // If no dates, just get all restaurants for this destination
        placesToEat = normalizePlacesToEat(selectedPackIt?.placestoeat, destLocId);
      }

      // 6) Health/Safety Alerts
      let alertsData =
      selectedPackIt.healthsafety?.healthSafetyAlerts ||
      selectedPackIt.healthSafetyAlerts ||
      [];
    let relevantAlerts = [];
    //console.log("found these alerts: ", alertsData);
    //console.log("found these alerts: ", alertsData.length);
    
    if (Array.isArray(alertsData) && alertsData.length > 0) {
      relevantAlerts = alertsData.filter((alert) => {
        // Derive an alert location ID:
        const alertLocId =
          alert.locationId ||
          selectedPackIt.healthsafety?.locationId ||
          (selectedPackIt.healthsafety?.location && selectedPackIt.healthsafety.location.locationId);
        //console.log("Using alert location: ", alertLocId, " vs destLocId: ", destLocId);
        return fuzzyMatch(alertLocId, destLocId);
      });
      // If alerts are stored as blocks with nested "alerts", flatten them.
      if (relevantAlerts.length > 0 && Array.isArray(relevantAlerts[0].alerts)) {
        relevantAlerts = relevantAlerts.flatMap((block) => safeToArray(block.alerts));
      }
    }

      const isDestCollapsed = isSectionCollapsed(i, 'full', 'dest');
      return (
        <div key={i} className="itinerary-day-card">
          <div
            className="day-header day-header-clickable"
            onClick={() => toggleSection(i, 'full', 'dest')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(i, 'full', 'dest'); } }}
            title={isDestCollapsed ? 'Expand destination' : 'Collapse destination'}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <button
                type="button"
                className="collapse-button"
                onClick={(e) => { e.stopPropagation(); toggleSection(i, 'full', 'dest'); }}
                title={isDestCollapsed ? 'Expand destination' : 'Collapse destination'}
                aria-label={isDestCollapsed ? 'Expand destination' : 'Collapse destination'}
              >
                <i className={`fas fa-chevron-${isDestCollapsed ? 'right' : 'down'}`}></i>
              </button>
              <div>
                {isPackItBeingEdited(packIt.id) ? (
                  <div className="edit-destination-fields" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={destObj.locationId || ''}
                      onChange={(e) => updateDestination(i, 'locationId', e.target.value)}
                      placeholder="Location ID"
                      className="edit-input"
                    />
                    <input
                      type="text"
                      value={destObj.address || ''}
                      onChange={(e) => updateDestination(i, 'address', e.target.value)}
                      placeholder="Address"
                      className="edit-input"
                    />
                    <input
                      type="text"
                      value={destObj.name || ''}
                      onChange={(e) => updateDestination(i, 'name', e.target.value)}
                      placeholder="Name"
                      className="edit-input"
                    />
                  </div>
                ) : (
                  <>
                    <div className="day-number">{destLocId}</div>
                    <div className="day-date">{destAddress}</div>
                  </>
                )}
              </div>
            </div>
            <div className="checkpoint-type">
              <span>Destination</span>
            </div>
          </div>

          {!isDestCollapsed && (
          <div className="itinerary-body">
            {/* START/END DESTINATION INDICATOR */}
            {(destObj.isStart || destObj.isEnd) && placesToStay.length === 0 && (
              <div className="start-end-indicator">
                <div className="indicator-content">
                  <i className={`fas ${destObj.isStart ? 'fa-map-marker-alt' : 'fa-flag-checkered'}`}></i>
                  <div className="indicator-text">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => toggleSection(i, 'departureInfo', 'dest')}
                        className="collapse-button"
                        title={isSectionCollapsed(i, 'departureInfo', 'dest') ? 'Expand' : 'Collapse'}
                      >
                        <i className={`fas fa-chevron-${isSectionCollapsed(i, 'departureInfo', 'dest') ? 'right' : 'down'}`}></i>
                      </button>
                      <h4>{destObj.isStart ? 'Starting Point' : 'Ending Point'}</h4>
                    </div>
                    {!isSectionCollapsed(i, 'departureInfo', 'dest') && (
                      <>
                        <p>
                          {destObj.isStart
                            ? 'This is your trip starting point. Plan for transportation, accommodations, and initial logistics.'
                            : 'This is your trip ending point. Plan for departure logistics, accommodations, and final transportation arrangements.'
                          }
                        </p>
                        <div className="indicator-tips">
                          <strong>Key considerations:</strong>
                          <ul>
                            {destObj.isStart ? (
                              <>
                                <li>Transportation arrangements</li>
                                <li>Check-in times and procedures</li>
                                <li>Initial supplies and essentials</li>
                                <li>Local emergency contacts</li>
                              </>
                            ) : (
                              <>
                                <li>Check-out times and procedures</li>
                                <li>Transportation arrangements</li>
                                <li>Final packing and cleanup</li>
                                <li>Return or onward travel logistics</li>
                              </>
                            )}
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* PLACES TO STAY */}
            {(!destObj.isStart && !destObj.isEnd) || placesToStay.length > 0 ? (
              <div className="row-container">
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => toggleSection(i, 'placesToStay', 'dest')}
                      className="collapse-button"
                      title={isSectionCollapsed(i, 'placesToStay', 'dest') ? 'Expand' : 'Collapse'}
                    >
                      <i className={`fas fa-chevron-${isSectionCollapsed(i, 'placesToStay', 'dest') ? 'right' : 'down'}`}></i>
                    </button>
                    <h4>Places to Stay</h4>
                  </div>
                  {(destObj.isStart || destObj.isEnd) && (
                    <span className="section-note">
                      <i className="fas fa-info-circle"></i>
                      {destObj.isStart ? ' Starting point accommodations' : ' Ending point accommodations'}
                    </span>
                  )}
                </div>
                {!isSectionCollapsed(i, 'placesToStay', 'dest') && (
                  placesToStay.length > 0 ? (
                    renderItemsInMode(
                      placesToStay,
                      (stay, sIndex) => (
                        <div 
                          className="item-card-clickable" 
                          onClick={() => !isPackItBeingEdited(packIt.id) && openDetailModal(stay, 'placestostay')}
                          style={{ cursor: isPackItBeingEdited(packIt.id) ? 'default' : 'pointer' }}
                        >
                          {!isPackItBeingEdited(packIt.id) && (
                            <div className="item-details-badge" title="Click for full details" aria-label="Click for full details">
                              <i className="fas fa-info-circle"></i>
                            </div>
                          )}
                          <div className="expandable-title">
                            {stay.name}
                          </div>
                          <div className="expandable-short">
                            {stay.description || stay.type}
                          </div>
                          <div className="expandable-details">
                            {(() => {
                              const amenityLine = formatAmenitiesForDisplay(stay.amenities);
                              return amenityLine ? (
                                <p><em>Amenities:</em> {amenityLine}</p>
                              ) : null;
                            })()}
                            {stay.address && (
                              <p><em>Address:</em> {stay.address}</p>
                            )}
                          </div>
                        </div>
                      ),
                      `${destLocId} - Places to Stay`
                    )
                  ) : (
                    <div className="no-items">
                      <p>No places to stay for this destination</p>
                    </div>
                  )
                )}
              </div>
            ) : null}

            {/* THINGS TO DO */}
            {relevantActivities.length > 0 && (
              <div className="row-container">
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => toggleSection(i, 'thingsToDo', 'dest')}
                      className="collapse-button"
                      title={isSectionCollapsed(i, 'thingsToDo', 'dest') ? 'Expand' : 'Collapse'}
                    >
                      <i className={`fas fa-chevron-${isSectionCollapsed(i, 'thingsToDo', 'dest') ? 'right' : 'down'}`}></i>
                    </button>
                    <h4>Things to Do</h4>
                  </div>
                </div>
                {!isSectionCollapsed(i, 'thingsToDo', 'dest') && (
                  relevantActivities.length > 0 ? (
                    renderItemsInMode(
                      relevantActivities,
                      (act, aIndex) => (
                      <div 
                        className="item-card-clickable" 
                        onClick={() => !isPackItBeingEdited(packIt.id) && openDetailModal(act, 'thingstodo')}
                        style={{ cursor: isPackItBeingEdited(packIt.id) ? 'default' : 'pointer' }}
                      >
                        {!isPackItBeingEdited(packIt.id) && (
                          <div className="item-details-badge" title="Click for full details" aria-label="Click for full details">
                            <i className="fas fa-info-circle"></i>
                          </div>
                        )}
                        <div className="expandable-title">
                          {act.name}
                        </div>
                        <div className="expandable-short">
                          {act.description || act.type}
                        </div>
                        <div className="expandable-details">
                          {act.address && (
                            <p><em>Address:</em> {act.address}</p>
                          )}
                          {act.website && (
                            <p><em>Website:</em> <a href={act.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{act.website}</a></p>
                          )}
                        </div>
                      </div>
                    ),
                    `${destLocId} - Things to Do`
                  )
                  ) : (
                    <div className="no-items">
                      <p>No activities for this destination</p>
                    </div>
                  )
                )}
              </div>
            )}

            {/* PLACES TO EAT */}
            {(!destObj.isStart && !destObj.isEnd) || placesToEat.length > 0 ? (
              <div className="row-container">
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => toggleSection(i, 'placesToEat', 'dest')}
                      className="collapse-button"
                      title={isSectionCollapsed(i, 'placesToEat', 'dest') ? 'Expand' : 'Collapse'}
                    >
                      <i className={`fas fa-chevron-${isSectionCollapsed(i, 'placesToEat', 'dest') ? 'right' : 'down'}`}></i>
                    </button>
                    <h4>Places to Eat</h4>
                  </div>
                  {(destObj.isStart || destObj.isEnd) && (
                    <span className="section-note">
                      <i className="fas fa-info-circle"></i>
                      {destObj.isStart ? ' Starting point dining' : ' Ending point dining'}
                    </span>
                  )}
                </div>
              {!isSectionCollapsed(i, 'placesToEat', 'dest') && (
                placesToEat.length > 0 ? (
                  renderItemsInMode(
                    placesToEat,
                    (rest, index) => (
                      <div 
                        className="item-card-clickable" 
                        onClick={() => !isPackItBeingEdited(packIt.id) && openDetailModal(rest, 'placestoeat')}
                        style={{ cursor: isPackItBeingEdited(packIt.id) ? 'default' : 'pointer' }}
                      >
                        {!isPackItBeingEdited(packIt.id) && (
                          <div className="item-details-badge" title="Click for full details" aria-label="Click for full details">
                            <i className="fas fa-info-circle"></i>
                          </div>
                        )}
                        <div className="expandable-title">
                          {isPackItBeingEdited(packIt.id) ? (
                            <input
                              type="text"
                              value={rest.name || ''}
                              onChange={(e) => updatePlaceToEat(rest.id, 'name', e.target.value)}
                              placeholder="Restaurant name"
                              className="edit-input"
                            />
                          ) : (
                            rest.name
                          )}
                                             {isPackItBeingEdited(packIt.id) && (
                            <button
                              className="remove-item-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePlaceToEat(index, i, destLocId, e, rest.name);
                              }}
                              title="Remove Restaurant"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                        <div className="expandable-short">
                          {isPackItBeingEdited(packIt.id) ? (
                            <textarea
                              value={rest.description || ''}
                              onChange={(e) => updatePlaceToEat(rest.id, 'description', e.target.value)}
                              placeholder="Description"
                              className="edit-textarea"
                              rows="2"
                            />
                          ) : (
                            rest.description || rest.type
                          )}
                        </div>
                        <div className="expandable-details">
                          {isPackItBeingEdited(packIt.id) ? (
                            <>
                              <input
                                type="text"
                                value={rest.address || ''}
                                onChange={(e) => updatePlaceToEat(rest.id, 'address', e.target.value)}
                                placeholder="Address"
                                className="edit-input"
                              />
                              <input
                                type="text"
                                value={rest.website || ''}
                                onChange={(e) => updatePlaceToEat(rest.id, 'website', e.target.value)}
                                placeholder="Website"
                                className="edit-input"
                              />
                            </>
                          ) : (
                            <>
                              {rest.address && (
                                <p><em>Address:</em> {rest.address}</p>
                              )}
                              {rest.website && (
                                <p><em>Website:</em> <a href={rest.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{rest.website}</a></p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ),
                    `${destLocId} - Places to Eat`
                  )
                ) : (
                  <div className="no-items">
                    <p>No restaurants for this destination</p>
                  </div>
                )
              )}
              </div>
            ) : null}

            {/* HEALTH / SAFETY ALERTS */}
            {relevantAlerts.length > 0 && (
  <div className="row-container">
    <div className="section-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          onClick={() => toggleSection(i, 'alerts', 'dest')}
          className="collapse-button"
          title={isSectionCollapsed(i, 'alerts', 'dest') ? 'Expand' : 'Collapse'}
        >
          <i className={`fas fa-chevron-${isSectionCollapsed(i, 'alerts', 'dest') ? 'right' : 'down'}`}></i>
        </button>
        <h4>Alerts</h4>
      </div>
    </div>
    {!isSectionCollapsed(i, 'alerts', 'dest') && (
      <div className="destination-scroll-row">
        {relevantAlerts.map((alert, alIndex) => (
          <div key={alIndex} className="destination-scroll-card">
            <ExpandableCard
              title={alert.alertType}
              shortDescription={alert.description}
              expandedContent={
                <>
                  <p><strong>Severity:</strong> {alert.severity}</p>
                  {(alert.recommendations || alert.recommendedActions) && (
                    <p>
                      <strong>Recommendations:</strong>{" "}
                      {safeToArray(alert.recommendations || alert.recommendedActions).join(", ")}
                    </p>
                  )}
                </>
              }
            />
          </div>
        ))}
      </div>
    )}
  </div>
)}

          </div>
          )}
        </div>
      );
    })}
    {userPlan === 'Basic' && (
      <div className="basic-plan-upgrade-message">
        <p>Upgrade to see all destinations and details</p>
        <button onClick={() => window.location.href = '/plan-subscription'}>
          Upgrade Now
        </button>
      </div>
    )}
  </div>
)}


{activeTab === 'weather' && (
  <div className={`tab-content weather-content ${userPlan === 'Basic' ? 'basic-plan-blur' : ''}`}>
    <div className="weather-header-section">
              <div className="weather-header-row">
          <h3 className="weather-header">Weather Forecast</h3>
          <div className="weather-controls">
            {renderWeatherDisplayModeSelector()}
            {renderTemperatureUnitSelector()}
            {renderCollapseAllButton()}
            {(selectedPackIt.weatherFull && selectedPackIt.weatherFull.length > 0) && (
              <div className="weather-about-forecast-wrapper" style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  className="weather-data-source-toggle"
                  onClick={() => setWeatherDataSourceExpanded(!weatherDataSourceExpanded)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--primary-600)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <i className={`fas fa-chevron-${weatherDataSourceExpanded ? 'up' : 'down'}`} style={{ fontSize: '10px' }}></i>
                  About this forecast
                </button>
                {weatherDataSourceExpanded && (
                  <div className="weather-about-forecast-popover">
                    <p style={{ margin: 0 }}>
                      Weather forecasts from 1–14 days use real-time stations and forecast models; trips further out use future weather data (up to ~1 year). Data combines global weather stations and high-resolution (1–11 km) models, with AI selecting the best source per location.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Destination Breadcrumb */}
        {(() => {
          const destinations = getOrderedDestinations();
          if (destinations.length > 0) {
            return (
              <div className="destination-breadcrumb" style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                <span style={{ fontWeight: '500' }}>Destinations: </span>
                {destinations.map((dest, idx) => (
                  <span key={idx}>
                    <span style={{ fontWeight: '500' }}>{dest}</span>
                    {idx < destinations.length - 1 && <span style={{ margin: '0 8px', color: '#999' }}>→</span>}
                  </span>
                ))}
              </div>
            );
          }
          return null;
        })()}
      {weatherDisplayMode === 'day' && (
        <div className="weather-summary-overview">
          <div className="weather-summary-stats">
                          <div className="summary-stat">
                <i className="fas fa-thermometer-half"></i>
                <span>Avg: {temperatureUnit === 'F' ? (selectedPackIt.weatherFull?.[0]?.day?.avgtemp_f || 'N/A') : (selectedPackIt.weatherFull?.[0]?.day?.avgtemp_c || 'N/A')}°{temperatureUnit}</span>
              </div>
            <div className="summary-stat">
              <i className="fas fa-cloud-rain"></i>
              <span>Rain: {selectedPackIt.weatherFull?.[0]?.day?.totalprecip_in || '0'} in</span>
            </div>
            <div className="summary-stat">
              <i className="fas fa-wind"></i>
              <span>Wind: {selectedPackIt.weatherFull?.[0]?.day?.maxwind_mph || 'N/A'} mph</span>
            </div>
          </div>
        </div>
      )}
    </div>

    {weatherDisplayMode === 'day' ? (
      // Horizontal layout by day
      <div className="weather-days-horizontal">
        {(selectedPackIt.weatherFull || []).map((wDay, index) => {
          const dayDate = new Date(wDay.date);
          dayDate.setDate(dayDate.getDate() + 1);
          
          return (
            <div key={index} className="weather-day-horizontal-card">
              {/* Day Header - Compact */}
              <div className="weather-day-header">
                <div className="day-info">
                  <h4 className="day-name">
                    {dayDate.toLocaleDateString(undefined, { weekday: 'short' })}
                  </h4>
                  <span className="day-date">
                    {dayDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className="location-badge">
                  {wDay.location}
                </div>
              </div>

              {/* Main Weather Display - At a Glance */}
              <div className="weather-main-display">
                <div className="weather-icon-temp">
                  <img
                    src={wDay.day.condition.icon}
                    alt={wDay.day.condition.text}
                    className="weather-main-icon"
                  />
                  <div className="temp-display">
                    <div className="high-temp">{temperatureUnit === 'F' ? wDay.day.maxtemp_f : wDay.day.maxtemp_c}°{temperatureUnit}</div>
                    <div className="low-temp">{temperatureUnit === 'F' ? wDay.day.mintemp_f : wDay.day.mintemp_c}°{temperatureUnit}</div>
                  </div>
                </div>
                
                <div className="weather-brief">
                  <div className="condition-brief">{wDay.day.condition.text}</div>
                  <div className="key-metrics">
                    <span className="metric">
                      <i className="fas fa-tint"></i> {wDay.day.avghumidity}%
                    </span>
                    <span className="metric">
                      <i className="fas fa-wind"></i> {temperatureUnit === 'F' ? wDay.day.maxwind_mph : wDay.day.maxwind_kph} {temperatureUnit === 'F' ? 'mph' : 'kph'}
                    </span>
                    <span className="metric">
                      <i className="fas fa-sun"></i> UV {wDay.day.uv}
                    </span>
                  </div>
                </div>
              </div>

              {/* View Details Button */}
              <div className="weather-details-section">
                <button 
                  className="weather-details-toggle"
                  onClick={() => {
                    setExpandedWeatherDetails(prev => ({
                      ...prev,
                      [index]: !prev[index]
                    }));
                  }}
                >
                  <i className={`fas fa-chevron-${expandedWeatherDetails[index] ? 'up' : 'down'}`}></i>
                  <span>View Details</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      // Horizontal layout by location
      <div className="weather-locations-horizontal">
        {(() => {
          // Group weather data by location
          const locationGroups = {};
          (selectedPackIt.weatherFull || []).forEach((wDay, index) => {
            const location = wDay.location || 'Unknown Location';
            if (!locationGroups[location]) {
              locationGroups[location] = [];
            }
            locationGroups[location].push({ ...wDay, originalIndex: index });
          });

          return Object.entries(locationGroups).map(([location, days]) => (
            <div key={location} className="weather-location-horizontal-card">
              {/* Weather forecast display first */}
              <div className="location-weather-forecast">
                <div className="location-days-horizontal">
                  {days.map((wDay, dayIndex) => {
                    const dayDate = new Date(wDay.date);
                    dayDate.setDate(dayDate.getDate() + 1);
                    
                    return (
                      <div key={dayIndex} className="location-day-card">
                        <div className="day-header">
                          <div className="day-name">{dayDate.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                          <div className="day-date">{dayDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                        </div>
                        
                        <div className="day-weather">
                          <img
                            src={wDay.day.condition.icon}
                            alt={wDay.day.condition.text}
                            className="weather-icon"
                          />
                          <div className="temp-range">
                            <div className="high">{wDay.day.maxtemp_f}°</div>
                            <div className="low">{wDay.day.mintemp_f}°</div>
                          </div>
                          <div className="condition">{wDay.day.condition.text}</div>
                        </div>

                        <div className="day-metrics">
                          <div className="metric">
                            <i className="fas fa-tint"></i>
                            <span>{wDay.day.avghumidity}%</span>
                          </div>
                          <div className="metric">
                            <i className="fas fa-wind"></i>
                            <span>{wDay.day.maxwind_mph} mph</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Location info below the weather forecast */}
              <div className="location-info-section">
                <div className="location-header">
                  <h4 className="location-name">{location}</h4>
                  <div className="location-summary">
                    <span className="days-count">{days.length} day{days.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="location-description">
                  <p>Weather forecast for {location} covering {days.length} day{days.length !== 1 ? 's' : ''} of your trip.</p>
                </div>
              </div>
            </div>
          ));
        })()}
      </div>
    )}
    
    {/* Full-width expanded weather details - moved outside the weather display mode sections */}
    {Object.entries(expandedWeatherDetails).map(([index, isExpanded]) => {
      if (!isExpanded) return null;
      
      const wDay = selectedPackIt.weatherFull[parseInt(index)];
      const dayDate = new Date(wDay.date);
      dayDate.setDate(dayDate.getDate() + 1);
      
      return (
        <div key={`expanded-${index}`} className="weather-full-width-details">
          <div className="full-width-header">
            <div className="expanded-day-info">
              <h3 className="expanded-day-title">
                {dayDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              <span className="expanded-location">{wDay.location}</span>
            </div>
            <button 
              className="close-details-button"
              onClick={() => {
                setExpandedWeatherDetails(prev => ({
                  ...prev,
                  [index]: false
                }));
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="full-width-content">
            <div className="expanded-details-grid">
              <div className="expanded-detail-section">
                <h5>Daily Summary</h5>
                <div className="expanded-detail-item">
                  <span className="label">Average:</span>
                  <span className="value">{temperatureUnit === 'F' ? wDay.day.avgtemp_f : wDay.day.avgtemp_c}°{temperatureUnit}</span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Precipitation:</span>
                  <span className="value">{temperatureUnit === 'F' ? wDay.day.totalprecip_in : wDay.day.totalprecip_mm} {temperatureUnit === 'F' ? 'in' : 'mm'}</span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Visibility:</span>
                  <span className="value">{temperatureUnit === 'F' ? wDay.day.avgvis_miles : wDay.day.avgvis_km} {temperatureUnit === 'F' ? 'mi' : 'km'}</span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Rain Start:</span>
                  <span className="value">
                    {(() => {
                      const rainyHour = (wDay.hour || []).find(h => h.chance_of_rain > 50);
                      return rainyHour ? new Date(rainyHour.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'No rain expected';
                    })()}
                  </span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Wind Speed:</span>
                  <span className="value">{temperatureUnit === 'F' ? wDay.day.maxwind_mph : wDay.day.maxwind_kph} {temperatureUnit === 'F' ? 'mph' : 'kph'}</span>
                </div>
              </div>
              
              <div className="expanded-detail-section">
                <h5>Sun & Moon</h5>
                <div className="expanded-detail-item">
                  <span className="label">Sunrise:</span>
                  <span className="value">{wDay.astro.sunrise}</span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Sunset:</span>
                  <span className="value">{wDay.astro.sunset}</span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Moon Phase:</span>
                  <span className="value">{wDay.astro.moon_phase}</span>
                </div>
              </div>
              
              <div className="expanded-detail-section">
                <h5>Time of Day</h5>
                <div className="expanded-detail-item">
                  <span className="label">Morning:</span>
                  <span className="value">
                    {(() => {
                      const morningHours = (wDay.hour || []).filter(h => {
                        const hour = new Date(h.time).getHours();
                        return hour >= 6 && hour < 12;
                      });
                      if (morningHours.length === 0) return 'N/A';
                      const avgTemp = morningHours.reduce((sum, h) => sum + (temperatureUnit === 'F' ? h.temp_f : h.temp_c), 0) / morningHours.length;
                      return `${Math.round(avgTemp)}°${temperatureUnit}`;
                    })()}
                  </span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Afternoon:</span>
                  <span className="value">
                    {(() => {
                      const afternoonHours = (wDay.hour || []).filter(h => {
                        const hour = new Date(h.time).getHours();
                        return hour >= 12 && hour < 18;
                      });
                      if (afternoonHours.length === 0) return 'N/A';
                      const avgTemp = afternoonHours.reduce((sum, h) => sum + (temperatureUnit === 'F' ? h.temp_f : h.temp_c), 0) / afternoonHours.length;
                      return `${Math.round(avgTemp)}°${temperatureUnit}`;
                    })()}
                  </span>
                </div>
                <div className="expanded-detail-item">
                  <span className="label">Evening:</span>
                  <span className="value">
                    {(() => {
                      const eveningHours = (wDay.hour || []).filter(h => {
                        const hour = new Date(h.time).getHours();
                        return hour >= 18 && hour < 22;
                      });
                      if (eveningHours.length === 0) return 'N/A';
                      const avgTemp = eveningHours.reduce((sum, h) => sum + (temperatureUnit === 'F' ? h.temp_f : h.temp_c), 0) / eveningHours.length;
                      return `${Math.round(avgTemp)}°${temperatureUnit}`;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* Full-width Hourly Forecast */}
            <div className="expanded-hourly-forecast-section">
              <h5>24-Hour Forecast</h5>
              <div className="expanded-hourly-forecast-grid">
                {(wDay.hour || [])
                  .sort((a, b) => new Date(a.time) - new Date(b.time))
                  .map((hourData, hIndex) => {
                  const hourTime = new Date(hourData.time).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  
                  return (
                    <div key={hIndex} className="expanded-hourly-item">
                      <div className="expanded-hourly-time">{hourTime}</div>
                      <img
                        src={hourData.condition.icon}
                        alt={hourData.condition.text}
                        className="expanded-hourly-icon"
                      />
                      <div className="expanded-hourly-temp">{temperatureUnit === 'F' ? hourData.temp_f : hourData.temp_c}°{temperatureUnit}</div>
                      <div className="expanded-hourly-condition">{hourData.condition.text}</div>
                      <div className="expanded-hourly-precip">{hourData.chance_of_rain}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    })}
    {userPlan === 'Basic' && (
      <div className="basic-plan-upgrade-message">
        <p>Upgrade to see full weather forecast</p>
        <button onClick={() => window.location.href = '/plan-subscription'}>
          Upgrade Now
        </button>
      </div>
    )}
  </div>
)}

        {/* Example: Show Trip Info
        <div className="packing-trip-summary">
          <p>
            <strong>Destination:</strong> {selectedPackIt.packingList.packingList.tripDetails?.destination}
            &nbsp;|&nbsp; 
            <strong>Dates:</strong> {selectedPackIt.packingList.packingList.tripDetails?.startDate} 
            &nbsp;-&nbsp; 
            {selectedPackIt.packingList.packingList.tripDetails?.endDate}
          </p>
          <p>
            <strong>Classification:</strong>{' '}
            {selectedPackIt.packingList.packingList.classification}
          </p>
        </div>
        */}

{activeTab === 'packing' && (
  <div className={`tab-content packing-content ${userPlan === 'Basic' ? 'basic-plan-blur' : ''}`}>
    <div className="packing-header-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
        <h3 className="packing-header">Packing List</h3>
        {isPackItBeingEdited(packIt.id) && (
          <button
            className="add-category-button modern-icon-button"
            onClick={() => addPackingItem('miscellaneous')}
            title="Add Item"
          >
            <i className="fas fa-plus"></i>
            Add Item
          </button>
        )}
      </div>
      {/* Destination Breadcrumb */}
      {(() => {
        const destinations = getOrderedDestinations();
        if (destinations.length > 0) {
          return (
            <div className="destination-breadcrumb" style={{ marginTop: '0px', fontSize: '14px', color: '#666' }}>
              <span style={{ fontWeight: '500' }}>Destinations: </span>
              {destinations.map((dest, idx) => (
                <span key={idx}>
                  <span style={{ fontWeight: '500' }}>{dest}</span>
                  {idx < destinations.length - 1 && <span style={{ margin: '0 8px', color: '#999' }}>→</span>}
                </span>
              ))}
            </div>
          );
        }
        return null;
      })()}
    </div>
    <div className="packing-categories-container" ref={packingListRef}>
      {(() => {
        // Determine the packing list data whether it's nested or not.
        const packingListData = (isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt)?.packingList?.packingList || (isPackItBeingEdited(packIt.id) ? editingPackIt : selectedPackIt)?.packingList;
        if (!packingListData || Object.keys(packingListData).length === 0) {
          return <p>No packing list data found.</p>;
        }

        // Helper: get the raw data for a category given a key.
        const getCategoryData = (key) => packingListData[key];

        // Helper: normalizes items to handle both string and object formats
        const normalizeItems = (items) => {
          if (!Array.isArray(items)) return [];
          return items.map(item => {
            // If item is a string, convert to object format with defaults
            if (typeof item === 'string') {
              return { 
                item: item,
                quantity: 1,
                notes: '',
                confidenceLevel: null,
                usageDays: []
              };
            }
            // If item is already an object, ensure it has the expected structure with defaults
            return {
              item: item.item || item.name || '',
              quantity: item.quantity != null ? item.quantity : 1,
              notes: item.notes || '',
              confidenceLevel: item.confidenceLevel != null ? item.confidenceLevel : null,
              usageDays: Array.isArray(item.usageDays) ? item.usageDays : []
            };
          });
        };

        // Helper: renders a category.
        // If the data is an array, then render a single PackingCategory.
        // If it's an object, iterate over its keys and render each subcategory.
        const renderCategory = (key, title) => {
          const category = getCategoryData(key);
          if (!category) return null;

          // If the category is an array, just render it as a flat list.
          if (Array.isArray(category)) {
            const normalizedItems = normalizeItems(category);
            return <PackingCategory title={title} items={normalizedItems} category={key} packIt={packIt} key={key} />;
          }
          // If it is an object then iterate its keys.
          if (typeof category === "object") {
            return Object.entries(category).map(([subKey, items]) => {
              // Items might be directly an array or further wrapped in an "items" field.
              const subItems = Array.isArray(items) ? items : items.items || [];
              const normalizedItems = normalizeItems(subItems);
              return (
                <PackingCategory
                  title={`${title} - ${subKey}`}
                  items={normalizedItems}
                  category={key}
                  subcategory={subKey}
                  packIt={packIt}
                  key={`${key}-${subKey}`}
                />
              );
            });
          }
          return null;
        };

        // Helper to format category title from key
        const formatCategoryTitle = (key) => {
          // Handle common mappings
          const titleMap = {
            'financials': 'Financials',
            'electronics': 'Electronics',
            'travelDocuments': 'Travel Documents',
            'toiletries': 'Toiletries',
            'accessories': 'Accessories',
            'clothes': 'Clothing',
            'clothing': 'Clothing',
            'clothingItems': 'Clothing',
            'kids_specific_items': "Kids' Items",
            'beach': 'Beach Items',
            'health': 'Health & Medical',
            'health_medical': 'Health & Medical',
            'HEALTH_MEDICAL': 'Health & Medical',
            'travelInfo': 'Travel Aids',
            'footwear': 'Footwear',
            'miscellaneous': 'Miscellaneous',
            'MISCELLANEOUS': 'Miscellaneous',
            'hygiene_grooming': 'Hygiene & Grooming',
            'HYGIENE_GROOMING': 'Hygiene & Grooming',
            'documents': 'Documents',
            'DOCUMENTS': 'Documents',
            'technology': 'Technology',
            'TECHNOLOGY': 'Technology',
            'basics': 'Basics',
            'BASICS': 'Basics',
            'snow': 'Snow',
            'SNOW': 'Snow'
          };
          
          if (titleMap[key]) return titleMap[key];
          
          // Default: convert snake_case or UPPER_CASE to Title Case
          return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
        };

        // Now render each packing list category.
        // First, render known categories in preferred order
        const knownCategories = [
          "BASICS", "basics",
          "CLOTHES", "clothes", "clothing", "clothingItems",
          "FOOTWEAR", "footwear",
          "ACCESSORIES", "accessories",
          "HYGIENE_GROOMING", "hygiene_grooming",
          "HEALTH_MEDICAL", "health_medical", "health",
          "TECHNOLOGY", "technology", "electronics",
          "DOCUMENTS", "documents", "travelDocuments",
          "SNOW", "snow",
          "MISCELLANEOUS", "miscellaneous",
          "kids_specific_items",
          "beach",
          "travelInfo",
          "financials"
        ];
        
        const renderedKeys = new Set();
        const categoryElements = [];
        
        // Render known categories first
        knownCategories.forEach(key => {
          if (packingListData[key] && !renderedKeys.has(key.toLowerCase())) {
            const element = renderCategory(key, formatCategoryTitle(key));
            if (element) {
              categoryElements.push(element);
              renderedKeys.add(key.toLowerCase());
            }
          }
        });
        
        // Then render any remaining categories
        Object.keys(packingListData).forEach(key => {
          const lowerKey = key.toLowerCase();
          if (!renderedKeys.has(lowerKey) && key !== 'classification') {
            const element = renderCategory(key, formatCategoryTitle(key));
            if (element) {
              categoryElements.push(element);
              renderedKeys.add(lowerKey);
            }
          }
        });
        
        return <>{categoryElements}</>;
      })()}
      {userPlan === 'Basic' && (
        <div className="basic-plan-upgrade-message">
          <p>Upgrade to see full packing list</p>
          <button onClick={() => window.location.href = '/plan-subscription'}>
            Upgrade Now
          </button>
        </div>
      )}
    </div>
  </div>
)}













                  </div>
                )}


                </div>
              );
            })}
          </div>

                    {/* 
            7) Simple pagination bar using 1-based indexing.
            We no longer use ReactPaginate; we just map over the page numbers 
          */}
          <div className="pagination-section">
              <div className="pagination-info">
                <div className="pagination-stats" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                  <span className="stat-item">
                    <span className="stat-number">{currentItems.length}</span>
                    <span className="stat-label">Showing</span>
                  </span>
                  <span className="stat-divider">•</span>
                  <span className="stat-item">
                    <span className="stat-number">{totalPackItsCount || filteredPackIts.length}</span>
                    <span className="stat-label">
                      Total
                      {!allPackItsLoaded && (
                        <button
                          onClick={fetchTotalPackItsCount}
                          className="refresh-count-btn"
                          title="Get accurate total count"
                          style={{
                            marginLeft: '8px',
                            padding: '2px 6px',
                            fontSize: '12px',
                            background: 'transparent',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          <i className="fas fa-sync-alt"></i>
                        </button>
                      )}
                    </span>
                  </span>
                  <span className="stat-divider">•</span>
                  <span className="stat-item">
                    <span className="stat-number">{currentPage}</span>
                    <span className="stat-label">Page {currentPage} of {totalPages}</span>
                  </span>
                  <span className="stat-divider">•</span>
                  <span className="stat-item">
                    <label className="stat-label" style={{ marginRight: '8px' }}>Per Page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      disabled={isLoadingPackIts}
                      style={{
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '4px',
                        background: 'var(--bg-elevated)',
                        color: 'var(--text-primary)',
                        cursor: isLoadingPackIts ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={0}>All</option>
                    </select>
                  </span>
                </div>
              </div>
              
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || isLoadingPackIts}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  
                  <div className="page-numbers">
                    {Array.from({ length: totalPages }, (_, index) => {
                      const pageNumber = index + 1;
                      const isActive = pageNumber === currentPage;
                      const isNearActive = Math.abs(pageNumber - currentPage) <= 2;
                      
                      if (isActive || isNearActive || pageNumber === 1 || pageNumber === totalPages) {
                        return (
                          <button
                            key={pageNumber}
                            className={`page-btn ${isActive ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNumber)}
                            disabled={isLoadingPackIts}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                        return <span key={pageNumber} className="page-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || isLoadingPackIts}
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
          
          {/* Chat container */}
          {!isChatMinimized && (
            <div className="chat-container">
              <div className="chat-header">
                <h3><i className="fas fa-comments"></i> Your Personal Travel Assistant</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <p className="chat-subtitle">Research, get weather, or talk to and edit your Pack-it. just ask!</p>
                  <div className="chatbot-info-badge" data-tooltip="I can research destinations, fetch weather forecasts, and modify your PackIt (itinerary, packing list, stays). Always verify important details independently.">
                    <i className="fas fa-info-circle"></i>
                  </div>
                </div>
              </div>
              <div className="chat-messages">
                {messages.length === 0 && (
                  <div className="chat-suggestions">
                    <p className="chat-suggestions-title">Try asking:</p>
                    <div className="chat-suggestions-grid">
                      {CHAT_SUGGESTIONS.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="chat-suggestion-chip"
                          onClick={() => handleSendMessage(suggestion.label)}
                          disabled={isWaitingForResponse}
                        >
                          <i className={`fas ${suggestion.icon}`}></i>
                          <span>{suggestion.label}</span>
                        </button>
                      ))}
                    </div>
                    <p className="chat-suggestions-hint">Or type your own question below</p>
                  </div>
                )}
                {messages.map((msg, index) => {
                  // Find the index of the last bedrock message
                  let lastBedrockIndex = -1;
                  for (let i = messages.length - 1; i >= 0; i--) {
                    if (messages[i].sender === 'bedrock') {
                      lastBedrockIndex = i;
                      break;
                    }
                  }
                  // Check if this is the last bedrock message
                  const isLastBedrockMessage = msg.sender === 'bedrock' && index === lastBedrockIndex;
                  
                  return (
                    <div
                      key={index}
                      ref={isLastBedrockMessage ? lastBedrockMessageRef : null}
                      className={`chat-message ${
                        msg.sender === 'user' ? 'user' : 'bedrock'
                      }`}
                    >
                      {formatChatMessage(msg.text)}
                    </div>
                  );
                })}
                {isWaitingForResponse && (
                  <div className="chat-message bedrock loading-message">
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="loading-text">Typing...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="chat-input">
                <textarea
                  value={inputMessage}
                  maxLength={1000}
                  onChange={(e) => {
                    // Enforce max length by truncating if needed (handles paste operations)
                    const value = e.target.value;
                    if (value.length > 1000) {
                      const truncatedValue = value.substring(0, 1000);
                      setInputMessage(truncatedValue);
                      setHasError(true);
                      setErrorMessage('Your message was truncated to 1000 characters. Please shorten your message.');
                    } else {
                      setInputMessage(value);
                      // Clear any previous truncation error
                      if (hasError && errorMessage.includes('truncated')) {
                        setHasError(false);
                        setErrorMessage('');
                      }
                    }
                    // Auto-resize textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
                  }}
                  placeholder="Type your question here..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                {inputMessage.length > 0 && (
                  <div className="chat-character-count" style={{ 
                    fontSize: '12px', 
                    color: inputMessage.length > 1000 ? '#ef4444' : inputMessage.length > 900 ? '#f59e0b' : '#6b7280',
                    textAlign: 'right',
                    marginTop: '4px',
                    paddingRight: '8px'
                  }}>
                    {inputMessage.length} / 1000 characters
                  </div>
                )}
                <div className="chat-buttons">
                  <button onClick={() => handleSendMessage()} className="chat-send-button">
                    <i className="fas fa-paper-plane"></i>
                    <span>Send</span>
                  </button>
                  <button onClick={clearChat} className="chat-clear-button">
                    <i className="fas fa-trash"></i>
                    <span>Clear</span>
                  </button>
                  {isWaitingForResponse && !hasError && (
                    <div className="waiting-indicator">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Thinking...</span>
                    </div>
                  )}
                  {hasError && (
                    <div className="error-indicator">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>{errorMessage || 'Something went wrong'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Download Format Modal */}
        {downloadFormatModalPackitId && (
          <div 
            className="modal-overlay" 
            onClick={() => setDownloadFormatModalPackitId(null)}
          >
            <div 
              className="modal-content download-format-modal" 
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 320, width: 320 }}
            >
              <div className="modal-header">
                <h2>Choose Download Format</h2>
              </div>
              <div className="modal-body">
                <p>Select the format you want to download your Pack-It in:</p>
                <div className="download-format-options">
                  <button
                    type="button"
                    className="download-format-btn"
                    onClick={() => handleDownload(downloadFormatModalPackitId, 'pdf')}
                  >
                    <i className="fas fa-file-pdf"></i>
                    <span>PDF</span>
                  </button>
                  <button
                    type="button"
                    className="download-format-btn"
                    onClick={() => handleDownload(downloadFormatModalPackitId, 'csv')}
                  >
                    <i className="fas fa-file-csv"></i>
                    <span>CSV</span>
                  </button>
                  <button
                    type="button"
                    className="download-format-btn"
                    onClick={() => handleDownload(downloadFormatModalPackitId, 'kml')}
                  >
                    <i className="fas fa-map-marked-alt"></i>
                    <span>KML</span>
                  </button>
                </div>
              </div>
              <div className="modal-footer download-format-modal-footer">
                <button
                  type="button"
                  className="download-format-cancel-btn"
                  onClick={() => setDownloadFormatModalPackitId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {openAlertPackIt && (() => {
          const packIt = packIts.find((p) => p.id === openAlertPackIt);
          if (!packIt) return null;
          
          // Show loading screen while fetching alerts
          if (isLoadingAlerts) {
            return (
              <div className="modal-overlay" onClick={() => closeAlertModal(openAlertPackIt)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Loading Alerts</h2>
                    <button 
                      className="modal-close-button" 
                      onClick={() => closeAlertModal(openAlertPackIt)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="loading-spinner">
                      Processing your request...
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          // Use selectedPackIt data if this is the selected pack-it, otherwise use packIt summary data
          const currentPackIt = selectedPackIt && selectedPackIt.id === openAlertPackIt ? selectedPackIt : packIt;
          console.log('Alert modal - openAlertPackIt:', openAlertPackIt, 'selectedPackIt.id:', selectedPackIt?.id);
          console.log('Alert modal - currentPackIt.healthsafety:', currentPackIt.healthsafety);
          
          let allAlerts = [];
          
          // Try to get alerts from both full details and summary data
          const alertsData = currentPackIt.healthsafety?.healthSafetyAlerts || currentPackIt.healthSafetyAlerts || [];
          console.log('Alert modal - currentPackIt:', currentPackIt);
          console.log('Alert modal - alertsData:', alertsData, 'totalAlerts:', packIt.totalAlerts);
          console.log('Alert modal - healthsafety:', currentPackIt.healthsafety);
          
          if (Array.isArray(alertsData) && alertsData.length > 0) {
            console.log('Alert modal - processing alertsData, length:', alertsData.length);
            if (Array.isArray(alertsData[0].alerts)) {
              allAlerts = alertsData.flatMap((block) => safeToArray(block.alerts));
              console.log('Alert modal - flattened alerts from blocks:', allAlerts);
            } else {
              // Filter out empty objects and only include alerts with actual content
              allAlerts = alertsData.filter(alert => 
                alert && 
                Object.keys(alert).length > 0 && 
                (alert.severity || alert.message || alert.title || alert.description)
              );
              console.log('Alert modal - filtered alerts:', allAlerts);
            }
          } else {
            console.log('Alert modal - no alertsData or empty array');
            // Don't show fallback summary - just show "No Active Alerts" if no data
          }

          // Define severity order (highest first)
          const severityOrder = { High: 3, Medium: 2, Low: 1 };
          const sortedAlerts = allAlerts.sort(
            (a, b) => (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0)
          );

          return (
            <div
              className="modal-overlay"
              onClick={() => closeAlertModal(openAlertPackIt)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>
                    <i className="fas fa-exclamation-triangle alert-icon"></i>
                    Travel Alerts for {packIt.name}
                  </h3>
                  <button
                    className="modal-close-button"
                    onClick={() => closeAlertModal(openAlertPackIt)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="alerts-list">
                    {sortedAlerts.length > 0 ? (
                      sortedAlerts.map((alert, idx) => (
                        <div key={idx} className="alert-item">
                          <div className="alert-header">
                            <h4 className="alert-title">
                              <i className={`fas ${
                                alert.severity === 'High' ? 'fa-exclamation-circle' :
                                alert.severity === 'Medium' ? 'fa-exclamation-triangle' :
                                'fa-info-circle'
                              }`} style={{
                                color: alert.severity === 'High' ? 'var(--error-500)' :
                                       alert.severity === 'Medium' ? 'var(--warning-500)' :
                                       'var(--info-500)'
                              }}></i>
                              {alert.alertType}
                            </h4>
                            <span className={`alert-severity ${alert.severity.toLowerCase()}`}>
                              {alert.severity}
                            </span>
                          </div>
                          
                          <p className="alert-description">
                            {alert.description}
                          </p>
                          
                          {alert.recommendations && (
                            <div className="alert-recommendations">
                              <h5>
                                <i className="fas fa-lightbulb"></i>
                                Recommendations
                              </h5>
                              <ul>
                                {safeToArray(alert.recommendations).map((rec, recIdx) => (
                                  <li key={recIdx}>{rec}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="alert-item">
                        <div className="alert-header">
                          <h4 className="alert-title">
                            <i className="fas fa-check-circle" style={{ color: 'var(--success-500)' }}></i>
                            No Active Alerts
                          </h4>
                        </div>
                        <p className="alert-description">
                          Great news! There are currently no active alerts for this trip. Your travel plans look safe and ready to go.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button onClick={() => closeAlertModal(openAlertPackIt)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      </div>

      {/* Detail Modal */}
      {detailModalOpen && selectedDetailItem && (
        <div className="detail-modal-overlay" onClick={closeDetailModal}>
          <div className="detail-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h2>{selectedDetailItem.name}</h2>
              <button className="detail-modal-close" onClick={closeDetailModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="detail-modal-body">
              {detailModalType === 'thingstodo' && (
                <>
                  {selectedDetailItem.description && (
                    <div className="detail-section">
                      <h3><i className="fas fa-info-circle"></i> Description</h3>
                      <p>{selectedDetailItem.description}</p>
                    </div>
                  )}
                  {selectedDetailItem.type && (
                    <div className="detail-section">
                      <h3><i className="fas fa-tag"></i> Type</h3>
                      <p>{selectedDetailItem.type}</p>
                    </div>
                  )}
                  {selectedDetailItem.address && (
                    <div className="detail-section">
                      <h3><i className="fas fa-map-marker-alt"></i> Address</h3>
                      <p>{selectedDetailItem.address}</p>
                    </div>
                  )}
                  {selectedDetailItem.website && (
                    <div className="detail-section">
                      <h3><i className="fas fa-link"></i> Website</h3>
                      <p><a href={selectedDetailItem.website} target="_blank" rel="noopener noreferrer">{selectedDetailItem.website}</a></p>
                    </div>
                  )}
                  {selectedDetailItem.availability && (
                    <div className="detail-section">
                      <h3><i className="fas fa-clock"></i> Availability</h3>
                      <p>{selectedDetailItem.availability}</p>
                    </div>
                  )}
                  {selectedDetailItem.estimatedVisitDuration && (
                    <div className="detail-section">
                      <h3><i className="fas fa-hourglass-half"></i> Estimated Visit Duration</h3>
                      <p>{selectedDetailItem.estimatedVisitDuration}</p>
                    </div>
                  )}
                  {selectedDetailItem.priceRange && (
                    <div className="detail-section">
                      <h3><i className="fas fa-dollar-sign"></i> Price Range</h3>
                      <p>{selectedDetailItem.priceRange}</p>
                    </div>
                  )}
                  {selectedDetailItem.weatherConsiderations && (
                    <div className="detail-section">
                      <h3><i className="fas fa-cloud-sun"></i> Weather Considerations</h3>
                      <p>{selectedDetailItem.weatherConsiderations}</p>
                    </div>
                  )}
                  {selectedDetailItem.bookingRequired !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-calendar-check"></i> Booking Required</h3>
                      <p>{selectedDetailItem.bookingRequired ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.kidFriendly !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-child"></i> Kid Friendly</h3>
                      <p>{selectedDetailItem.kidFriendly ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.petFriendly !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-paw"></i> Pet Friendly</h3>
                      <p>{selectedDetailItem.petFriendly ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.accessibility && (
                    <div className="detail-section">
                      <h3><i className="fas fa-wheelchair"></i> Accessibility</h3>
                      <ul>
                        {selectedDetailItem.accessibility.wheelchairAccessible !== undefined && (
                          <li>Wheelchair Accessible: {selectedDetailItem.accessibility.wheelchairAccessible ? 'Yes' : 'No'}</li>
                        )}
                        {selectedDetailItem.accessibility.audioGuidesAvailable !== undefined && (
                          <li>Audio Guides Available: {selectedDetailItem.accessibility.audioGuidesAvailable ? 'Yes' : 'No'}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.transportation && (
                    <div className="detail-section">
                      <h3><i className="fas fa-car"></i> Transportation</h3>
                      <ul>
                        {selectedDetailItem.transportation.parkingAvailable !== undefined && (
                          <li>Parking Available: {selectedDetailItem.transportation.parkingAvailable ? 'Yes' : 'No'}</li>
                        )}
                        {selectedDetailItem.transportation.nearestBusStop && (
                          <li>Nearest Bus Stop: {selectedDetailItem.transportation.nearestBusStop}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.nearbyAttractions && Array.isArray(selectedDetailItem.nearbyAttractions) && selectedDetailItem.nearbyAttractions.length > 0 && (
                    <div className="detail-section">
                      <h3><i className="fas fa-map"></i> Nearby Attractions</h3>
                      <ul>
                        {selectedDetailItem.nearbyAttractions.map((attraction, idx) => (
                          <li key={idx}>{attraction}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.probability !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-percentage"></i> Recommendation Probability</h3>
                      <p>{(selectedDetailItem.probability * 100).toFixed(0)}%</p>
                    </div>
                  )}
                </>
              )}

              {detailModalType === 'placestoeat' && (
                <>
                  {selectedDetailItem.description && (
                    <div className="detail-section">
                      <h3><i className="fas fa-info-circle"></i> Description</h3>
                      <p>{selectedDetailItem.description}</p>
                    </div>
                  )}
                  {selectedDetailItem.cuisineType && (
                    <div className="detail-section">
                      <h3><i className="fas fa-utensils"></i> Cuisine Type</h3>
                      <p>{selectedDetailItem.cuisineType}</p>
                    </div>
                  )}
                  {selectedDetailItem.rating && (
                    <div className="detail-section">
                      <h3><i className="fas fa-star"></i> Rating</h3>
                      <p>{selectedDetailItem.rating} / 5.0</p>
                    </div>
                  )}
                  {selectedDetailItem.address && (
                    <div className="detail-section">
                      <h3><i className="fas fa-map-marker-alt"></i> Address</h3>
                      <p>{selectedDetailItem.address}</p>
                    </div>
                  )}
                  {selectedDetailItem.website && (
                    <div className="detail-section">
                      <h3><i className="fas fa-link"></i> Website</h3>
                      <p><a href={selectedDetailItem.website} target="_blank" rel="noopener noreferrer">{selectedDetailItem.website}</a></p>
                    </div>
                  )}
                  {selectedDetailItem.contact && (
                    <div className="detail-section">
                      <h3><i className="fas fa-phone"></i> Contact</h3>
                      <p>{selectedDetailItem.contact}</p>
                    </div>
                  )}
                  {selectedDetailItem.availability && (
                    <div className="detail-section">
                      <h3><i className="fas fa-clock"></i> Availability</h3>
                      <p>{selectedDetailItem.availability}</p>
                    </div>
                  )}
                  {selectedDetailItem.priceRange && (
                    <div className="detail-section">
                      <h3><i className="fas fa-dollar-sign"></i> Price Range</h3>
                      <p>{selectedDetailItem.priceRange}</p>
                    </div>
                  )}
                  {selectedDetailItem.dressCode && (
                    <div className="detail-section">
                      <h3><i className="fas fa-tshirt"></i> Dress Code</h3>
                      <p>{selectedDetailItem.dressCode}</p>
                    </div>
                  )}
                  {selectedDetailItem.bookingRequired !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-calendar-check"></i> Booking Required</h3>
                      <p>{selectedDetailItem.bookingRequired ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.kidFriendly !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-child"></i> Kid Friendly</h3>
                      <p>{selectedDetailItem.kidFriendly ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.petFriendly !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-paw"></i> Pet Friendly</h3>
                      <p>{selectedDetailItem.petFriendly ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.featuredDishes && Array.isArray(selectedDetailItem.featuredDishes) && selectedDetailItem.featuredDishes.length > 0 && (
                    <div className="detail-section">
                      <h3><i className="fas fa-utensils"></i> Featured Dishes</h3>
                      <ul>
                        {selectedDetailItem.featuredDishes.map((dish, idx) => (
                          <li key={idx}>{dish}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.dietaryOptions && Array.isArray(selectedDetailItem.dietaryOptions) && selectedDetailItem.dietaryOptions.length > 0 && (
                    <div className="detail-section">
                      <h3><i className="fas fa-leaf"></i> Dietary Options</h3>
                      <ul>
                        {selectedDetailItem.dietaryOptions.map((option, idx) => (
                          <li key={idx}>{option}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.accessibility && (
                    <div className="detail-section">
                      <h3><i className="fas fa-wheelchair"></i> Accessibility</h3>
                      <ul>
                        {selectedDetailItem.accessibility.wheelchairAccessible !== undefined && (
                          <li>Wheelchair Accessible: {selectedDetailItem.accessibility.wheelchairAccessible ? 'Yes' : 'No'}</li>
                        )}
                        {selectedDetailItem.accessibility.audioGuidesAvailable !== undefined && (
                          <li>Audio Guides Available: {selectedDetailItem.accessibility.audioGuidesAvailable ? 'Yes' : 'No'}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.transportation && (
                    <div className="detail-section">
                      <h3><i className="fas fa-car"></i> Transportation</h3>
                      <ul>
                        {selectedDetailItem.transportation.parkingAvailable !== undefined && (
                          <li>Parking Available: {selectedDetailItem.transportation.parkingAvailable ? 'Yes' : 'No'}</li>
                        )}
                        {selectedDetailItem.transportation.nearestBusStop && (
                          <li>Nearest Bus Stop: {selectedDetailItem.transportation.nearestBusStop}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.nearbyAttractions && Array.isArray(selectedDetailItem.nearbyAttractions) && selectedDetailItem.nearbyAttractions.length > 0 && (
                    <div className="detail-section">
                      <h3><i className="fas fa-map"></i> Nearby Attractions</h3>
                      <ul>
                        {selectedDetailItem.nearbyAttractions.map((attraction, idx) => (
                          <li key={idx}>{attraction}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedDetailItem.probability !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-percentage"></i> Recommendation Probability</h3>
                      <p>{(selectedDetailItem.probability * 100).toFixed(0)}%</p>
                    </div>
                  )}
                </>
              )}

              {detailModalType === 'placestostay' && (
                <>
                  {selectedDetailItem.description && (
                    <div className="detail-section">
                      <h3><i className="fas fa-info-circle"></i> Description</h3>
                      <p>{selectedDetailItem.description}</p>
                    </div>
                  )}
                  {selectedDetailItem.address && (
                    <div className="detail-section">
                      <h3><i className="fas fa-map-marker-alt"></i> Address</h3>
                      <p>{selectedDetailItem.address}</p>
                    </div>
                  )}
                  {selectedDetailItem.website && (
                    <div className="detail-section">
                      <h3><i className="fas fa-link"></i> Website</h3>
                      <p><a href={selectedDetailItem.website} target="_blank" rel="noopener noreferrer">{selectedDetailItem.website}</a></p>
                    </div>
                  )}
                  {selectedDetailItem.bookingUrl && (
                    <div className="detail-section">
                      <h3><i className="fas fa-calendar-alt"></i> Booking URL</h3>
                      <p><a href={selectedDetailItem.bookingUrl} target="_blank" rel="noopener noreferrer">{selectedDetailItem.bookingUrl}</a></p>
                    </div>
                  )}
                  {selectedDetailItem.contact && (
                    <div className="detail-section">
                      <h3><i className="fas fa-phone"></i> Contact</h3>
                      <p>{selectedDetailItem.contact}</p>
                    </div>
                  )}
                  {selectedDetailItem.rating && (
                    <div className="detail-section">
                      <h3><i className="fas fa-star"></i> Rating</h3>
                      <p>{selectedDetailItem.rating} / 5.0</p>
                    </div>
                  )}
                  {selectedDetailItem.priceRange && (
                    <div className="detail-section">
                      <h3><i className="fas fa-dollar-sign"></i> Price Range</h3>
                      <p>{selectedDetailItem.priceRange}</p>
                    </div>
                  )}
                  {selectedDetailItem.roomType && (
                    <div className="detail-section">
                      <h3><i className="fas fa-bed"></i> Room Type</h3>
                      <p>{selectedDetailItem.roomType}</p>
                    </div>
                  )}
                  {selectedDetailItem.checkInTime && (
                    <div className="detail-section">
                      <h3><i className="fas fa-sign-in-alt"></i> Check-in Time</h3>
                      <p>{selectedDetailItem.checkInTime}</p>
                    </div>
                  )}
                  {selectedDetailItem.checkOutTime && (
                    <div className="detail-section">
                      <h3><i className="fas fa-sign-out-alt"></i> Check-out Time</h3>
                      <p>{selectedDetailItem.checkOutTime}</p>
                    </div>
                  )}
                  {selectedDetailItem.checkInDate && (
                    <div className="detail-section">
                      <h3><i className="fas fa-calendar-check"></i> Check-in Date</h3>
                      <p>{new Date(selectedDetailItem.checkInDate).toLocaleDateString()}</p>
                    </div>
                  )}
                  {selectedDetailItem.checkOutDate && (
                    <div className="detail-section">
                      <h3><i className="fas fa-calendar-times"></i> Check-out Date</h3>
                      <p>{new Date(selectedDetailItem.checkOutDate).toLocaleDateString()}</p>
                    </div>
                  )}
                  {selectedDetailItem.cancellationPolicy && (
                    <div className="detail-section">
                      <h3><i className="fas fa-ban"></i> Cancellation Policy</h3>
                      <p>{selectedDetailItem.cancellationPolicy}</p>
                    </div>
                  )}
                  {selectedDetailItem.bookingRequired !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-calendar-check"></i> Booking Required</h3>
                      <p>{selectedDetailItem.bookingRequired ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {selectedDetailItem.petFriendly !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-paw"></i> Pet Friendly</h3>
                      <p>{selectedDetailItem.petFriendly ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {(() => {
                    const rawAmenities = selectedDetailItem.amenities;
                    const text = formatAmenitiesForDisplay(rawAmenities);
                    if (!text) return null;
                    if (Array.isArray(rawAmenities) && rawAmenities.length > 0) {
                      return (
                        <div className="detail-section">
                          <h3><i className="fas fa-list"></i> Amenities</h3>
                          <ul>
                            {rawAmenities.map((amenity, idx) => (
                              <li key={idx}>{amenity != null ? String(amenity) : ''}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    return (
                      <div className="detail-section">
                        <h3><i className="fas fa-list"></i> Amenities</h3>
                        <p>{text}</p>
                      </div>
                    );
                  })()}
                  {selectedDetailItem.coordinateProbability !== undefined && (
                    <div className="detail-section">
                      <h3><i className="fas fa-percentage"></i> Location Accuracy</h3>
                      <p>{(selectedDetailItem.coordinateProbability * 100).toFixed(0)}%</p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="detail-modal-footer">
              {(() => {
                // Check if modal contains contact-related information
                const hasContactInfo = selectedDetailItem && (
                  selectedDetailItem.address ||
                  selectedDetailItem.contact ||
                  selectedDetailItem.website ||
                  selectedDetailItem.bookingUrl
                );
                return hasContactInfo ? (
                  <div className="ai-accuracy-disclaimer">
                    <i className="fas fa-info-circle"></i>
                    <span>Please check directly with provider for the most current information.</span>
                  </div>
                ) : null;
              })()}
              <button onClick={closeDetailModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};


export default PackIt;
